/*
@license
  Concept by RoarTheme (https://roartheme.co)
  Access unminified JS in assets/theme.js

  Use this event listener to run your own JS outside of this file.
  Documentation - https://roartheme.co/blogs/concept/javascript-events-for-developers

  document.addEventListener('page:loaded', function() {
    // Page has loaded and theme assets are ready
  });
*/

window.theme = window.theme || {};
window.Shopify = window.Shopify || {};

theme.config = {
  hasSessionStorage: true,
  hasLocalStorage: true,
  mqlSmall: false,
  mediaQuerySmall: "screen and (max-width: 767px)",
  motionReduced: window.matchMedia("(prefers-reduced-motion: reduce)")
    .matches,
  isTouch:
    "ontouchstart" in window ||
    navigator.MaxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0,
  rtl: document.documentElement.getAttribute("dir") === "rtl" ? true : false,
};

theme.supportsPassive = false;

try {
  const opts = Object.defineProperty({}, "passive", {
    get: function () {
      theme.supportsPassive = true;
    },
  });
  window.addEventListener("testPassive", null, opts);
  window.removeEventListener("testPassive", null, opts);
} catch (e) {}

document.documentElement.classList.add(
  theme.config.isTouch ? "touch" : "no-touch"
);
console.log(
  theme.settings.themeName +
    " theme (" +
    theme.settings.themeVersion +
    ") by RoarTheme | Learn more at https://roartheme.co"
);

(function () {
  /*============================================================================
    Things that require DOM to be ready
  ==============================================================================*/
  theme.DOMready = function (callback) {
    if (document.readyState != "loading") callback();
    else document.addEventListener("DOMContentLoaded", callback);
  };

  theme.headerGroup = {
    rounded: () => {
      return Array.from(
        document.querySelectorAll(
          ".shopify-section-group-header-group .section--rounded"
        )
      );
    },
    sections: () => {
      return Array.from(
        document.querySelectorAll(".shopify-section-group-header-group")
      );
    },
    init: () => {
      let previousSection = null;
      const rounded = theme.headerGroup.rounded();
      const sections = theme.headerGroup.sections();

      sections.forEach((shopifySection, index) => {
        const section = shopifySection.querySelector(".section");
        if (section) {
          section.classList.remove("section--next-rounded");
          section.classList.remove("section--first-rounded");
          section.classList.remove("section--last-rounded");

          if (section.classList.contains("section--rounded")) {
            if (index === 0) {
              section.classList.add("section--first-rounded");
            }

            if (index === rounded.length - 1) {
              section.classList.add("section--last-rounded");
            }

            if (
              previousSection &&
              previousSection.classList.contains(
                "section--rounded"
              )
            ) {
              previousSection.classList.add(
                "section--next-rounded"
              );
            }
          }

          if (index === sections.length - 1) {
            const nextSection = document.querySelector(
              ".main-content .shopify-section:first-child .section"
            );
            if (
              nextSection &&
              nextSection.classList.contains("section--rounded")
            ) {
              section.classList.add("section--next-rounded");
            }
          }

          previousSection = section;
        }
      });
    },
  };

  theme.a11y = {
    trapFocusHandlers: {},

    getFocusableElements: (container) => {
      return Array.from(
        container.querySelectorAll(
          'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
        )
      );
    },

    trapFocus: (container, elementToFocus = container) => {
      const elements = theme.a11y.getFocusableElements(container);
      const first = elements[0];
      const last = elements[elements.length - 1];

      theme.a11y.removeTrapFocus();

      theme.a11y.trapFocusHandlers.focusin = (event) => {
        if (
          event.target !== container &&
          event.target !== last &&
          event.target !== first
        )
          return;

        document.addEventListener(
          "keydown",
          theme.a11y.trapFocusHandlers.keydown
        );
      };

      theme.a11y.trapFocusHandlers.focusout = function () {
        document.removeEventListener(
          "keydown",
          theme.a11y.trapFocusHandlers.keydown
        );
      };

      theme.a11y.trapFocusHandlers.keydown = function (event) {
        if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
        // On the last focusable element and tab forward, focus the first element.
        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }

        //  On the first focusable element and tab backward, focus the last element.
        if (
          (event.target === container || event.target === first) &&
          event.shiftKey
        ) {
          event.preventDefault();
          last.focus();
        }
      };

      document.addEventListener(
        "focusout",
        theme.a11y.trapFocusHandlers.focusout
      );
      document.addEventListener(
        "focusin",
        theme.a11y.trapFocusHandlers.focusin
      );

      elementToFocus.focus();

      if (
        elementToFocus.tagName === "INPUT" &&
        ["search", "text", "email", "url"].includes(
          elementToFocus.type
        ) &&
        elementToFocus.value
      ) {
        elementToFocus.setSelectionRange(
          0,
          elementToFocus.value.length
        );
      }
    },

    removeTrapFocus: (elementToFocus = null) => {
      document.removeEventListener(
        "focusin",
        theme.a11y.trapFocusHandlers.focusin
      );
      document.removeEventListener(
        "focusout",
        theme.a11y.trapFocusHandlers.focusout
      );
      document.removeEventListener(
        "keydown",
        theme.a11y.trapFocusHandlers.keydown
      );

      if (elementToFocus && typeof elementToFocus.focus === "function")
        elementToFocus.focus();
    },
  };

  theme.utils = {
    throttle: (callback) => {
      let requestId = null,
        lastArgs;
      const later = (context) => () => {
        requestId = null;
        callback.apply(context, lastArgs);
      };
      const throttled = (...args) => {
        lastArgs = args;
        if (requestId === null) {
          requestId = requestAnimationFrame(later(this));
        }
      };
      throttled.cancel = () => {
        cancelAnimationFrame(requestId);
        requestId = null;
      };
      return throttled;
    },

    debounce: (fn, wait) => {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    waitForEvent: (element, eventName) => {
      return new Promise((resolve) => {
        const done = (event) => {
          if (event.target === element) {
            element.removeEventListener(eventName, done);
            resolve(event);
          }
        };
        element.addEventListener(eventName, done);
      });
    },

    fetchConfig: (type = "json", method = "POST") => {
      return {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Accept: `application/${type}`,
        },
      };
    },

    postLink: (path, options) => {
      options = options || {};
      const method = options["method"] || "post";
      const params = options["parameters"] || {};

      const form = document.createElement("form");
      form.setAttribute("method", method);
      form.setAttribute("action", path);

      for (const key in params) {
        const hiddenField = document.createElement("input");
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("name", key);
        hiddenField.setAttribute("value", params[key]);
        form.appendChild(hiddenField);
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    postLink2: (path, options) => {
      options = options || {};
      const method = options["method"] || "post";
      const params = options["parameters"] || {};

      const form = document.createElement("form");
      form.setAttribute("method", method);
      form.setAttribute("action", path);

      for (const key in params) {
        for (const index in params[key]) {
          for (const key2 in params[key][index]) {
            const hiddenField = document.createElement("input");
            hiddenField.setAttribute("type", "hidden");
            hiddenField.setAttribute(
              "name",
              `${key}[${index}][${key2}]`
            );
            hiddenField.setAttribute(
              "value",
              params[key][index][key2]
            );
            form.appendChild(hiddenField);
          }
        }
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    sectionId: (element) => {
      if (element.hasAttribute("data-section-id"))
        return element.getAttribute("data-section-id");

      element = element.classList.contains("shopify-section")
        ? element
        : element.closest(".shopify-section");
      return element.id.replace("shopify-section-", "");
    },

    imageLoaded: (imageOrArray) => {
      if (!imageOrArray) {
        return Promise.resolve();
      }
      imageOrArray =
        imageOrArray instanceof Element
          ? [imageOrArray]
          : Array.from(imageOrArray);
      return Promise.all(
        imageOrArray.map((image) => {
          return new Promise((resolve) => {
            if (
              (image.tagName === "IMG" && image.complete) ||
              !image.offsetParent
            ) {
              resolve();
            } else {
              image.onload = () => resolve();
            }
          });
        })
      );
    },

    visibleMedia: (media) => {
      return Array.from(media).find((item) => {
        const style = window.getComputedStyle(item);
        return style.display !== "none";
      });
    },

    setScrollbarWidth: () => {
      const scrollbarWidth =
        window.innerWidth - document.body.clientWidth;
      document.documentElement.style.setProperty(
        "--scrollbar-width",
        `${scrollbarWidth > 0 ? scrollbarWidth : 0}px`
      );
    },

    externalLinksNewTab: () => {
      if (theme.settings.externalLinksNewTab) {
        document.addEventListener("click", (evt) => {
          const link =
            evt.target.tagName === "A"
              ? evt.target
              : evt.target.closest("a");
          if (
            link &&
            link.tagName === "A" &&
            window.location.hostname !== new URL(link.href).hostname
          ) {
            link.target = "_blank";
          }
        });
      }
    },

    deepMerge: (target, source) => {
      for (const key in source) {
        if (source.hasOwnProperty(key)) {
          const sourceVal = source[key];
          const targetVal = target[key];

          // 双方都是纯对象且目标对象存在该属性时递归合并
          if (
            typeof sourceVal === 'object' &&
            sourceVal !== null &&
            !Array.isArray(sourceVal) &&
            target.hasOwnProperty(key) &&
            typeof targetVal === 'object' &&
            targetVal !== null &&
            !Array.isArray(targetVal)
          ) {
            theme.utils.deepMerge(targetVal, sourceVal);
          } else {
            target[key] = sourceVal;
          }
        }
      }
      return target;
    }
  };

  theme.HTMLUpdateUtility = {
    /**
     * Used to swap an HTML node with a new node.
     * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
     *
     * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
     */
    viewTransition: (
      oldNode,
      newContent,
      preProcessCallbacks = [],
      postProcessCallbacks = []
    ) => {
      preProcessCallbacks?.forEach((callback) => callback(newContent));

      const newNodeWrapper = document.createElement("div");
      theme.HTMLUpdateUtility.setInnerHTML(
        newNodeWrapper,
        newContent.outerHTML
      );
      const newNode = newNodeWrapper.firstChild;

      // dedupe IDs
      const uniqueKey = Date.now();
      oldNode.querySelectorAll("[id], [form]").forEach((element) => {
        element.id && (element.id = `${element.id}-${uniqueKey}`);
        element.form &&
          element.setAttribute(
            "form",
            `${element.form.getAttribute("id")}-${uniqueKey}`
          );
      });

      oldNode.parentNode.insertBefore(newNode, oldNode);
      oldNode.style.display = "none";

      postProcessCallbacks?.forEach((callback) => callback(newNode));

      setTimeout(() => oldNode.remove(), 500);
    },

    // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    setInnerHTML: (element, html) => {
      element.innerHTML = html;
      element.querySelectorAll("script").forEach((oldScriptTag) => {
        const newScriptTag = document.createElement("script");
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(
          document.createTextNode(oldScriptTag.innerHTML)
        );
        oldScriptTag.parentNode.replaceChild(
          newScriptTag,
          oldScriptTag
        );
      });
    },
  };

  theme.Currency = {
    formatMoney: (cents, format = "") => {
      if (typeof cents === "string") {
        cents = cents.replace(".", "");
      }
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/,
        formatString =
          format || theme.settings.moneyFormat;
      function defaultTo(value2, defaultValue) {
        return value2 == null || value2 !== value2
          ? defaultValue
          : value2;
      }
      function formatWithDelimiters(
        number,
        precision,
        thousands,
        decimal
      ) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ",");
        decimal = defaultTo(decimal, ".");
        if (isNaN(number) || number == null) {
          return 0;
        }
        number = (number / 100).toFixed(precision);
        let parts = number.split("."),
          dollarsAmount = parts[0].replace(
            /(\d)(?=(\d\d\d)+(?!\d))/g,
            "$1" + thousands
          ),
          centsAmount = parts[1] ? decimal + parts[1] : "";
        return dollarsAmount + centsAmount;
      }
      let value = "";
      switch (formatString.match(placeholderRegex)[1]) {
        case "amount":
          value = formatWithDelimiters(cents, 2);
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0);
          break;
        case "amount_with_space_separator":
          value = formatWithDelimiters(cents, 2, " ", ".");
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, 2, ".", ",");
          break;
        case "amount_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 2, "'", ".");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
        case "amount_no_decimals_with_space_separator":
          value = formatWithDelimiters(cents, 0, " ");
          break;
        case "amount_no_decimals_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 0, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
          break;
      }
      if (formatString.indexOf("with_comma_separator") !== -1) {
        return formatString.replace(placeholderRegex, value);
      } else {
        return formatString.replace(placeholderRegex, value);
      }
    },
  };

  theme.pubsub = {
    PUB_SUB_EVENTS: {
      cartUpdate: "cartUpdate",
      quantityUpdate: "quantityUpdate",
      variantChange: "variantChange",
      cartError: "cartError",
      facetUpdate: "facetUpdate",
      quantityRules: "quantityRules",
      quantityBoundries: "quantityBoundries",
      optionValueSelectionChange: "optionValueSelectionChange",
    },

    subscribers: {},

    subscribe: (eventName, callback) => {
      if (theme.pubsub.subscribers[eventName] === undefined) {
        theme.pubsub.subscribers[eventName] = [];
      }

      theme.pubsub.subscribers[eventName] = [
        ...theme.pubsub.subscribers[eventName],
        callback,
      ];

      return function unsubscribe() {
        theme.pubsub.subscribers[eventName] = theme.pubsub.subscribers[
          eventName
        ].filter((cb) => {
          return cb !== callback;
        });
      };
    },

    publish: (eventName, data) => {
      if (theme.pubsub.subscribers[eventName]) {
        theme.pubsub.subscribers[eventName].forEach((callback) => {
          callback(data);
        });
      }
    },
  };

  theme.scrollShadow = {
    Updater: (function () {
      class Updater {
        constructor(targetElement) {
          this.cb = () => this.update(targetElement);
          this.rO = new ResizeObserver(this.cb);
          this.mO = new MutationObserver(() => this.on(this.el));
        }
        on(element) {
          if (this.el) {
            this.el.removeEventListener("scroll", this.cb);
            this.rO.disconnect();
            this.mO.disconnect();
          }
          if (!element) return;
          if (
            element.nodeName === "TABLE" &&
            !/scroll|auto/.test(
              getComputedStyle(element).getPropertyValue(
                "overflow"
              )
            )
          ) {
            this.rO.observe(element);
            element = element.tBodies[0];
          }
          element.addEventListener("scroll", this.cb);
          [element, ...element.children].forEach((el) =>
            this.rO.observe(el)
          );
          this.mO.observe(element, { childList: true });
          this.el = element;
        }
        update(targetElement) {
          const scrollTop = this.el.scrollTop;
          const scrollBottom =
            this.el.scrollHeight -
            this.el.offsetHeight -
            this.el.scrollTop;
          const scrollLeft = this.el.scrollLeft;
          const scrollRight =
            this.el.scrollWidth -
            this.el.offsetWidth -
            this.el.scrollLeft * (theme.config.rtl ? -1 : 1);
          let cssText = `--t: ${
            Math.floor(scrollTop) > 0 ? 1 : 0
          }; --b: ${Math.floor(scrollBottom) > 0 ? 1 : 0}; --l: ${
            Math.floor(scrollLeft) > 0 ? 1 : 0
          }; --r: ${Math.floor(scrollRight) > 0 ? 1 : 0};`;
          if (this.el.nodeName === "TBODY") {
            const clientRect = this.el.getBoundingClientRect();
            const rootClientRect =
              this.el.parentElement.getBoundingClientRect();
            cssText += `top: ${
              clientRect.top - rootClientRect.top
            }px; bottom: ${
              rootClientRect.bottom - clientRect.bottom
            }px; left: ${
              clientRect.left - rootClientRect.left
            }px; right: ${
              rootClientRect.right - clientRect.right
            }px;`;
          }
          requestAnimationFrame(() => {
            targetElement.style.cssText = cssText;
          });
        }
      }

      return Updater;
    })(),
  };

  theme.cookiesEnabled = function () {
    let cookieEnabled = navigator.cookieEnabled;

    if (!cookieEnabled) {
      document.cookie = "testcookie";
      cookieEnabled = document.cookie.indexOf("testcookie") !== -1;
    }
    return cookieEnabled;
  };

  theme.isStorageSupported = function (type) {
    // Return false if we are in an iframe without access to sessionStorage
    if (window.self !== window.top) {
      return false;
    }

    const testKey = "test";
    let storage;
    if (type === "session") {
      storage = window.sessionStorage;
    }
    if (type === "local") {
      storage = window.localStorage;
    }

    try {
      storage.setItem(testKey, "1");
      storage.removeItem(testKey);
      return true;
    } catch (error) {
      // Do nothing, this may happen in Safari in incognito mode
      return false;
    }
  };

  theme.getElementWidth = function (element, value) {
    const style = window.getComputedStyle(element);

    const text = document.createElement("span");
    text.style.fontFamily = style.fontFamily;
    text.style.fontSize = style.fontSize;
    text.style.fontWeight = style.fontWeight;
    text.style.visibility = "hidden";
    text.style.position = "absolute";
    text.innerHTML = value;

    document.body.appendChild(text);
    const width = text.getBoundingClientRect().width;
    text.remove();

    return width;
  };

  theme.HoverButton = (function () {
    class HoverButton {
      constructor(container) {
        this.container = container;
      }

      get btnFill() {
        return this.container.querySelector("[data-fill]");
      }

      load() {
        if (
          theme.config.isTouch ||
          document.body.getAttribute("data-button-hover") === "none"
        )
          return;

        this.container.addEventListener(
          "mouseenter",
          this.onEnterHandler.bind(this)
        );
        this.container.addEventListener(
          "mouseleave",
          this.onLeaveHandler.bind(this)
        );
      }

      onEnterHandler() {
        if (this.btnFill) {
          Motion.animate(
            this.btnFill,
            { y: ["76%", "0%"] },
            { duration: 0.6 }
          );
        }
      }

      onLeaveHandler() {
        if (this.btnFill) {
          Motion.animate(
            this.btnFill,
            { y: "-76%" },
            { duration: 0.6 }
          );
        }
      }

      unload() {
        // todo
      }
    }

    return HoverButton;
  })();

  theme.MagnetButton = (function () {
    const config = {
      magnet: 10,
    };

    class MagnetButton {
      constructor(container) {
        this.container = container;
        this.magnet = container.hasAttribute("data-magnet")
          ? parseInt(container.getAttribute("data-magnet"))
          : config.magnet;
      }

      get btnText() {
        return this.container.querySelector("[data-text]");
      }

      get bounding() {
        return this.container.getBoundingClientRect();
      }

      load() {
        if (
          theme.config.isTouch ||
          document.body.getAttribute("data-button-hover") === "none"
        )
          return;

        this.container.addEventListener(
          "mousemove",
          this.onMoveHandler.bind(this)
        );
        this.container.addEventListener(
          "mouseleave",
          this.onLeaveHandler.bind(this)
        );
      }

      onMoveHandler(event) {
        if (theme.config.motionReduced) return;
        if (this.magnet === 0) return;

        if (this.btnText) {
          Motion.animate(
            this.btnText,
            {
              x:
                ((event.clientX - this.bounding.left) /
                  this.container.offsetWidth -
                  0.5) *
                this.magnet,
              y:
                ((event.clientY - this.bounding.top) /
                  this.container.offsetHeight -
                  0.5) *
                this.magnet,
            },
            { duration: 1.5, easing: Motion.spring() }
          );
        } else {
          Motion.animate(
            this.container,
            {
              x:
                ((event.clientX - this.bounding.left) /
                  this.container.offsetWidth -
                  0.5) *
                this.magnet,
              y:
                ((event.clientY - this.bounding.top) /
                  this.container.offsetHeight -
                  0.5) *
                this.magnet,
            },
            { duration: 1.5, easing: Motion.spring() }
          );
        }
      }

      onLeaveHandler() {
        if (theme.config.motionReduced) return;
        if (this.magnet === 0) return;

        if (this.btnText) {
          Motion.animate(
            this.btnText,
            { x: 0, y: 0 },
            { duration: 1.5, easing: Motion.spring() }
          );
        } else {
          Motion.animate(
            this.container,
            { x: 0, y: 0 },
            { duration: 1.5, easing: Motion.spring() }
          );
        }
      }

      unload() {
        // todo
      }
    }

    return MagnetButton;
  })();

  theme.RevealButton = (function () {
    const config = {
      magnet: 25,
    };

    class RevealButton {
      constructor(container) {
        this.container = container;
        this.magnet = container.hasAttribute("data-magnet")
          ? parseInt(container.getAttribute("data-magnet"))
          : config.magnet;
      }

      get btnText() {
        return this.container.querySelector("[data-text]");
      }

      get btnReveal() {
        return this.container.querySelector("[data-reveal]");
      }

      get bounding() {
        return this.container.getBoundingClientRect();
      }

      load() {
        if (theme.config.isTouch) return;

        this.container.addEventListener(
          "mousemove",
          this.onMoveHandler.bind(this)
        );
        this.container.addEventListener(
          "mouseleave",
          this.onLeaveHandler.bind(this)
        );
      }

      onMoveHandler(event) {
        if (this.btnReveal) {
          Motion.animate(
            this.btnReveal,
            {
              opacity: 1,
              x:
                ((event.clientX - this.bounding.left) /
                  this.container.offsetWidth -
                  0.5) *
                this.magnet,
              y:
                ((event.clientY - this.bounding.top) /
                  this.container.offsetHeight -
                  0.5) *
                this.magnet,
            },
            { duration: 0.2, easing: "steps(2, start)" }
          );
        } else if (this.btnText) {
          Motion.animate(
            this.btnText,
            {
              x:
                ((event.clientX - this.bounding.left) /
                  this.container.offsetWidth -
                  0.5) *
                this.magnet,
              y:
                ((event.clientY - this.bounding.top) /
                  this.container.offsetHeight -
                  0.5) *
                this.magnet,
            },
            { duration: 1.5, easing: Motion.spring() }
          );
        } else {
          Motion.animate(
            this.container,
            {
              x:
                ((event.clientX - this.bounding.left) /
                  this.container.offsetWidth -
                  0.5) *
                this.magnet,
              y:
                ((event.clientY - this.bounding.top) /
                  this.container.offsetHeight -
                  0.5) *
                this.magnet,
            },
            { duration: 1.5, easing: Motion.spring() }
          );
        }
      }

      onLeaveHandler() {
        if (this.btnReveal) {
          Motion.animate(
            this.btnReveal,
            { x: 0, y: 0, opacity: 0 },
            { duration: 0.2, easing: [0.61, 1, 0.88, 1] }
          );
        } else if (this.btnText) {
          Motion.animate(
            this.btnText,
            { x: 0, y: 0 },
            { duration: 1.5, easing: Motion.spring() }
          );
        } else {
          Motion.animate(
            this.container,
            { x: 0, y: 0 },
            { duration: 1.5, easing: Motion.spring() }
          );
        }
      }

      unload() {
        // todo
      }
    }

    return RevealButton;
  })();

  theme.Animation = (function () {
    class Animation {
      constructor(container) {
        this.container = container;
      }

      get immediate() {
        return this.container.hasAttribute("data-immediate");
      }

      get media() {
        return Array.from(
          this.container.querySelectorAll("img, iframe, svg, g-map")
        );
      }

      get type() {
        return this.container.hasAttribute("data-animate")
          ? this.container.getAttribute("data-animate")
          : "fade-up";
      }

      get delay() {
        return this.container.hasAttribute("data-animate-delay")
          ? parseInt(
              this.container.getAttribute("data-animate-delay")
            ) / 1000
          : 0;
      }

      get paused() {
        return this.container.hasAttribute("paused");
      }

      beforeLoad() {
        if (this.type === "none" || this.paused) return;

        switch (this.type) {
          case "fade-in":
            Motion.animate(
              this.container,
              { opacity: 0 },
              { duration: 0 }
            );
            break;

          case "fade-up":
            Motion.animate(
              this.container,
              {
                transform: "translateY(min(2rem, 90%))",
                opacity: 0,
              },
              { duration: 0 }
            );
            break;

          case "fade-up-large":
            Motion.animate(
              this.container,
              { transform: "translateY(90%)", opacity: 0 },
              { duration: 0 }
            );
            break;

          case "zoom-out":
            Motion.animate(
              this.container,
              { transform: "scale(1.3)" },
              { duration: 0 }
            );
            break;
        }
      }

      async load() {
        if (this.type === "none" || this.paused) return;

        switch (this.type) {
          case "fade-in":
            await Motion.animate(
              this.container,
              { opacity: 1 },
              {
                duration: 1.5,
                delay: this.delay,
                easing: [0.16, 1, 0.3, 1],
              }
            ).finished;
            break;

          case "fade-up":
            await Motion.animate(
              this.container,
              { transform: "translateY(0)", opacity: 1 },
              {
                duration: 1.5,
                delay: this.delay,
                easing: [0.16, 1, 0.3, 1],
              }
            ).finished;
            break;

          case "fade-up-large":
            await Motion.animate(
              this.container,
              { transform: "translateY(0)", opacity: 1 },
              {
                duration: 1,
                delay: this.delay,
                easing: [0.16, 1, 0.3, 1],
              }
            ).finished;
            break;

          case "zoom-out":
            await Motion.animate(
              this.container,
              { transform: "scale(1)" },
              {
                duration: 1.3,
                delay: this.delay,
                easing: [0.16, 1, 0.3, 1],
              }
            ).finished;
            break;
        }

        this.container.classList.add("animate");
      }

      async reset(duration) {
        if (this.type === "none") return;

        switch (this.type) {
          case "fade-in":
            await Motion.animate(
              this.container,
              { opacity: 0 },
              {
                duration: duration ? duration : 1.5,
                delay: this.delay,
                easing: duration ? "none" : [0.16, 1, 0.3, 1],
              }
            ).finished;
            break;

          case "fade-up":
            await Motion.animate(
              this.container,
              {
                transform: "translateY(max(-2rem, -90%))",
                opacity: 0,
              },
              {
                duration: duration ? duration : 1.5,
                delay: this.delay,
                easing: duration ? "none" : [0.16, 1, 0.3, 1],
              }
            ).finished;
            break;

          case "fade-up-large":
            await Motion.animate(
              this.container,
              { transform: "translateY(-90%)", opacity: 0 },
              {
                duration: duration ? duration : 1,
                delay: this.delay,
                easing: duration ? "none" : [0.16, 1, 0.3, 1],
              }
            ).finished;
            break;

          case "zoom-out":
            await Motion.animate(
              this.container,
              { transform: "scale(0)" },
              {
                duration: duration ? duration : 1.3,
                delay: this.delay,
                easing: duration ? "none" : [0.16, 1, 0.3, 1],
              }
            ).finished;
            break;
        }

        this.container.classList.remove("animate");
      }

      reload() {
        if (this.type === "none") return;

        this.container.removeAttribute("paused");
        this.beforeLoad();
        this.load();
      }

      unload() {
        // todo
      }
    }

    return Animation;
  })();

  theme.Carousel = (function () {
    class Carousel {
      constructor(container, options, navigation) {
        this.container = container;
        this.options = options;
        this.prevButton = navigation.previous;
        this.nextButton = navigation.next;
      }

      load() {
        this.slider = new Flickity(this.container, this.options);

        this.prevButton.addEventListener(
          "click",
          this.onPrevButtonClick.bind(this)
        );
        this.nextButton.addEventListener(
          "click",
          this.onNextButtonClick.bind(this)
        );

        this.slider.on("dragStart", this.onDragStartHandler.bind(this));
        this.slider.on("select", this.onSelectHandler.bind(this));
      }

      onDragStartHandler() {
        this.prevRemoveTransform();
        this.nextRemoveTransform();
      }

      onSelectHandler() {
        if (!this.slider.slides[this.slider.selectedIndex - 1]) {
          this.prevButton.disabled = true;
          this.nextButton.disabled = false;
        } else if (!this.slider.slides[this.slider.selectedIndex + 1]) {
          this.prevButton.disabled = false;
          this.nextButton.disabled = true;
        } else {
          this.prevButton.disabled = false;
          this.nextButton.disabled = false;
        }
      }

      onPrevButtonClick(event) {
        event.preventDefault();

        this.slider.previous();
        this.nextRemoveTransform();
      }

      onNextButtonClick(event) {
        event.preventDefault();

        this.slider.next();
        this.prevRemoveTransform();
      }

      prevRemoveTransform() {
        this.prevButton.style.transform = null;
        this.prevButton.querySelector("[data-fill]").style.transform =
          null;
      }

      nextRemoveTransform() {
        this.nextButton.style.transform = null;
        this.nextButton.querySelector("[data-fill]").style.transform =
          null;
      }

      unload() {
        // todo
      }
    }

    return Carousel;
  })();

  // Delay JavaScript until user interaction
  theme.initWhenVisible = (function () {
    class ScriptLoader {
      constructor(callback, delay = 5000) {
        this.loadScriptTimer = setTimeout(callback, delay);
        this.userInteractionEvents = [
          "click",
          "mousemove",
          "keydown",
          "touchstart",
          "touchmove",
          "wheel",
        ];

        this.onScriptLoader = this.triggerScriptLoader.bind(
          this,
          callback
        );
        this.userInteractionEvents.forEach((event) => {
          window.addEventListener(event, this.onScriptLoader, {
            passive: !0,
          });
        });
      }

      triggerScriptLoader(callback) {
        callback();
        clearTimeout(this.loadScriptTimer);
        this.userInteractionEvents.forEach((event) => {
          window.removeEventListener(event, this.onScriptLoader, {
            passive: !0,
          });
        });
      }
    }

    return ScriptLoader;
  })();

  // Improve initial load time by skipping the rendering of offscreen content
  new theme.initWhenVisible(() => {
    document.body.removeAttribute("data-page-rendering");
  });

  // Improve iOS Safari resizing
  theme.windowWidth = window.innerWidth;
  window.addEventListener("resize", () => {
    const { innerWidth } = window;
    if (innerWidth !== theme.windowWidth) {
      theme.windowWidth = innerWidth;
      document.dispatchEvent(new CustomEvent("window:resize"));
    }
  });

  theme.DOMready(theme.headerGroup.init);
  theme.DOMready(theme.utils.setScrollbarWidth);
  theme.DOMready(theme.utils.externalLinksNewTab);
  document.addEventListener(
    "window:resize",
    theme.utils.throttle(theme.utils.setScrollbarWidth)
  );

  theme.ContainerDraggable = function (container) {
    let isDragging = false;
    let startX, scrollLeft;

    container.addEventListener('mousedown', (e) => {
      isDragging = true;
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
      container.style.cursor = 'grabbing'; // 拖动时显示正在抓取的光标
    });

    container.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        container.style.cursor = 'grab';
      }
    });

    container.addEventListener('mouseup', () => {
      isDragging = false;
      container.style.cursor = 'grab';
    });

    container.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 1; // 乘以2可以增加滚动速度
      container.scrollLeft = scrollLeft - walk;
    });
  }

  /*============================================================================
    Things that don't require DOM to be ready
  ==============================================================================*/

  theme.config.hasSessionStorage = theme.isStorageSupported("session");
  theme.config.hasLocalStorage = theme.isStorageSupported("local");

  // Trigger events when going between breakpoints
  const mql = window.matchMedia(theme.config.mediaQuerySmall);
  theme.config.mqlSmall = mql.matches;
  mql.onchange = (mql) => {
    if (mql.matches) {
      theme.config.mqlSmall = true;
      document.dispatchEvent(new CustomEvent("matchSmall"));
    } else {
      theme.config.mqlSmall = false;
      document.dispatchEvent(new CustomEvent("unmatchSmall"));
    }
  };

  // Page has loaded and theme assets are ready
  window.addEventListener("DOMContentLoaded", () => {
    document.body.classList.add("loaded");
    document.dispatchEvent(new CustomEvent("page:loaded"));
  });
  window.addEventListener("pageshow", (event) => {
    // Removes unload class when returning to page via history
    if (event.persisted) {
      document.body.classList.remove("unloading");
    }
  });
  window.addEventListener("beforeunload", () => {
    document.body.classList.add("unloading");
  });
})();

// Prevent vertical scroll while using flickity sliders
new theme.initWhenVisible(() => {
  var e = !1;
  var t;

  document.body.addEventListener(
    "touchstart",
    function (i) {
      if (!i.target.closest(".flickity-slider")) {
        return (e = !1);
        void 0;
      }
      e = !0;
      t = {
        x: i.touches[0].pageX,
        y: i.touches[0].pageY,
      };
    },
    theme.supportsPassive ? { passive: true } : false
  );

  document.body.addEventListener(
    "touchmove",
    function (i) {
      if (e && i.cancelable) {
        var n = {
          x: i.touches[0].pageX - t.x,
          y: i.touches[0].pageY - t.y,
        };
        Math.abs(n.x) > Flickity.defaults.dragThreshold &&
          i.preventDefault();
      }
    },
    theme.supportsPassive ? { passive: false } : false
  );
});

class LoadingBar extends HTMLElement {
  constructor() {
    super();

    document.addEventListener("page:loaded", () => {
      Motion.animate(this, { opacity: 0 }, { duration: 1 }).finished.then(
        () => {
          this.hidden = true;
        }
      );
    });
  }
}
customElements.define("loading-bar", LoadingBar);

class MouseCursor extends HTMLElement {
  constructor() {
    super();

    if (theme.config.isTouch) return;

    this.config = {
      posX: 0,
      posY: 0,
    };

    document.addEventListener("mousemove", (event) => {
      this.config.posX += (event.clientX - this.config.posX) / 4;
      this.config.posY += (event.clientY - this.config.posY) / 4;

      this.style.setProperty("--x", `${this.config.posX}px`);
      this.style.setProperty("--y", `${this.config.posY}px`);
    });
  }
}
customElements.define("mouse-cursor", MouseCursor);

class CustomHeader extends HTMLElement {
  constructor() {
    super();
  }

  get allowTransparent() {
    if (
      document.querySelector(
        ".shopify-section:first-child [allow-transparent-header]"
      )
    ) {
      return true;
    }

    return false;
  }

  get headerSection() {
    return document.querySelector(".header-section");
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    if (Shopify.designMode) {
      const section = this.closest(".shopify-section");
      section.addEventListener(
        "shopify:section:load",
        this.init.bind(this)
      );
      section.addEventListener(
        "shopify:section:unload",
        this.init.bind(this)
      );
      section.addEventListener(
        "shopify:section:reorder",
        this.init.bind(this)
      );
    }
  }

  init() {
    this.setHeight();

    if (this.allowTransparent) {
      this.headerSection.classList.add("header-transparent");
      this.headerSection.classList.add("no-animate");

      setTimeout(() => {
        this.headerSection.classList.remove("no-animate");
      }, 500);
    } else {
      this.headerSection.classList.remove("header-transparent");
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty(
        "--header-height",
        Math.round(this.clientHeight) + "px"
      );

      if (
        this.classList.contains("header--center") &&
        document.documentElement.style.getPropertyValue(
          "--header-nav-height"
        ).length === 0
      ) {
        document.documentElement.style.setProperty(
          "--header-nav-height",
          Math.round(
            document.getElementById("MenuToggle")?.clientHeight
          ) + "px"
        );
      }
    });
  }
}
customElements.define("custom-header", CustomHeader, { extends: "header" });

class StickyHeader extends CustomHeader {
  constructor() {
    super();
    this.alwaysTransparent = false
  }

  get isAlwaysSticky() {
    return this.getAttribute("data-sticky-type") === "always";
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.beforeInit();
  }

  beforeInit() {
    this.headerSection.classList.add("header-sticky");
    this.headerSection.setAttribute(
      "data-sticky-type",
      this.getAttribute("data-sticky-type")
    );

    if (this.isAlwaysSticky) {
      this.headerSection.classList.add("header-sticky");
    }

    window.addEventListener(
      "scroll",
      theme.utils.throttle(this.onScrollHandler.bind(this)),
      false
    );
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;

    if (
      scrollTop >
      this.headerBounds.top +
        this.firstScrollTop +
        this.headerBounds.height
    ) {
      if (!this.alwaysTransparent) {
        this.headerSection.classList.add("header-scrolled");
      }
      document.documentElement.style.setProperty(
        "--sticky-header-height",
        Math.round(this.clientHeight) + "px"
      );
      document.dispatchEvent(
        new CustomEvent("header:scrolled", {
          bubbles: true,
          detail: { scrolled: true },
        })
      );

      if (
        scrollTop >
        this.headerBounds.top +
          this.firstScrollTop +
          this.headerBounds.height * 2
      ) {
        this.headerSection.classList.add("header-nav-scrolled");
        this.headerSection
          .querySelectorAll("details")
          .forEach((details) => {
            if (details.hasAttribute("open")) {
              details.open = false;
            }
          });
      }
    } else {
      this.headerSection.classList.remove("header-scrolled");
      this.headerSection.classList.remove("header-nav-scrolled");
      document.dispatchEvent(
        new CustomEvent("header:scrolled", {
          bubbles: true,
          detail: { scrolled: false },
        })
      );
    }

    if (scrollTop > this.headerBounds.bottom + this.firstScrollTop + 100) {
      if (scrollTop > this.currentScrollTop) {
        this.headerSection.classList.add("header-hidden");
      } else {
        this.headerSection.classList.remove("header-hidden");
      }
    } else {
      this.headerSection.classList.remove("header-hidden");
    }

    this.currentScrollTop = scrollTop;
  }

  setAlwaysTransparent(yes) {
    this.alwaysTransparent = yes
    if (yes) {
      this.headerSection.classList.remove("header-scrolled");
    }
    this.onScrollHandler()
  }

  // 将header隐藏，并禁止on-scroll-up类型的行为
  setHidden(hidden) {
    if (hidden) {
      this.headerSection.classList.add('header-hidden-always');
    } else {
      this.headerSection.classList.remove('header-hidden-always');
    }
  }
}
customElements.define("sticky-header", StickyHeader, { extends: "header" });

class RevealLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.revealButton = new theme.RevealButton(this);
    this.revealButton.load();
  }
}
customElements.define("reveal-link", RevealLink, { extends: "a" });

class HoverLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define("hover-link", HoverLink, { extends: "a" });

class MagnetLink extends HoverLink {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define("magnet-link", MagnetLink, { extends: "a" });

class HoverButton extends HTMLButtonElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();

    if (this.type === "submit" && this.form) {
      this.form.addEventListener("submit", () =>
        this.setAttribute("aria-busy", "true")
      );
    }
    window.addEventListener("pageshow", () =>
      this.removeAttribute("aria-busy")
    );

    Motion.inView(this, this.init.bind(this));
  }

  static get observedAttributes() {
    return ["aria-busy"];
  }

  get contentElement() {
    return (this._contentElement =
      this._contentElement || this.querySelector(".btn-text"));
  }

  get animationElement() {
    return (this._animationElement =
      this._animationElement ||
      document.createRange().createContextualFragment(`
      <span class="btn-loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild);
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  init() {
    this.append(this.animationElement);
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === "true") {
      Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { opacity: 1 }, { duration: 0.15 }],
      ]);
      Motion.animate(
        this.animationElement.children,
        { transform: ["scale(1.6)", "scale(0.6)"] },
        {
          duration: 0.35,
          delay: Motion.stagger(0.35 / 2),
          direction: "alternate",
          repeat: Infinity,
        }
      );
    } else {
      Motion.timeline([
        [this.animationElement, { opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { opacity: 1 }, { duration: 0.15 }],
      ]);
    }
  }
}
customElements.define("hover-button", HoverButton, { extends: "button" });

class BuyItNowButton extends HoverButton {
  constructor() {
    super();
    this.addEventListener('click', () => {
      this.setAttribute("aria-busy", "true")
      document.querySelector('#more-payment-options-link').click();
    })
  }
}
customElements.define("buy-it-now-button", BuyItNowButton, { extends: "button" });

class MagnetButton extends HoverButton {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define("magnet-button", MagnetButton, { extends: "button" });

class HoverElement extends HTMLElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define("hover-element", HoverElement);

class MagnetElement extends HoverElement {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define("magnet-element", MagnetElement);

class AnimateElement extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (theme.config.motionReduced) return;

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media)
        await theme.utils.imageLoaded(this.media);
      this.animation.load();
    });
  }

  reset() {
    this.animation?.reset();
  }

  refresh() {
    this.animation?.reload();
  }
}
customElements.define("animate-element", AnimateElement);

class AnimatePicture extends HTMLPictureElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (theme.config.motionReduced) return;
    if (!document.body.hasAttribute("data-image-animation")) return;

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media)
        await theme.utils.imageLoaded(this.media);
      this.animation.load();
    });
  }
}
customElements.define("animate-picture", AnimatePicture, {
  extends: "picture",
});

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), {
        margin: "200px 0px 200px 0px",
      });
    } else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get items() {
    return (this._items = this._items || Array.from(this.children));
  }

  get autoplay() {
    return this.hasAttribute("autoplay");
  }

  get speed() {
    return this.hasAttribute("autoplay")
      ? parseInt(this.getAttribute("autoplay-speed")) * 1000
      : 5000;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        fade: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        autoPlay: this.autoplay ? this.speed : false,
        on: {
          ready: function () {
            setTimeout(() =>
              this.element.setAttribute("loaded", "")
            );
          },
        },
      });

      this.slider.on("change", this.onChange.bind(this));
      this.addEventListener("slider:previous", () =>
        this.slider.previous()
      );
      this.addEventListener("slider:next", () => this.slider.next());
      this.addEventListener("slider:play", () =>
        this.slider.playPlayer()
      );
      this.addEventListener("slider:pause", () =>
        this.slider.pausePlayer()
      );

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) =>
          this.slider.select(this.items.indexOf(event.target))
        );
      }
    }
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy();
  }

  onChange() {
    this.dispatchEvent(
      new CustomEvent("slider:change", {
        bubbles: true,
        detail: { currentPage: this.slider.selectedIndex },
      })
    );
  }
}
customElements.define("announcement-bar", AnnouncementBar);

class AccordionsDetails extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("toggle", this.onToggle);
  }

  get items() {
    return (this._items =
      this._items ||
      Array.from(
        this.querySelectorAll('details[is="accordion-details"]')
      ));
  }

  onToggle(event) {
    const { current: target, open } = event.detail;
    this.items.forEach((item) => {
      if (item !== target) {
        item.close();
      }
    });

    if (open) {
      let headerHeight = 0;
      if (
        !theme.config.mqlSmall &&
        document.querySelector("header.header")
      ) {
        headerHeight = Math.round(
          document.querySelector("header.header").clientHeight
        );
      }
      setTimeout(() => {
        window.scrollTo({
          top:
            target.getBoundingClientRect().top +
            window.scrollY -
            headerHeight,
          behavior: theme.config.motionReduced ? "auto" : "smooth",
        });
      }, 250);
    }
  }
}
customElements.define("accordions-details", AccordionsDetails);

class AccordionDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this._open = this.hasAttribute("open");
    this.summaryElement = this.querySelector("summary");
    this.contentElement = this.querySelector("summary + *");
    this.setAttribute("aria-expanded", this._open ? "true" : "false");

    this.summaryElement.addEventListener(
      "click",
      this.onSummaryClick.bind(this)
    );

    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", () => {
        if (this.designModeActive) this.open = true;
      });
      this.addEventListener("shopify:block:deselect", () => {
        if (this.designModeActive) this.open = false;
      });
    }

    this.initChildDetails()
  }

  get designModeActive() {
    return true;
  }

  get controlledElement() {
    return this.closest("accordions-details");
  }

  static get observedAttributes() {
    return ["open"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "open") {
      this.setAttribute(
        "aria-expanded",
        newValue === "" ? "true" : "false"
      );
    }
  }

  get open() {
    return this._open;
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      } else {
        value
          ? this.setAttribute("open", "")
          : this.removeAttribute("open");
      }
    }

    this.setAttribute("aria-expanded", value ? "true" : "false");
    this.dispatchEventHandler();
  }

  initChildDetails() {
    let el = this
    const arr = []
    while (true) {
      el = el.nextElementSibling
      if (!el) break
      const isSelfComp = el.tagName.toLowerCase() === 'accordion-details' || el.getAttribute('is') === 'accordion-details'
      if (isSelfComp) {
        if (el.hasAttribute('is-child')) {
          arr.push(el)
        } else {
          break
        }
      }
    }
    for (const el of arr) {
      const childDiv = document.createElement('div')
      childDiv.classList.add('child-detail-wrapper')
      childDiv.appendChild(el)
      this.appendChild(childDiv)
    }
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.open = !this.open;
  }

  close() {
    this._open = false;
    this.transition(false);
  }

  async transition(value) {
    this.style.overflow = "hidden";

    if (value) {
      this.setAttribute("open", "");

      await Motion.timeline([
        [
          this,
          {
            height: [
              `${this.summaryElement.clientHeight}px`,
              `${this.scrollHeight}px`,
            ],
          },
          { duration: 0.25, easing: "ease" },
        ],
        [
          this.contentElement,
          {
            opacity: [0, 1],
            transform: ["translateY(10px)", "translateY(0)"],
          },
          { duration: 0.15, at: "-0.1" },
        ],
      ]).finished;
    } else {
      await Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [
          this,
          {
            height: [
              `${this.clientHeight}px`,
              `${this.summaryElement.clientHeight}px`,
            ],
          },
          { duration: 0.25, at: "<", easing: "ease" },
        ],
      ]).finished;

      this.removeAttribute("open");
    }

    this.style.height = "auto";
    this.style.overflow = "visible";
  }

  dispatchEventHandler() {
    (this.controlledElement ?? this).dispatchEvent(
      new CustomEvent("toggle", {
        bubbles: true,
        detail: { current: this, open: this.open },
      })
    );
  }
}
customElements.define("accordion-details", AccordionDetails, {
  extends: "details",
});

class FooterDetails extends AccordionDetails {
  constructor() {
    super();

    this.load();
    document.addEventListener("matchSmall", this.load.bind(this));
    document.addEventListener("unmatchSmall", this.load.bind(this));
  }

  get designModeActive() {
    return theme.config.mqlSmall;
  }

  get openDefault() {
    return this.hasAttribute("data-opened");
  }

  load() {
    if (!theme.config.mqlSmall || this.openDefault) {
      this.open = true;
    } else {
      if (this.open && !this.openDefault) {
        this.open = false;
      }
    }
  }
}
customElements.define("footer-details", FooterDetails, { extends: "details" });

class GestureElement extends HTMLElement {
  constructor() {
    super();
    this.config = {
      thresholdY: Math.max(25, Math.floor(0.15 * window.innerHeight)),
      velocityThreshold: 10,
      disregardVelocityThresholdY: Math.floor(0.5 * this.clientHeight),
      pressThreshold: 8,
      diagonalSwipes: false,
      diagonalLimit: Math.tan(((45 * 1.5) / 180) * Math.PI),
      longPressTime: 500,
    };

    this.handlers = {
      panstart: [],
      panmove: [],
      panend: [],
      swipeup: [],
      swipedown: [],
      longpress: [],
    };

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.addEventListener(
      "touchstart",
      this.onTouchStart.bind(this),
      theme.supportsPassive ? { passive: true } : false
    );
    this.addEventListener(
      "touchmove",
      this.onTouchMove.bind(this),
      theme.supportsPassive ? { passive: true } : false
    );
    this.addEventListener(
      "touchend",
      this.onTouchEnd.bind(this),
      theme.supportsPassive ? { passive: true } : false
    );
  }

  on(type, fn) {
    if (this.handlers[type]) {
      this.handlers[type].push(fn);
      return {
        type,
        fn,
        cancel: () => this.off(type, fn),
      };
    }
  }

  off(type, fn) {
    if (this.handlers[type]) {
      const idx = this.handlers[type].indexOf(fn);
      if (idx !== -1) {
        this.handlers[type].splice(idx, 1);
      }
    }
  }

  fire(type, event) {
    for (let i = 0; i < this.handlers[type].length; i++) {
      this.handlers[type][i](event);
    }
  }

  onTouchStart(event) {
    this.thresholdY = this.config.thresholdY;
    this.disregardVelocityThresholdY =
      this.config.disregardVelocityThresholdY;
    this.touchStartY = event.changedTouches[0].screenY;
    this.touchMoveY = null;
    this.touchEndY = null;
    this.swipingDirection = null;

    // Long press.
    this.longPressTimer = setTimeout(
      () => this.fire("longpress", event),
      this.config.longPressTime
    );
    this.fire("panstart", event);
  }

  onTouchMove(event) {
    const touchMoveY =
      event.changedTouches[0].screenY - (this.touchStartY ?? 0);
    this.velocityY = touchMoveY - (this.touchMoveY ?? 0);
    this.touchMoveY = touchMoveY;
    const absTouchMoveY = Math.abs(this.touchMoveY);
    this.swipingVertical = absTouchMoveY > this.thresholdY;
    this.swipingDirection = this.swipingVertical
      ? "vertical"
      : "pre-vertical";

    if (absTouchMoveY > this.config.pressThreshold) {
      clearTimeout(this.longPressTimer ?? undefined);
    }
    this.fire("panmove", event);
  }

  onTouchEnd(event) {
    this.touchEndY = event.changedTouches[0].screenY;
    this.fire("panend", event);
    clearTimeout(this.longPressTimer ?? undefined);

    const y = this.touchEndY - (this.touchStartY ?? 0);
    const absY = Math.abs(y);

    if (absY > this.thresholdY) {
      this.swipedVertical = this.config.diagonalSwipes
        ? y <= this.config.diagonalLimit
        : absY > this.thresholdY;
      if (this.swipedVertical) {
        if (y < 0) {
          // Upward swipe.
          if (
            (this.velocityY ?? 0) <
              -this.config.velocityThreshold ||
            y < -this.disregardVelocityThresholdY
          ) {
            this.fire("swipeup", event);
          }
        } else {
          // Downward swipe.
          if (
            (this.velocityY ?? 0) > this.config.velocityThreshold ||
            y > this.disregardVelocityThresholdY
          ) {
            this.fire("swipedown", event);
          }
        }
      }
    }
  }
}
customElements.define("gesture-element", GestureElement);

class OverlayElement extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("mousemove", this.onMouseMove);
    this.addEventListener("mouseleave", this.onMouseLeave);
    this.addEventListener("mousedown", this.onMouseDown);
    this.addEventListener("mouseup", this.onMouseUp);
  }

  get cursor() {
    return document.querySelector("mouse-cursor");
  }

  onMouseMove() {
    this.cursor.classList.add("active");
  }
  onMouseLeave() {
    this.cursor.classList.remove("active");
  }
  onMouseDown() {
    this.cursor.classList.add("pressed");
  }
  onMouseUp() {
    this.cursor.classList.remove("pressed");
  }
}
customElements.define("overlay-element", OverlayElement);

const lockLayerCount = new WeakMap();
class ModalElement extends HTMLElement {
  constructor() {
    super();

    this.events = {
      afterHide: "modal:afterHide",
      afterShow: "modal:afterShow",
      closeAll: "modal:closeAll",
    };

    this.classes = {
      open: "has-modal-open",
      opening: "has-modal-opening",
    };
  }

  static get observedAttributes() {
    return ["id", "open"];
  }

  get shouldLock() {
    return false;
  }

  get shouldAppendToBody() {
    return false;
  }

  get shouldCloseAll() {
    return false;
  }

  get open() {
    return this.hasAttribute("open");
  }

  get controls() {
    return Array.from(
      document.querySelectorAll(`[aria-controls="${this.id}"]`)
    );
  }

  get overlay() {
    return (this._overlay =
      this._overlay || this.querySelector(".fixed-modal"));
  }

  get gesture() {
    return (this._gesture =
      this._gesture || this.querySelector("gesture-element"));
  }

  get designMode() {
    return this.hasAttribute("shopify-design-mode");
  }

  get focusElement() {
    return this.querySelector("button");
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.controls.forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this), {
        signal: this.abortController.signal,
      })
    );
    document.addEventListener(
      "keyup",
      (event) => event.code === "Escape" && this.hide(),
      { signal: this.abortController.signal }
    );
    document.addEventListener(this.events.closeAll, () => this.hide(), {
      signal: this.abortController.signal,
    });

    if (this.gesture) {
      this.gestureConfig = {
        animationFrame: null,
        moveY: 0,
        maxGestureDistance: 0,
        endPoint: 0,
        layerHeight: null,
      };

      this.gestureWrap = this.gesture.parentElement;

      setTimeout(() => {
        this.gesture.on("panstart", this.onPanStart.bind(this));
        this.gesture.on("panmove", this.onPanMove.bind(this));
        this.gesture.on("panend", this.onPanEnd.bind(this));
      }, 75);
    }

    if (Shopify.designMode && this.designMode) {
      const section = this.closest(".shopify-section");
      section.addEventListener(
        "shopify:section:select",
        (event) => this.show(null, !event.detail.load),
        { signal: this.abortController.signal }
      );
      section.addEventListener(
        "shopify:section:deselect",
        this.hide.bind(this),
        { signal: this.abortController.signal }
      );
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "open":
        this.controls.forEach((button) =>
          button.setAttribute(
            "aria-expanded",
            newValue === null ? "false" : "true"
          )
        );

        if (
          oldValue === null &&
          (newValue === "" || newValue === "immediate")
        ) {
          this.hidden = false;
          this.removeAttribute("inert");

          this.originalParentBeforeAppend = null;
          if (
            this.shouldAppendToBody &&
            this.parentElement !== document.body
          ) {
            this.originalParentBeforeAppend = this.parentElement;
            document.body.append(this);
          }
          const showTransitionPromise =
            this.showTransition(newValue !== "immediate") ||
            Promise.resolve();
          showTransitionPromise.then(() => {
            this.afterShow();
            this.dispatchEvent(
              new CustomEvent(this.events.afterShow, {
                bubbles: true,
              })
            );
          });
        } else if (oldValue !== null && newValue === null) {
          this.setAttribute("inert", "");

          const hideTransitionPromise =
            this.hideTransition() || Promise.resolve();
          hideTransitionPromise.then(() => {
            this.afterHide();

            if (!this.hasAttribute("inert")) return;

            if (
              this.parentElement === document.body &&
              this.originalParentBeforeAppend
            ) {
              this.originalParentBeforeAppend.appendChild(this);
              this.originalParentBeforeAppend = null;
            }
            this.dispatchEvent(
              new CustomEvent(this.events.afterHide, {
                bubbles: true,
              })
            );

            this.hidden = true;
          });
        }
        this.dispatchEvent(
          new CustomEvent("toggle", { bubbles: true })
        );

        break;
    }
  }

  onButtonClick(event) {
    event.preventDefault();

    this.open ? this.hide() : this.show(event.currentTarget);
  }

  hide() {
    if (!this.open) return;

    this.beforeHide();
    this.resetGesture();
    this.removeAttribute("open");

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }
  show(activeElement = null, animate = true) {
    if (this.open) return;

    if (this.shouldCloseAll) {
      document.dispatchEvent(new CustomEvent(this.events.closeAll), {
        bubbles: true,
      });
    }

    this.beforeShow();
    this.activeElement = activeElement;
    this.setAttribute("open", animate ? "" : "immediate");

    if (this.shouldLock) {
      document.body.classList.add(this.classes.opening);
    }

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  beforeHide() {}
  beforeShow() {}

  afterHide() {
    setTimeout(() => {
      theme.a11y.removeTrapFocus(this.activeElement);
      if (this.shouldLock) {
        lockLayerCount.set(
          ModalElement,
          lockLayerCount.get(ModalElement) - 1
        );

        document.body.classList.toggle(
          this.classes.open,
          lockLayerCount.get(ModalElement) > 0
        );
      }
    });
  }
  afterShow() {
    theme.a11y.trapFocus(this, this.focusElement);
    if (this.shouldLock) {
      lockLayerCount.set(
        ModalElement,
        lockLayerCount.get(ModalElement) + 1
      );

      document.body.classList.remove(this.classes.opening);
      document.body.classList.add(this.classes.open);
    }
  }

  showTransition() {
    setTimeout(() => {
      this.setAttribute("active", "");
    }, 75);
    return new Promise((resolve) => {
      this.overlay.addEventListener("transitionend", resolve, {
        once: true,
      });
    });
  }
  hideTransition() {
    this.removeAttribute("active");
    return new Promise((resolve) => {
      this.overlay.addEventListener("transitionend", resolve, {
        once: true,
      });
    });
  }

  resetGesture() {
    if (this.gesture) {
      this.gestureWrap.style.transform = "";
      this.gestureWrap.style.transition = "";
      this.overlay.style.opacity = "";
      this.overlay.style.transition = "";
    }
  }

  onPanStart() {
    this.removeTransition(this.gestureWrap, "transform");

    if (this.hasAttribute("open")) {
      if (this.gestureConfig.layerHeight === null) {
        this.gestureConfig.layerHeight =
          this.gestureWrap.getBoundingClientRect().height;
      }
      this.gestureConfig.maxGestureDistance =
        this.gestureConfig.layerHeight - 50;
      this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;
    }
  }

  onPanMove() {
    if (this.gestureConfig.animationFrame) return;

    this.gestureConfig.animationFrame = requestAnimationFrame(() => {
      const clamp = (a, min = 0, max = 1) =>
        Math.min(max, Math.max(min, a));
      const invlerp = (x, y, a) => clamp((a - x) / (y - x));

      // Give an indication of whether we've passed the swiping threshold.
      this.gestureWrap.style.transition = "transform .1s linear";
      this.overlay.style.transition = "opacity .1s linear";

      if (this.hasAttribute("open")) {
        if (this.gestureConfig.layerHeight === null) {
          this.gestureConfig.layerHeight =
            this.gestureWrap.getBoundingClientRect().height;
        }

        this.gestureConfig.moveY = this.gesture.touchMoveY;
        this.gestureConfig.maxGestureDistance =
          this.gestureConfig.layerHeight - 50;

        if (this.gestureConfig.moveY >= 0) {
          this.gestureWrap.style.transform = `translateY(${Math.min(
            this.gestureConfig.moveY,
            this.gestureConfig.maxGestureDistance
          )}px)`;

          this.overlay.style.opacity = invlerp(
            this.gestureConfig.layerHeight,
            0,
            this.gestureConfig.moveY
          );
        } else {
          this.gestureWrap.style.transform = "translateY(0)";
          this.gestureWrap.style.transition = "";
          this.overlay.style.opacity = "1";
        }
      }

      this.gestureConfig.animationFrame = null;
    });
  }

  onPanEnd() {
    this.gestureConfig.animationFrame === null ||
      cancelAnimationFrame(this.gestureConfig.animationFrame);
    this.gestureConfig.animationFrame = null;
    this.gestureWrap.style.transition = "transform .1s linear";

    if (this.gestureConfig.layerHeight === null) {
      this.gestureConfig.layerHeight =
        this.gestureWrap.getBoundingClientRect().height;
    }

    this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;

    if (this.hasAttribute("open")) {
      this.gestureConfig.moveY = this.gesture.touchMoveY;

      if (this.gestureConfig.moveY < this.gestureConfig.endPoint) {
        this.gestureWrap.style.transform = "translateY(0)";
      } else {
        this.gestureWrap.style.transform = `translateY(${this.gestureConfig.layerHeight}px)`;

        this.gestureWrap.addEventListener(
          this.whichTransitionEvent(),
          () => {
            this.hide();
          },
          { once: true }
        );
      }
    }
  }

  removeTransition(node, transition) {
    const match = node.style.transition.match(
      new RegExp("(?:^|,)\\s*" + transition + "(?:$|\\s|,)[^,]*", "i")
    );
    if (match) {
      const transitionArray = node.style.transition.split("");
      transitionArray.splice(match.index, match[0].length);
      node.style.transition = transitionArray.join("");
    }
  }

  whichTransitionEvent() {
    let t;
    const el = document.createElement("fakeelement");
    const transitions = {
      WebkitTransition: "webkitTransitionEnd",
      MozTransition: "transitionend",
      MSTransition: "msTransitionEnd",
      OTransition: "oTransitionEnd",
      transition: "transitionEnd",
    };

    for (t in transitions) {
      if (el.style[t] !== undefined) {
        return transitions[t];
      }
    }
  }
}
customElements.define("modal-element", ModalElement);
lockLayerCount.set(ModalElement, 0);

class DrawerElement extends ModalElement {
  constructor() {
    super();

    this.events = {
      afterHide: "drawer:afterHide",
      afterShow: "drawer:afterShow",
    };
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define("drawer-element", DrawerElement);

class MenuDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get menuItems() {
    return (this._menuItems =
      this._menuItems ||
      this.querySelectorAll(".drawer__menu:not(.active)>li"));
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
      Motion.animate(
        this.menuItems,
        {
          transform: ["translateX(-20px)", "translateX(0)"],
          opacity: [0, 1],
        },
        {
          duration: 0.6,
          easing: [0.075, 0.82, 0.165, 1],
          delay: Motion.stagger(0.1),
        }
      ).finished.then(() => {
        this.menuItems.forEach((item) => item.removeAttribute("style"));
      });
    }, 300);
  }

  beforeHide() {
    super.beforeHide();

    setTimeout(() => {
      this.querySelectorAll("details[is=menu-details]").forEach(
        (subMenu) => {
          subMenu.onCloseButtonClick();
        }
      );
    }, 300);
  }
}
customElements.define("menu-drawer", MenuDrawer);

class ShareDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get menuItems() {
    return (this._menuItems =
      this._menuItems || this.querySelectorAll(".share-buttons>li"));
  }

  get urlToShare() {
    const urlInput = this.querySelector("input[type=hidden]");
    return urlInput ? urlInput.value : document.location.href;
  }

  connectedCallback() {
    if (navigator.share) {
      this.controls.forEach((button) =>
        button.addEventListener("click", this.shareTo.bind(this))
      );
    } else {
      super.connectedCallback();
    }
  }

  async shareTo(event) {
    event.preventDefault();
    try {
      await navigator.share({
        url: this.urlToShare,
        title: document.title,
      });
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Share canceled by user");
      } else {
        console.error(error);
      }
    }
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
      Motion.animate(
        this.menuItems,
        {
          transform: ["translateY(2.5rem)", "translateY(0)"],
          opacity: [0, 1],
        },
        {
          duration: 0.6,
          easing: [0.075, 0.82, 0.165, 1],
          delay: Motion.stagger(0.1),
        }
      ).finished.then(() => {
        this.menuItems.forEach((item) => item.removeAttribute("style"));
      });
    }, 300);
  }
}
customElements.define("share-drawer", ShareDrawer);

class BackInStockDrawer extends DrawerElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      this.init();
    } else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get submited() {
    return this.querySelector(".alert") !== null;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    // Open modal if errors or success message exist
    if (this.submited) {
      setTimeout(() => {
        this.show();
      }, 1000);
    }
  }
}
customElements.define("back-in-stock-drawer", BackInStockDrawer);

class MenuDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener("click", this.onSummaryClick.bind(this));
    this.closeButton?.addEventListener(
      "click",
      this.onCloseButtonClick.bind(this)
    );

    if (this.hasAttribute('auto-open')) {
      this.setAttribute("open", "");
      this.parent.classList.add("active");
      this.classList.add("active");
      this.summary.setAttribute("aria-expanded", true);
    }
  }

  get parent() {
    return this.closest("[data-parent]");
  }

  get summary() {
    return this.querySelector("summary");
  }

  get closeButton() {
    return this.querySelector("[data-close]");
  }

  onSummaryClick(event) {
    event.preventDefault();

    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.onCloseButtonClick()
      return
    }

    this.setAttribute("open", "");

    setTimeout(() => {
      this.parent.classList.add("active");
      this.classList.add("active");
      this.summary.setAttribute("aria-expanded", true);
    }, 100);
  }

  onCloseButtonClick() {
    this.parent.classList.remove("active");
    this.classList.remove("active");
    this.summary.setAttribute("aria-expanded", false);

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        requestAnimationFrame(handleAnimation);
      } else {
        this.removeAttribute("open");
      }
    };

    requestAnimationFrame(handleAnimation);
  }
}
customElements.define("menu-details", MenuDetails, { extends: "details" });

class QuantityLabel extends HTMLLabelElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector(".quantity__rules-cart").append(
      this.animationElement
    );
  }

  static get observedAttributes() {
    return ["aria-busy"];
  }

  get contentElement() {
    return (this._contentElement =
      this._contentElement || this.querySelector(".quantity-cart"));
  }

  get animationElement() {
    return (this._animationElement =
      this._animationElement ||
      document.createRange().createContextualFragment(`
      <span class="btn-loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild);
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === "true") {
      Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { opacity: 1 }, { duration: 0.15 }],
      ]);
      Motion.animate(
        this.animationElement.children,
        { transform: ["scale(1.6)", "scale(0.6)"] },
        {
          duration: 0.35,
          delay: Motion.stagger(0.35 / 2),
          direction: "alternate",
          repeat: Infinity,
        }
      );
    } else {
      Motion.timeline([
        [this.animationElement, { opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { opacity: 1 }, { duration: 0.15 }],
      ]);
    }
  }
}
customElements.define("quantity-label", QuantityLabel, { extends: "label" });

class QuantityInput extends HTMLElement {
  quantityUpdateUnsubscriber = undefined;
  quantityBoundriesUnsubscriber = undefined;
  quantityRulesUnsubscriber = undefined;

  constructor() {
    super();
  }

  get sectionId() {
    return this.getAttribute("data-section-id");
  }

  get productId() {
    return this.getAttribute("data-product-id");
  }

  get input() {
    return this.querySelector("input");
  }

  get value() {
    return this.input.value;
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.buttons = Array.from(this.querySelectorAll("button"));
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.input.addEventListener("focus", () =>
      setTimeout(() => this.input.select())
    );

    this.buttons.forEach(
      (button) =>
        button.addEventListener("click", this.onButtonClick.bind(this)),
      { signal: this.abortController.signal }
    );

    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
    this.quantityBoundriesUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.quantityBoundries,
      this.setQuantityBoundries.bind(this)
    );
    this.quantityRulesUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.quantityRules,
      this.updateQuantityRules.bind(this)
    );
  }

  disconnectedCallback() {
    this.abortController.abort();

    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
    if (this.quantityBoundriesUnsubscriber) {
      this.quantityBoundriesUnsubscriber();
    }
    if (this.quantityRulesUnsubscriber) {
      this.quantityRulesUnsubscriber();
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.name === "plus") {
      if (
        parseInt(this.input.getAttribute("data-min")) >
          parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.getAttribute("data-min");
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.getAttribute("data-min") === previousValue &&
      event.currentTarget.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  onInputChange() {
    if (this.input.value === "") {
      this.input.value = parseInt(this.input.min);
    }
    this.validateQtyRules();
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector('button[name="minus"]');
      if (buttonMinus)
        buttonMinus.toggleAttribute(
          "disabled",
          parseInt(value) <= parseInt(this.input.min)
        );
    }
    if (this.input.max) {
      const buttonPlus = this.querySelector('button[name="plus"]');
      if (buttonPlus)
        buttonPlus.toggleAttribute(
          "disabled",
          parseInt(value) >= parseInt(this.input.max)
        );
    }
  }

  updateQuantityRules({ data: { sectionId, productId, parsedHTML } }) {
    if (sectionId !== this.sectionId || productId !== this.productId)
      return;

    const selectors = [
      ".quantity__input",
      ".quantity__rules",
      ".quantity__label",
    ];
    const quantityFormUpdated = parsedHTML.getElementById(
      `QuantityForm-${sectionId}-${this.productId}`
    );
    const quantityForm = this.closest(
      `#QuantityForm-${sectionId}-${this.productId}`
    );
    for (let selector of selectors) {
      const current = quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;

      if (selector === ".quantity__input") {
        const attributes = [
          "data-cart-quantity",
          "data-min",
          "data-max",
          "step",
        ];
        for (let attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated);
          } else {
            current.removeAttribute(attribute);
          }
        }
      } else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }

  setQuantityBoundries({ data: { sectionId, productId } }) {
    if (sectionId !== this.sectionId || productId !== this.productId)
      return;

    const data = {
      cartQuantity: this.input.hasAttribute("data-cart-quantity")
        ? parseInt(this.input.getAttribute("data-cart-quantity"))
        : 0,
      min: this.input.hasAttribute("data-min")
        ? parseInt(this.input.getAttribute("data-min"))
        : 1,
      max: this.input.hasAttribute("data-max")
        ? parseInt(this.input.getAttribute("data-max"))
        : null,
      step: this.input.hasAttribute("step")
        ? parseInt(this.input.getAttribute("step"))
        : 1,
    };

    let min = data.min;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

    this.input.min = min;

    if (max) {
      this.input.max = max;
    } else {
      this.input.removeAttribute("max");
    }
    this.input.value = min;

    theme.pubsub.publish(
      theme.pubsub.PUB_SUB_EVENTS.quantityUpdate,
      undefined
    );
  }

  reset() {
    this.input.value = this.input.defaultValue;
  }
}
customElements.define("quantity-input", QuantityInput);

class CartCount extends HTMLElement {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText);
  }

  onCartUpdate(event) {
    if (event.cart.errors) return;

    this.innerText = event.cart.item_count;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define("cart-count", CartCount);

class RecentlyViewed extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.hasLocalStorage) {
      this.hidden = true;
      return;
    }

    Motion.inView(this, this.init.bind(this), {
      margin: "600px 0px 600px 0px",
    });
  }

  init() {
    fetch(this.getAttribute("data-url") + this.getQueryString())
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, "text/html")
          .querySelector(".shopify-section");

        const recommendations =
          sectionInnerHTML.querySelector("recently-viewed");
        if (
          recommendations &&
          recommendations.innerHTML.trim().length
        ) {
          this.innerHTML = recommendations.innerHTML;
          this.dispatchEvent(
            new CustomEvent("recentlyViewed:loaded")
          );
        } else {
          this.closest(".recently-section")?.remove();
          this.dispatchEvent(new CustomEvent("is-empty"));
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getQueryString() {
    let recentlyViewed = "[]";
    if (theme.config.hasLocalStorage) {
      recentlyViewed =
        window.localStorage.getItem(
          `${theme.settings.themeName}:recently-viewed`
        ) || "[]";
    }

    const items = new Set(JSON.parse(recentlyViewed));
    if (this.hasAttribute("data-product-id")) {
      items.delete(parseInt(this.getAttribute("data-product-id")));
    }
    return Array.from(items.values(), (item) => `id:${item}`)
      .slice(
        0,
        this.hasAttribute("data-limit")
          ? parseInt(this.getAttribute("data-limit"))
          : 4
      )
      .join(" OR ");
  }
}
customElements.define("recently-viewed", RecentlyViewed);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), {
      margin: "600px 0px 600px 0px",
    });
  }

  init() {
    fetch(this.getAttribute("data-url"))
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, "text/html")
          .querySelector(".shopify-section");

        if (sectionInnerHTML === null) return;

        const recommendations = sectionInnerHTML.querySelector(
          "product-recommendations"
        );
        if (
          recommendations &&
          recommendations.innerHTML.trim().length
        ) {
          this.innerHTML = recommendations.innerHTML;
          this.dispatchEvent(
            new CustomEvent("recommendations:loaded")
          );
        } else {
          this.setAttribute("hidden", "");
          this.closest(".recommendations-section")?.remove();
          this.dispatchEvent(new CustomEvent("is-empty"));
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}
customElements.define("product-recommendations", ProductRecommendations);

class ProductComplementary extends HTMLElement {
  constructor() {
    super();
  }

  get container() {
    return this.closest("product-recommendations");
  }

  get prevButton() {
    return this.container.querySelector("[data-prev-button]");
  }

  get nextButton() {
    return this.container.querySelector("[data-next-button]");
  }

  connectedCallback() {
    if (this.innerHTML.trim().length) {
      if (this.classList.contains("flickity")) {
        this.carousel = new theme.Carousel(
          this,
          {
            prevNextButtons: false,
            adaptiveHeight: true,
            pageDots: false,
            contain: true,
            cellAlign: "center",
            rightToLeft: theme.config.rtl,
          },
          {
            previous: this.prevButton,
            next: this.nextButton,
          }
        );

        this.carousel.load();
      }
    } else {
      this.container.hidden = true;
    }
  }

  disconnectedCallback() {
    if (this.carousel) {
      this.carousel.unload();
    }
  }
}
customElements.define("product-complementary", ProductComplementary);

class MediaElement extends HTMLElement {
  constructor() {
    super();
  }

  get parallax() {
    return this.hasAttribute("data-parallax")
      ? parseFloat(this.getAttribute("data-parallax"))
      : false;
  }

  get direction() {
    return this.hasAttribute("data-parallax-dir")
      ? this.getAttribute("data-parallax-dir")
      : "vertical";
  }

  get media() {
    return Array.from(
      this.querySelectorAll(
        "picture>img, video, iframe, svg, video-media, g-map"
      )
    );
  }

  connectedCallback() {
    if (theme.config.motionReduced || !this.parallax) return;
    this.setupParallax();
  }

  setupParallax() {
    const [scale, translate] = [
      1 + this.parallax,
      (this.parallax * 100) / (1 + this.parallax),
    ];

    if (this.direction === "vertical") {
      Motion.scroll(
        Motion.animate(
          this.media,
          {
            transform: [
              `scale(${scale}) translateY(0)`,
              `scale(${scale}) translateY(${translate}%)`,
            ],
            transformOrigin: ["bottom", "bottom"],
          },
          { easing: "linear" }
        ),
        { target: this, offset: ["start end", "end start"] }
      );
    } else if (this.direction === "horizontal") {
      Motion.scroll(
        Motion.animate(
          this.media,
          {
            transform: [
              `scale(${scale}) translateX(0)`,
              `scale(${scale}) translateX(${translate}%)`,
            ],
            transformOrigin: ["right", "right"],
          },
          { easing: "linear" }
        ),
        { target: this, offset: ["start end", "end start"] }
      );
    } else {
      Motion.scroll(
        Motion.animate(
          this.media,
          {
            transform: [`scale(1)`, `scale(${scale})`],
            transformOrigin: ["center", "center"],
          },
          { easing: "linear" }
        ),
        { target: this, offset: ["start end", "end start"] }
      );
    }
  }
}
customElements.define("media-element", MediaElement);

class SplitWords extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (theme.config.motionReduced) return;
    if (!document.body.hasAttribute("data-title-animation")) return;

    const splitting = Splitting({ target: this, by: "words" });

    splitting[0].words.forEach((item, index) => {
      const wrapper = document.createElement("animate-element");
      wrapper.className = "block";
      wrapper.setAttribute(
        "data-animate",
        this.getAttribute("data-animate")
      );
      wrapper.setAttribute(
        "data-animate-delay",
        (this.hasAttribute("data-animate-delay")
          ? parseInt(this.getAttribute("data-animate-delay"))
          : 0) +
          index * 30
      );

      for (const itemContent of item.childNodes) {
        wrapper.appendChild(itemContent);
      }

      item.appendChild(wrapper);
    });
  }
}
customElements.define("split-words", SplitWords);

class HighlightedText extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.classList.add("animate");
  }
}
customElements.define("highlighted-text", HighlightedText, { extends: "em" });

class MarqueeElement extends HTMLElement {
  constructor() {
    super();

    if (theme.config.motionReduced) return;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), {
        margin: "200px 0px 200px 0px",
      });
    } else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get childElement() {
    return this.firstElementChild;
  }

  get speed() {
    return this.hasAttribute("data-speed")
      ? parseInt(this.getAttribute("data-speed"))
      : 16;
  }

  get maximum() {
    return this.hasAttribute("data-maximum")
      ? parseInt(this.getAttribute("data-maximum"))
      : Math.ceil(this.parentWidth / this.childElementWidth) + 2;
  }

  get direction() {
    return this.hasAttribute("data-direction")
      ? this.getAttribute("data-direction")
      : "left";
  }

  get parallax() {
    return !theme.config.isTouch && this.hasAttribute("data-parallax")
      ? parseFloat(this.getAttribute("data-parallax"))
      : false;
  }

  get parentWidth() {
    return this.getWidth(this);
  }

  get childElementWidth() {
    return this.getWidth(this.childElement);
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.childElementCount === 1) {
      this.childElement.classList.add("animate");

      for (let index = 0; index < this.maximum; index++) {
        this.clone = this.childElement.cloneNode(true);
        this.clone.setAttribute("aria-hidden", true);
        this.appendChild(this.clone);
        this.clone
          .querySelectorAll(".media")
          .forEach((media) => media.classList.remove("loading"));
      }

      this.style.setProperty(
        "--duration",
        `${
          (33 - this.speed) *
          Math.min(
            2.5,
            Math.ceil(this.childElementWidth / this.parentWidth)
          )
        }s`
      );
    }

    if (this.parallax) {
      let translate = (this.parallax * 100) / (1 + this.parallax);
      if (this.direction === "right") {
        translate = translate * -1;
      }
      if (theme.config.rtl) {
        translate = translate * -1;
      }

      Motion.scroll(
        Motion.animate(
          this,
          {
            transform: [
              `translateX(${translate}%)`,
              `translateX(0)`,
            ],
          },
          { easing: "linear" }
        ),
        { target: this, offset: ["start end", "end start"] }
      );
    } else {
      // pause when out of view
      const observer = new IntersectionObserver(
        (entries, _observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.classList.remove("paused");
            } else {
              this.classList.add("paused");
            }
          });
        },
        { rootMargin: "0px 0px 50px 0px" }
      );
      observer.observe(this);
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }
}
customElements.define("marquee-element", MarqueeElement);

class ScrolledImages extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), {
      margin: "200px 0px 200px 0px",
    });
  }

  get parallax() {
    return this.hasAttribute("data-parallax")
      ? parseFloat(this.getAttribute("data-parallax"))
      : false;
  }

  get template() {
    return this.querySelector("template");
  }

  get images() {
    return Array.from(
      this.template.content.querySelectorAll(".scrolled-images__item")
    );
  }

  init() {
    this.beforeInit();

    if (theme.config.motionReduced || !this.parallax) return;
    this.setupParallax();
  }

  beforeInit() {
    const main = this.querySelector(".scrolled-images__main");

    for (let i = 0; i < 3; i++) {
      let images = this.shuffle(this.images);

      if (images.length < 8) {
        let start = 0;
        while (images.length < 8) {
          images.push(images[start].cloneNode(true));
          start++;
        }
      }

      const row = document.createElement("div");
      row.classList = "scrolled-images__row";
      images.forEach((item) => row.appendChild(item.cloneNode(true)));

      main.appendChild(row);
    }
  }

  setupParallax() {
    Array.from(this.querySelectorAll(".scrolled-images__row")).forEach(
      (element, index) => {
        let translate =
          (-1 * this.parallax * 100) / (1 + this.parallax);
        if (theme.config.rtl) {
          translate = translate * -1;
        }

        if (index % 2 === 0) {
          Motion.scroll(
            Motion.animate(
              element,
              {
                transform: [
                  `translateX(${translate}%)`,
                  "translateX(0)",
                ],
              },
              { easing: "linear" }
            ),
            { target: this, offset: Motion.ScrollOffset.Any }
          );
        } else {
          Motion.scroll(
            Motion.animate(
              element,
              {
                transform: [
                  "translateX(0)",
                  `translateX(${translate}%)`,
                ],
              },
              { easing: "linear" }
            ),
            { target: this, offset: Motion.ScrollOffset.Any }
          );
        }
      }
    );
  }

  shuffle(arr) {
    return arr.sort(() => Math.random() - 0.5);
  }
}
customElements.define("scrolled-images", ScrolledImages);

class DropdownElement extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: "has-dropdown",
    };

    this.events = {
      afterHide: "dropdown:afterHide",
      afterShow: "dropdown:afterShow",
    };

    this.detectHoverListener = this.detectHover.bind(this);
    this.controls.forEach((button) => {
      button.addEventListener("click", this.onToggleClicked.bind(this));
      button.addEventListener(
        "mouseenter",
        this.detectHoverListener.bind(this)
      );
      button.addEventListener(
        "mouseleave",
        this.detectHoverListener.bind(this)
      );
    });

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);
  }

  static get observedAttributes() {
    return ["id", "open"];
  }

  get open() {
    return this.hasAttribute("open");
  }

  get controls() {
    return Array.from(
      document.querySelectorAll(`[aria-controls="${this.id}"]`)
    );
  }

  get container() {
    return this.querySelector("*:first-child");
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "open":
        this.controls.forEach((button) =>
          button.setAttribute(
            "aria-expanded",
            newValue === null ? "false" : "true"
          )
        );
        break;
    }
  }

  show() {
    document.body.classList.add(this.classes.bodyClass);
    this.setAttribute("open", "");

    document.addEventListener("click", this.detectClickOutsideListener);
    document.addEventListener("keydown", this.detectEscKeyboardListener);
    document.addEventListener("focusout", this.detectFocusOutListener);

    this.afterShow();

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  hide() {
    document.body.classList.remove(this.classes.bodyClass);
    this.removeAttribute("open");

    document.removeEventListener("click", this.detectClickOutsideListener);
    document.removeEventListener("keydown", this.detectEscKeyboardListener);
    document.removeEventListener("focusout", this.detectFocusOutListener);

    this.afterHide();

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }

  onToggleClicked(event) {
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }

  afterShow() {
    Motion.animate(
      this,
      { opacity: [0, 1], visibility: "visible" },
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    );
    Motion.animate(
      this.container,
      { transform: ["translateY(-105%)", "translateY(0)"] },
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    );
  }

  afterHide() {
    Motion.animate(
      this,
      { opacity: 0, visibility: "hidden" },
      {
        duration: theme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );
    Motion.animate(
      this.container,
      { transform: "translateY(-105%)" },
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    );
  }

  detectClickOutside(event) {
    if (!this.parentElement.contains(event.target)) {
      this.hide();
    }
  }

  detectEscKeyboard(event) {
    if (event.code === "Escape") {
      this.hide();
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.hide();
    }
  }

  detectHover(event) {
    if (theme.config.isTouch) return;

    if (event.type === "mouseenter") {
      this.show();
    } else {
      this.hide();
    }
  }
}
customElements.define("dropdown-element", DropdownElement);

class DropdownLocalization extends DropdownElement {
  constructor() {
    super();
  }

  afterShow() {
    Motion.animate(
      this,
      { opacity: [0, 1], visibility: "visible" },
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    );
  }

  afterHide() {
    Motion.animate(
      this,
      { opacity: 0, visibility: "hidden" },
      {
        duration: theme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );
  }
}
customElements.define("dropdown-localization", DropdownLocalization);

const lockDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: "has-dropdown-menu",
    };

    this.events = {
      afterHide: "menu:afterHide",
      afterShow: "menu:afterShow",
    };

    this._open = this.hasAttribute("open");
    this.summaryElement.addEventListener(
      "click",
      this.onSummaryClicked.bind(this)
    );

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);

    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener(
      "mouseenter",
      this.detectHoverListener.bind(this)
    );
    this.addEventListener(
      "mouseleave",
      this.detectHoverListener.bind(this)
    );
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      } else {
        value
          ? this.setAttribute("open", "")
          : this.removeAttribute("open");
      }
    }
  }

  get summaryElement() {
    return this.firstElementChild;
  }

  get contentElement() {
    return this.lastElementChild;
  }

  get open() {
    return this._open;
  }

  get trigger() {
    return this.hasAttribute("trigger")
      ? this.getAttribute("trigger")
      : "click";
  }

  get level() {
    return this.hasAttribute("level") ? this.getAttribute("level") : "top";
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  onSummaryClicked(event) {
    event.preventDefault();

    const disableLink = this.hasAttribute('disable-link')
    if (
      !theme.config.isTouch &&
      this.trigger === "hover" &&
      !disableLink &&
      this.summaryElement.hasAttribute("data-link") &&
      this.summaryElement.getAttribute("data-link").length > 0
    ) {
      window.location.href =
        this.summaryElement.getAttribute("data-link");
    } else {
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      lockDropdownCount.set(
        DetailsDropdown,
        lockDropdownCount.get(DetailsDropdown) + 1
      );
      document.body.classList.add(this.classes.bodyClass);

      this.setAttribute("open", "");
      this.summaryElement.setAttribute("open", "");
      setTimeout(() => this.contentElement.setAttribute("open", ""), 100);
      document.addEventListener("click", this.detectClickOutsideListener);
      document.addEventListener(
        "keydown",
        this.detectEscKeyboardListener
      );
      document.addEventListener("focusout", this.detectFocusOutListener);
      await this.transitionIn();
      this.shouldReverse();

      return theme.utils.waitForEvent(this, this.events.afterShow);
    } else {
      lockDropdownCount.set(
        DetailsDropdown,
        lockDropdownCount.get(DetailsDropdown) - 1
      );
      document.body.classList.toggle(
        this.classes.bodyClass,
        lockDropdownCount.get(DetailsDropdown) > 0
      );

      this.summaryElement.removeAttribute("open");
      this.contentElement.removeAttribute("open");
      document.removeEventListener(
        "click",
        this.detectClickOutsideListener
      );
      document.removeEventListener(
        "keydown",
        this.detectEscKeyboardListener
      );
      document.removeEventListener(
        "focusout",
        this.detectFocusOutListener
      );
      await this.transitionOut();
      if (!this.open) this.removeAttribute("open");

      return theme.utils.waitForEvent(this, this.events.afterHide);
    }
  }

  async transitionIn() {
    Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: "visible" },
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay: theme.config.motionReduced ? 0 : 0.2,
      }
    );
    const translateY = this.level === "top" ? "-105%" : "2rem";
    return Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, "translateY(0)"] },
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async transitionOut() {
    Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: "hidden" },
      {
        duration: theme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );
    const translateY = this.level === "top" ? "-105%" : "2rem";
    return Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  detectClickOutside(event) {
    if (
      !this.contains(event.target) &&
      !(event.target.closest("details") instanceof DetailsDropdown)
    ) {
      this.open = false;
    }
  }

  detectEscKeyboard(event) {
    if (event.code === "Escape") {
      const targetMenu = event.target.closest("details[open]");
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger !== "hover" || theme.config.isTouch) return;

    if (event.type === "mouseenter") {
      this.open = true;
    } else {
      this.open = false;
    }
  }

  shouldReverse() {
    const maxWidth =
      this.contentElement.offsetLeft +
      this.contentElement.clientWidth * 2;
    if (maxWidth > window.innerWidth) {
      this.contentElement.classList.add("should-reverse");
    }
  }
}
customElements.define("details-dropdown", DetailsDropdown, {
  extends: "details",
});
lockDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    if (Shopify.designMode) {
      this.addEventListener(
        "shopify:block:select",
        () => (this.open = true)
      );
      this.addEventListener(
        "shopify:block:deselect",
        () => (this.open = false)
      );
    }
  }

  async transitionIn() {
    this.contentElement.querySelector("tabs-element")?.unload();
    setTimeout(
      () => {
        this.contentElement.querySelector("tabs-element")?.load();
      },
      theme.config.motionReduced ? 0 : 450
    );

    document.body.classList.add("with-mega");
    return Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: "visible",
        transform: ["translateY(-105%)", "translateY(0)"],
      },
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async transitionOut() {
    document.body.classList.remove("with-mega");
    return Motion.animate(
      this.contentElement.firstElementChild,
      { visibility: "hidden", transform: "translateY(-105%)" },
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }
}
customElements.define("details-mega", DetailsMega, { extends: "details" });

class LocalizationListbox extends HTMLFormElement {
  constructor() {
    super();

    this.items.forEach((item) =>
      item.addEventListener("click", this.onItemClick.bind(this))
    );
  }

  get items() {
    return (this._items =
      this._items || Array.from(this.querySelectorAll("a")));
  }

  get input() {
    return this.querySelector(
      'input[name="locale_code"], input[name="country_code"]'
    );
  }

  get returnTo() {
    return this.querySelector('input[name="return_to"]');
  }

  onItemClick(event) {
    event.preventDefault();

    this.input.value = event.currentTarget.getAttribute("data-value");
    this.returnTo.value = window.location.pathname;
    this.submit();
  }
}
customElements.define("localization-listbox", LocalizationListbox, {
  extends: "form",
});

class LocalizationForm extends HTMLFormElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this));
    } else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.addEventListener("change", this.submit);
  }
}
customElements.define("localization-form", LocalizationForm, {
  extends: "form",
});

const cachedSectionsRenderingAPI = new Map();
class APIButton extends HTMLButtonElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get sectionId() {
    return this.getAttribute("data-section-id");
  }

  get sectionRenderingId() {
    return this.getAttribute("data-id");
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (
      document.getElementById(this.sectionRenderingId) === null ||
      document
        .getElementById(this.sectionRenderingId)
        .hasAttribute("loaded")
    )
      return;

    const url = `${theme.routes.root_url}?section_id=${this.sectionId}`;
    cachedSectionsRenderingAPI.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);

    this.sectionsRenderingAPIListener =
      this.afterSectionsRenderingAPI.bind(this);
    document.addEventListener(
      "sectionsRenderingAPI:cached",
      this.sectionsRenderingAPIListener
    );
  }

  renderSectionFromCache(url) {
    const responseText = cachedSectionsRenderingAPI.get(url);
    if (responseText.length) {
      this.updateSectionRendering(responseText);
    }
  }

  renderSectionFromFetch(url) {
    cachedSectionsRenderingAPI.set(url, "");
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.updateSectionRendering(responseText);
        cachedSectionsRenderingAPI.set(url, responseText);

        document.dispatchEvent(
          new CustomEvent("sectionsRenderingAPI:cached", {
            detail: {
              url,
              sectionId: this.sectionId,
            },
          })
        );
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateSectionRendering(responseText) {
    const parsedHTML = new DOMParser().parseFromString(
      responseText,
      "text/html"
    );
    document
      .getElementById(this.sectionRenderingId)
      .replaceWith(parsedHTML.getElementById(this.sectionRenderingId));

    // remove listener
    document.removeEventListener(
      "sectionsRenderingAPI:cached",
      this.sectionsRenderingAPIListener
    );
  }

  afterSectionsRenderingAPI(event) {
    if (event.detail.sectionId === this.sectionId) {
      this.renderSectionFromCache(event.detail.url);
    }
  }
}
customElements.define("api-button", APIButton, { extends: "button" });

class APIHoverButton extends APIButton {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define("api-hover-button", APIHoverButton, {
  extends: "button",
});

class APIMagnetButton extends APIButton {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define("api-magnet-button", APIMagnetButton, {
  extends: "button",
});

class StickyElement extends HTMLElement {
  constructor() {
    super();

    this.endScroll = window.innerHeight - this.offsetHeight - 500;
    this.currPos = window.scrollY;
    this.screenHeight = window.innerHeight;
    this.stickyElementHeight = this.offsetHeight;
    this.bottomGap = this.offsetHeight < window.innerHeight ? 20 : 0;

    window.addEventListener(
      "scroll",
      theme.utils.throttle(this.onScrollHandler.bind(this)),
      {
        capture: true,
        passive: true,
      }
    );
  }

  get headerHeight() {
    const header = document.querySelector(
      '.header-section header[is="sticky-header"]'
    );
    return header
      ? Math.round(header.clientHeight) + 20
      : this.offsetHeight < window.innerHeight
      ? 20
      : 0;
  }

  get inset() {
    return this.style.getPropertyValue("--inset") || 0;
  }

  onScrollHandler() {
    this.screenHeight = window.innerHeight;
    this.stickyElementHeight = this.offsetHeight;

    this.positionStickySidebar();
  }

  positionStickySidebar() {
    this.endScroll =
      window.innerHeight - this.offsetHeight - this.bottomGap;
    const style = window.getComputedStyle(this);
    const stickyElementTop = parseInt(
      style.insetBlockStart.replace("px", "")
    );

    if (
      this.stickyElementHeight + this.headerHeight + this.bottomGap >
      this.screenHeight
    ) {
      if (window.scrollY < this.currPos) {
        // Scroll up
        if (stickyElementTop < this.headerHeight) {
          this.style.insetBlockStart = `${
            stickyElementTop + this.currPos - window.scrollY
          }px`;
          this.style.setProperty(
            "--inset",
            `${stickyElementTop + this.currPos - window.scrollY}px`
          );
        } else if (
          stickyElementTop >= this.headerHeight &&
          stickyElementTop !== this.headerHeight
        ) {
          this.style.insetBlockStart = `${this.headerHeight}px`;
          this.style.setProperty("--inset", `${this.headerHeight}px`);
        }
      } else {
        // Scroll down
        if (stickyElementTop > this.endScroll) {
          this.style.insetBlockStart = `${
            stickyElementTop + this.currPos - window.scrollY
          }px`;
          this.style.setProperty(
            "--inset",
            `${stickyElementTop + this.currPos - window.scrollY}px`
          );
        } else if (
          stickyElementTop < this.endScroll &&
          stickyElementTop !== this.endScroll
        ) {
          this.style.insetBlockStart = `${this.endScroll}px`;
          this.style.setProperty("--inset", `${this.endScroll}px`);
        }
      }
    } else {
      this.style.insetBlockStart = `${this.headerHeight}px`;
      this.style.setProperty("--inset", `${this.headerHeight}px`);
    }

    this.currPos = window.scrollY;
  }
}
customElements.define("sticky-element", StickyElement);

class ParallaxElement extends HTMLDivElement {
  constructor() {
    super();

    this.load();

    document.addEventListener("matchSmall", this.unload.bind(this));
    document.addEventListener("unmatchSmall", this.load.bind(this));
  }

  get mobileDisabled() {
    return false;
  }

  load() {
    if (theme.config.motionReduced) return;
    if (
      this.mobileDisabled &&
      (theme.config.isTouch || theme.config.mqlSmall)
    )
      return;

    this.motion();
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, {
        transform: [
          `translateY(${this.getAttribute("data-start")})`,
          `translateY(${this.getAttribute("data-stop")})`,
        ],
      }),
      { target: this, offset: Motion.ScrollOffset.Any }
    );
  }

  unload() {
    if (this.animation) {
      this.animation();
      this.style.transform = null;
    }
  }
}
customElements.define("parallax-element", ParallaxElement, { extends: "div" });

class ParallaxOverlay extends HTMLElement {
  constructor() {
    super();

    this.load();

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:unload",
        this.refresh.bind(this)
      );
    }
  }

  refresh() {
    let options = {};
    options[this.getAttribute("data-target")] =
      this.getAttribute("data-stop");

    Motion.animate(this, options, { duration: 0 });
  }

  load() {
    let options = {};
    options[this.getAttribute("data-target")] = [
      this.getAttribute("data-start"),
      this.getAttribute("data-stop"),
    ];

    this.animation = Motion.scroll(Motion.animate(this, options), {
      target: this.parentElement,
      offset: Motion.ScrollOffset.Enter,
    });
  }
}
customElements.define("parallax-overlay", ParallaxOverlay);

class FooterParallax extends ParallaxElement {
  constructor() {
    super();

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:unload",
        this.refresh.bind(this)
      );
    }
  }

  get mobileDisabled() {
    return true;
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, {
        transform: ["translateY(-50%)", "translateY(0)"],
      }),
      { target: this, offset: Motion.ScrollOffset.Enter }
    );
  }

  refresh() {
    if (theme.config.motionReduced) return;
    if (
      this.mobileDisabled &&
      (theme.config.isTouch || theme.config.mqlSmall)
    )
      return;

    setTimeout(() => {
      Motion.animate(
        this,
        { transform: "translateY(0)" },
        { duration: 0 }
      );
    });
  }
}
customElements.define("footer-parallax", FooterParallax, { extends: "div" });

class FooterGroup extends HTMLElement {
  constructor() {
    super();

    this.init();

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:load",
        this.init.bind(this)
      );
    }
  }

  get rounded() {
    return Array.from(this.querySelectorAll(".section--rounded"));
  }

  get sections() {
    return Array.from(this.querySelectorAll(".shopify-section"));
  }

  init() {
    this.sections.forEach((shopifySection, index) => {
      const section = shopifySection.querySelector(".section");
      if (section) {
        section.classList.remove("section--next-rounded");
        section.style.zIndex = this.sections.length - index;
      }
    });

    this.rounded.forEach((section) => {
      const shopifySection = section.closest(".shopify-section");
      let previousShopifySection = shopifySection.previousElementSibling;

      if (previousShopifySection === null) {
        previousShopifySection = document.querySelector(
          ".main-content .shopify-section:last-child"
        );
      }

      if (previousShopifySection) {
        const previousSection =
          previousShopifySection.querySelector(".section");
        if (previousSection) {
          previousSection.classList.add("section--next-rounded");
        }
      }
    });
  }
}
customElements.define("footer-group", FooterGroup);

class CarouselElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), {
      margin: "200px 0px 200px 0px",
    });
  }

  get items() {
    return (this._items = this._items || Array.from(this.children));
  }

  get watchCSS() {
    return this.hasAttribute("watch-css");
  }

  get initialIndex() {
    return parseInt(this.getAttribute("initial-index") || 0);
  }

  init() {
    if (this.items.length > 1) {
      this.carousel = new Flickity(this, {
        watchCSS: this.watchCSS,
        prevNextButtons: false,
        adaptiveHeight: true,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        initialIndex: this.initialIndex,
      });

      this.addEventListener("control:select", (event) =>
        this.select(event.detail.index)
      );
      this.carousel.on("change", this.onChange.bind(this));

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) =>
          this.carousel.select(this.items.indexOf(event.target))
        );
      }
    }
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy();
  }

  select(index = 0, immediate = false) {
    if (!immediate) {
      const { selectedIndex, slides } = this.carousel;

      immediate = Math.abs(index - selectedIndex) > 3;

      if (index === 0 && selectedIndex === slides.length - 1) {
        immediate = false;
      }

      if (index === slides.length - 1 && selectedIndex === 0) {
        immediate = false;
      }
    }
    this.carousel.select(index, false, immediate);
  }

  onChange(index) {
    this.dispatchEvent(
      new CustomEvent("carousel:change", {
        bubbles: true,
        detail: { index },
      })
    );
  }
}
customElements.define("carousel-element", CarouselElement);

class TestimonialsElement extends CarouselElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      setTimeout(() => this.update(), 600);
    });
  }

  update() {
    if (this.carousel) this.carousel.select(0);
  }
}
customElements.define("testimonials-element", TestimonialsElement);

class SecondaryMedia extends HTMLElement {
  constructor() {
    super();

    Motion.inView(
      this,
      () => {
        setTimeout(() => this.init());
      },
      { margin: "200px 0px 200px 0px" }
    );
  }

  get template() {
    return this.previousElementSibling;
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy();
  }

  init() {
    this.appendChild(this.template.content.cloneNode(true));
    this.mediaCount = this.querySelectorAll(".media").length;

    if (this.mediaCount > 1) {
      this.carousel = new Flickity(this, {
        accessibility: false,
        draggable: false,
        pageDots: true,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
      });

      if (this.mediaCount === 2) {
        this.classList.add("without-dots");
      }

      this.addEventListener("mousemove", this.onMoveHandler);
      this.addEventListener("mouseleave", this.onLeaveHandler);
    }
  }

  onMoveHandler(event) {
    if (this.mediaCount === 2) {
      return this.carousel.select(1);
    }

    const { width } = this.carousel.size;
    const mouseX = event.clientX - this.getBoundingClientRect().left;

    if (this.mediaCount === 3) {
      if (mouseX < width / 2) {
        return this.carousel.select(1);
      }
      return this.carousel.select(2);
    }

    if (this.mediaCount === 4) {
      if (mouseX < width / 3) {
        return this.carousel.select(1);
      }
      if (mouseX < (2 * width) / 3) {
        return this.carousel.select(2);
      }
      return this.carousel.select(3);
    }
  }

  onLeaveHandler() {
    this.carousel.select(0);
  }
}
customElements.define("secondary-media", SecondaryMedia);

class MotionList extends HTMLElement {
  constructor() {
    super();

    if (theme.config.motionReduced || this.hasAttribute("motion-reduced"))
      return;

    if (this.hasAttribute("initialized")) return;

    this.unload();
    Motion.inView(this, this.load.bind(this));
  }

  get items() {
    return this.querySelectorAll(".card");
  }

  get itemsToShow() {
    return this.querySelectorAll(".card:not([style])");
  }

  unload() {
    Motion.animate(
      this.items,
      { y: 50, opacity: 0, visibility: "hidden" },
      { duration: 0 }
    );
  }

  load() {
    Motion.animate(
      this.items,
      { y: [50, 0], opacity: [0, 1], visibility: ["hidden", "visible"] },
      {
        duration: theme.config.motionReduced ? 0 : 0.5,
        delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1),
      }
    );
  }

  reload() {
    Motion.animate(
      this.itemsToShow,
      { y: [50, 0], opacity: [0, 1], visibility: ["hidden", "visible"] },
      {
        duration: theme.config.motionReduced ? 0 : 0.5,
        delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1),
      }
    );
  }
}
customElements.define("motion-list", MotionList);

class LazyBackground extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  get image() {
    const style = window.getComputedStyle(this);
    return style.backgroundImage
      ? style.backgroundImage.slice(5, -2).replace("width=1", "width=720")
      : false;
  }

  async init() {
    if (!this.image) return;

    const img = document.createElement("img");
    img.src = this.image;
    img.style.visibility = "hidden";
    img.style.position = "absolute";

    await theme.utils.imageLoaded(img);
    this.style.backgroundImage = `url(${this.image})`;
    img.remove();
  }
}
customElements.define("lazy-background", LazyBackground);

class MenuToggle extends MagnetButton {
  constructor() {
    super();

    this.addEventListener("click", this.onClick);
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  get expanded() {
    return this.getAttribute("aria-expanded") === "true";
  }

  onClick() {
    this.setAttribute("aria-expanded", this.expanded ? "false" : "true");
    if (this.controlledElement)
      this.controlledElement.classList.toggle("active");
  }
}
customElements.define("menu-toogle", MenuToggle, { extends: "button" });

class ScrollShadow extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get template() {
    return this.querySelector("template");
  }

  init() {
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(this.template.content.cloneNode(true));
    this.shadowRoot.addEventListener("slotchange", () => this.load());
    this.updater = new theme.scrollShadow.Updater(
      this.shadowRoot.children[1]
    );

    this.load();
  }

  load() {
    this.updater.on(this.children[0]);
  }

  disconnectedCallback() {
    this.updater?.on();
  }
}
customElements.define("scroll-shadow", ScrollShadow);

class CustomSelect extends HTMLSelectElement {
  constructor() {
    super();

    this.onChange();
    this.addEventListener("change", this.onChange);
  }

  onChange() {
    this.value !== "" || this.selectedIndex === -1
      ? this.setAttribute("selected", "")
      : this.removeAttribute("selected");
  }
}
customElements.define("custom-select", CustomSelect, { extends: "select" });

class GMap extends HTMLElement {
  constructor() {
    super();

    if (
      !this.hasAttribute("data-api-key") ||
      !this.hasAttribute("data-map-address")
    ) {
      return;
    }

    Motion.inView(this, this.prepMapApi.bind(this), {
      margin: "600px 0px 600px 0px",
    });

    // Global function called by Google on auth errors.
    // Show an auto error message on all map instances.
    window.gm_authFailure = () => {
      // Show errors only to merchant in the editor.
      if (Shopify.designMode) {
        window.mapError(theme.strings.authError);
      }
    };

    window.mapError = (error, element) => {
      const container = element
        ? element.closest(".shopify-section")
        : document;
      container.querySelectorAll(".gmap--error").forEach((error) => {
        error.remove();
      });

      container.querySelectorAll("g-map").forEach((map) => {
        const message = document.createElement("div");
        message.classList.add(
          "rte",
          "alert",
          "alert--error",
          "gmap--error"
        );
        message.innerHTML = error;
        map.closest(".with-map").prepend(message);
      });
    };

    window.gmNoop = () => {};
  }

  prepMapApi() {
    this.loadScript()
      .then(this.initMap.bind(this))
      .then(() => {
        setTimeout(() => {
          const container = this.closest(".banner__map");
          if (container && container.previousElementSibling) {
            container.previousElementSibling.classList.remove(
              "opacity-0"
            );
          }
        }, 1e3);
      });
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.getAttribute(
        "data-api-key"
      )}&callback=gmNoop`;
    });
  }

  initMap() {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { address: this.getAttribute("data-map-address") },
      (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK) {
          // Show errors only to merchant in the editor.
          if (Shopify.designMode) {
            let errorMessage;

            switch (status) {
              case "ZERO_RESULTS":
                errorMessage = theme.strings.addressNoResults;
                break;
              case "OVER_QUERY_LIMIT":
                errorMessage = theme.strings.addressQueryLimit;
                break;
              case "REQUEST_DENIED":
                errorMessage = theme.strings.authError;
                break;
              default:
                errorMessage = theme.strings.addressError;
                break;
            }
            window.mapError(errorMessage, this);
          }
        } else {
          const mapOptions = {
            zoom: parseInt(this.getAttribute("data-zoom")),
            center: results[0].geometry.location,
            draggable: true,
            clickableIcons: false,
            scrollwheel: false,
            disableDoubleClickZoom: true,
            disableDefaultUI: true,
          };

          const map = new google.maps.Map(this, mapOptions),
            center = map.getCenter();

          map.setCenter(center);

          const icon = {
            path: "M22.6746 0C10.2174 0 0 8.79169 0 21.5118C0 31.2116 4.33864 38.333 9.26606 42.998C11.7232 45.3243 14.3387 47.0534 16.6674 48.2077C18.9384 49.3333 21.1148 50 22.6746 50C24.2345 50 26.4108 49.3333 28.6818 48.2077C31.0105 47.0534 33.626 45.3243 36.0832 42.998C41.0106 38.333 45.3492 31.2116 45.3492 21.5118C45.3492 8.79169 35.1318 0 22.6746 0ZM29.6514 22.6746C29.6514 26.5278 26.5278 29.6514 22.6746 29.6514C18.8214 29.6514 15.6978 26.5278 15.6978 22.6746C15.6978 18.8214 18.8214 15.6978 22.6746 15.6978C26.5278 15.6978 29.6514 18.8214 29.6514 22.6746Z",
            fillColor: this.getAttribute("data-marker-color"),
            fillOpacity: 1,
            anchor: new google.maps.Point(15, 55),
            strokeWeight: 0,
            scale: 0.7,
          };

          new google.maps.Marker({
            map: map,
            position: map.getCenter(),
            icon: icon,
          });

          const styledMapType = new google.maps.StyledMapType(
            JSON.parse(
              this.parentNode.querySelector("[data-gmap-style]")
                .innerHTML
            )
          );

          map.mapTypes.set("styled_map", styledMapType);
          map.setMapTypeId("styled_map");
        }
      }
    );
  }
}
customElements.define("g-map", GMap);

class GMapLocations extends HTMLUListElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
    this.buttons.forEach((button, index) =>
      button.addEventListener("click", () => (this.selectedIndex = index))
    );

    if (Shopify.designMode) {
      this.addEventListener(
        "shopify:block:select",
        (event) =>
          (this.selectedIndex = this.buttons.indexOf(event.target))
      );
    }
  }

  static get observedAttributes() {
    return ["selected-index"];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute(
      "selected-index",
      Math.min(Math.max(index, 0), this.buttons.length - 1).toString()
    );
  }

  get buttons() {
    return (this._buttons =
      this._buttons ||
      Array.from(
        this.hasAttribute("selector")
          ? this.querySelectorAll(this.getAttribute("selector"))
          : this.children
      ));
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle("active", index === parseInt(newValue));
      button
        .querySelector("[data-map-button]")
        ?.classList.toggle(
          "button--secondary",
          index !== parseInt(newValue)
        );
    });

    if (
      name === "selected-index" &&
      oldValue !== null &&
      oldValue !== newValue
    ) {
      if (this.controlledElement) {
        const button = this.buttons[parseInt(newValue)];
        this.controlledElement.setAttribute(
          "data-map-address",
          button.getAttribute("data-map-address")
        );
        this.controlledElement.initMap();
      }
    }
  }
}
customElements.define("g-map-locations", GMapLocations, { extends: "ul" });

class PreviousButton extends HoverButton {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  load() {
    this.onClickListener = this.onClick.bind(this);
    this.addEventListener("click", this.onClickListener);

    if (this.controlledElement) {
      this.updateStatusListener = this.updateStatus.bind(this);
      this.controlledElement.addEventListener(
        "slider:previousStatus",
        this.updateStatusListener
      );
    }
  }

  unload() {
    this.removeEventListener("click", this.onClickListener);
    if (this.controlledElement) {
      this.controlledElement.removeEventListener(
        "slider:previousStatus",
        this.updateStatusListener
      );
    }
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(
      new CustomEvent("slider:previous", {
        bubbles: true,
        cancelable: true,
      })
    );
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case "hidden":
        this.hidden = true;
        break;

      case "disabled":
        this.disabled = true;
        break;

      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define("previous-button", PreviousButton, { extends: "button" });

class NextButton extends HoverButton {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  load() {
    this.onClickListener = this.onClick.bind(this);
    this.addEventListener("click", this.onClickListener);

    if (this.controlledElement) {
      this.updateStatusListener = this.updateStatus.bind(this);
      this.controlledElement.addEventListener(
        "slider:nextStatus",
        this.updateStatusListener
      );
    }
  }

  unload() {
    this.removeEventListener("click", this.onClickListener);
    if (this.controlledElement) {
      this.controlledElement.removeEventListener(
        "slider:nextStatus",
        this.updateStatusListener
      );
    }
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(
      new CustomEvent("slider:next", { bubbles: true, cancelable: true })
    );
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case "hidden":
        this.hidden = true;
        break;

      case "disabled":
        this.disabled = true;
        break;

      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define("next-button", NextButton, { extends: "button" });

class SliderElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), {
      margin: "200px 0px 200px 0px",
    });
  }

  get looping() {
    return false;
  }

  get items() {
    return (this._items =
      this._items ||
      Array.from(
        this.hasAttribute("selector")
          ? this.querySelectorAll(this.getAttribute("selector"))
          : this.children
      ));
  }

  get itemsToShow() {
    return Array.from(this.items).filter(
      (element) => element.clientWidth > 0
    );
  }

  get itemOffset() {
    if (theme.config.rtl) {
      return this.itemsToShow.length > 1
        ? this.itemsToShow[0].offsetLeft -
            this.itemsToShow[1].offsetLeft
        : 0;
    }

    return this.itemsToShow.length > 1
      ? this.itemsToShow[1].offsetLeft - this.itemsToShow[0].offsetLeft
      : 0;
  }

  get perPage() {
    let elementWidth = this.clientWidth;
    const mql = window.matchMedia("screen and (min-width: 1280px)");
    if (mql.matches) {
      const styles = window.getComputedStyle(this);
      elementWidth =
        this.clientWidth -
        parseFloat(styles.paddingLeft) -
        parseFloat(styles.paddingRight);
    }

    return Math.floor(elementWidth / this.itemOffset);
  }

  get totalPages() {
    return this.itemsToShow.length - this.perPage + 1;
  }

  reset() {
    this._items = Array.from(
      this.hasAttribute("selector")
        ? this.querySelectorAll(this.getAttribute("selector"))
        : this.children
    );
  }

  init() {
    this.hasPendingOnScroll = false;
    this.currentPage = 1;
    this.updateButtons();

    this.addEventListener(
      "scroll",
      theme.utils.debounce(this.update.bind(this), 50)
    );
    this.addEventListener("scrollend", this.scrollend);
    this.addEventListener("slider:previous", this.previous);
    this.addEventListener("slider:next", this.next);

    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) =>
        event.target.scrollIntoView({ behavior: "smooth" })
      );
    }
  }

  previous() {
    this.scrollPosition =
      this.scrollLeft -
      this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition);
  }

  next() {
    this.scrollPosition =
      this.scrollLeft +
      this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition);
  }

  select(selectedIndex, immediate = false) {
    this.scrollPosition =
      this.scrollLeft -
      (this.currentPage - selectedIndex) *
        this.itemOffset *
        (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition, immediate);
  }

  selected(selectedIndex) {
    return this.itemsToShow[selectedIndex];
  }

  scrollend() {
    this.hasPendingOnScroll = false;
    this.dispatchEventHandler();
  }

  update() {
    if (window.onscrollend === void 0) {
      clearTimeout(this.scrollendTimeout);
    }

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(Math.abs(this.scrollLeft) / this.itemOffset) + 1;

    if (this.currentPage !== previousPage) {
      if (!this.hasPendingOnScroll) {
        this.dispatchEventHandler();
      }

      this.itemsToShow.forEach((sliderItem, index) => {
        sliderItem.classList.toggle(
          "selected",
          index + 1 === this.currentPage
        );
      });
    }

    if (window.onscrollend === void 0) {
      this.scrollendTimeout = setTimeout(() => {
        this.dispatchEvent(
          new CustomEvent("scrollend", {
            bubbles: true,
            composed: true,
          })
        );
      }, 75);
    }

    if (this.looping) return;

    this.updateButtons();
  }

  updateButtons() {
    let previousDisabled = this.currentPage === 1;
    let nextDisabled = this.currentPage === this.itemsToShow.length;

    if (this.perPage > 1) {
      previousDisabled =
        previousDisabled ||
        (this.itemsToShow.length > 0 &&
          this.isVisible(this.itemsToShow[0]) &&
          this.scrollLeft === 0);
      nextDisabled =
        nextDisabled ||
        (this.itemsToShow.length > 0 &&
          this.isVisible(
            this.itemsToShow[this.itemsToShow.length - 1]
          ));
    }

    this.dispatchEvent(
      new CustomEvent("slider:previousStatus", {
        bubbles: true,
        detail: {
          status: previousDisabled
            ? nextDisabled
              ? "hidden"
              : "disabled"
            : "visible",
        },
      })
    );
    this.dispatchEvent(
      new CustomEvent("slider:nextStatus", {
        bubbles: true,
        detail: {
          status: nextDisabled
            ? previousDisabled
              ? "hidden"
              : "disabled"
            : "visible",
        },
      })
    );
  }

  isVisible(element, offset = 0) {
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.scrollLeft
    );

    /*
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
    const offsetLeft = element.getBoundingClientRect().left;
    return offsetLeft + element.clientWidth <= lastVisibleSlide && offsetLeft >= this.scrollLeft;
    */
  }

  scrollToPosition(position, immediate = false) {
    this.hasPendingOnScroll = !immediate;

    this.scrollTo({
      left: position,
      behavior: immediate
        ? "instant"
        : theme.config.motionReduced
        ? "auto"
        : "smooth",
    });
  }

  dispatchEventHandler() {
    this.dispatchEvent(
      new CustomEvent("slider:change", {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.itemsToShow[this.currentPage - 1],
        },
      })
    );
  }
}
customElements.define("slider-element", SliderElement);

class SliderDots extends HTMLElement {
  constructor() {
    super();

    new theme.initWhenVisible(this.init.bind(this));
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  get items() {
    return (this._items = this._items || Array.from(this.children));
  }

  get itemsToShow() {
    return Array.from(this.items).filter(
      (element) => element.clientWidth > 0
    );
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.controlledElement) {
      this.controlledElement.addEventListener(
        "slider:change",
        this.onChange.bind(this)
      );

      this.items.forEach((item) => {
        item.addEventListener("click", this.onButtonClick.bind(this));
      });
    }
  }

  reset() {
    this._items = Array.from(this.children);
  }

  onChange(event) {
    this.transitionTo(parseInt(event.detail.currentPage) - 1);

    this.itemsToShow.forEach((item) => {
      item.setAttribute(
        "aria-current",
        parseInt(item.getAttribute("data-index")) ===
          parseInt(event.detail.currentPage)
          ? "true"
          : "false"
      );
    });
  }

  transitionTo(selectedIndex, immediate = false) {
    if (this.itemsToShow[selectedIndex]) {
      const scrollElement = this.hasAttribute("align-selected")
        ? this.closest(this.getAttribute("align-selected"))
        : this;
      scrollElement.scrollTo({
        left:
          this.itemsToShow[selectedIndex].offsetLeft -
          scrollElement.clientWidth / 2 +
          this.itemsToShow[selectedIndex].clientWidth / 2,
        top:
          this.itemsToShow[selectedIndex].offsetTop -
          scrollElement.clientHeight / 2 -
          this.itemsToShow[selectedIndex].clientHeight / 2,
        behavior: immediate
          ? "instant"
          : theme.config.motionReduced
          ? "auto"
          : "smooth",
      });
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;

    this.itemsToShow.forEach((item) => {
      item.setAttribute(
        "aria-current",
        item.getAttribute("data-index") ===
          target.getAttribute("data-index")
          ? "true"
          : "false"
      );
    });

    this.controlledElement.select(
      parseInt(target.getAttribute("data-index"))
    );
  }
}
customElements.define("slider-dots", SliderDots);

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    if (this.hasAttribute("style")) return;

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.style.setProperty(
      "--progress",
      `${
        (parseInt(this.getAttribute("data-value")) * 0.75 * 100) /
        parseInt(this.getAttribute("data-max"))
      }%`
    );
  }
}
customElements.define("progress-bar", ProgressBar);

const onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    if (this.posterElement) {
      this.posterElement.addEventListener(
        "click",
        this.onPosterClick.bind(this)
      );
    }

    if (this.autoplay) {
      Motion.inView(this, this.init.bind(this));
    }
  }

  get posterElement() {
    return this.querySelector('[id^="DeferredPoster-"]');
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  get autoplay() {
    return this.hasAttribute("autoplay");
  }

  get playing() {
    return this.hasAttribute("playing");
  }

  get player() {
    return (this.playerProxy =
      this.playerProxy ||
      new Proxy(this.playerTarget(), {
        get: (target, prop) => {
          return async () => {
            target = await target;
            this.playerHandler(target, prop);
          };
        },
      }));
  }

  static get observedAttributes() {
    return ["playing"];
  }

  onPosterClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.playing) {
      this.paused = true;
      this.pause();
    } else {
      this.paused = false;
      this.play();
    }
  }

  init() {
    if (!this.paused) {
      this.play();
    }
    return () => {
      this.pause();
    };
  }

  play() {
    if (!this.playing) {
      this.player.play();
    }
  }

  replay() {
    const video = this.querySelector('video')
    if (video) {
      video.currentTime = 0
    }
    this.play()
  }

  togglePlay() {
    if (this.playing) {
      this.paused = true;
      this.pause();
    } else {
      this.paused = false;
      this.play();
    }
  }

  pause() {
    if (this.playing) {
      this.player.pause();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "playing") {
      if (oldValue === null && newValue === "") {
        return this.dispatchEvent(
          new CustomEvent("video:play", { bubbles: true })
        );
      }

      if (newValue === null) {
        return this.dispatchEvent(
          new CustomEvent("video:pause", { bubbles: true })
        );
      }
    }
  }
}

class VideoMedia extends DeferredMedia {
  constructor() {
    super();
    this.bufferProgress = 0;
    this.displayProgress = 0;
    this.style.setProperty('--progress', '0%')

    // 如果是本地视频，立即加载
    if (!this.hasAttribute("host")) {
      this.loadLocalVideo();
    }
  }

  // 预先将Video插入到DOM，让视频预加载数据，避免闪屏
  // 原模板代码是播放时才插入video到DOM，这导致闪屏现象。
  loadLocalVideo() {
    const templateElement = this.querySelector("template");
    if (templateElement) {
      const videoElement = templateElement.content.firstElementChild.cloneNode(true);
      this.appendChild(videoElement);
      
      // 设置预加载属性
      const video = this.querySelector("video");
      if (video) {
        video.removeAttribute('poster')
        const poster = video.querySelector('img')
        if (poster) {
          poster.style.display = 'none'
        }
        // 监听加载完成事件
        video.addEventListener('loadedmetadata', () => {
          this.setAttribute("loaded", "");
          this.closest(".media")?.classList.remove("loading");
        });
      }
    }
  }

  playerTarget() {
    if (this.hasAttribute("host")) {
      this.setAttribute("loaded", "");
      this.closest(".media")?.classList.remove("loading");

      return new Promise(async (resolve) => {
        const templateElement = this.querySelector("template");
        if (templateElement) {
          templateElement.replaceWith(
            templateElement.content.firstElementChild.cloneNode(
              true
            )
          );
        }
        const muteVideo =
          this.hasAttribute("autoplay") ||
          window.matchMedia("screen and (max-width: 1023px)").matches;
        const script = document.createElement("script");
        script.type = "text/javascript";
        if (this.getAttribute("host") === "youtube") {
          if (!window.YT || !window.YT.Player) {
            script.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          await onYouTubePromise;
          const player = new YT.Player(this.querySelector("iframe"), {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute("played", "");
                  this.setAttribute("playing", "");
                } else if (
                  event.data === YT.PlayerState.ENDED ||
                  event.data === YT.PlayerState.PAUSED
                ) {
                  this.removeAttribute("playing");
                }
              },
            },
          });
        }
        if (this.getAttribute("host") === "vimeo") {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = "https://player.vimeo.com/api/player.js";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          const player = new Vimeo.Player(
            this.querySelector("iframe")
          );
          if (muteVideo) {
            player.setMuted(true);
          }
          player.on("play", () => {
            this.setAttribute("played", "");
            this.setAttribute("playing", "");
          });
          player.on("pause", () => this.removeAttribute("playing"));
          player.on("ended", () => this.removeAttribute("playing"));
          resolve(player);
        }
      });
    } else {
      // 已经由loadLocalVideo插入到DOM
      // const templateElement = this.querySelector("template");
      // if (templateElement) {
      //  this.appendChild(
      //    templateElement.content.firstElementChild.cloneNode(true)
      //  );
      // }
      // this.setAttribute("loaded", "");
      // this.closest(".media")?.classList.remove("loading");

      const player = this.querySelector("video");
      player.addEventListener("play", () => {
        this.setAttribute("playing", "");
        this.setAttribute("played", "");
        this.removeAttribute("suspended");
        this.dispatchEvent(new CustomEvent('playStart', { bubbles: false, composed: false }));
      });
      player.addEventListener("pause", () => {
        if (!player.seeking && player.paused) {
          this.removeAttribute("playing");
          this.dispatchEvent(new CustomEvent('playStop', { bubbles: false, composed: false }));
        }
      });
      player.addEventListener('ended', () => {
        this.dispatchEvent(new CustomEvent('playComplete', { 
            bubbles: false,
            composed: false
        }));
      });

      
      function updateProgress() {
        // 每次只移动一小部分差距
        this.displayProgress += (this.bufferProgress - this.displayProgress) * 0.3;
        this.style.setProperty('--progress', `${this.displayProgress.toFixed(2)}%`);

        this.dispatchEvent(new CustomEvent('videoProgress', { 
            bubbles: false,
            composed: false,
            detail: {
              progress: this.displayProgress.toFixed(2)
            }
        }));
        
        if (Math.abs(this.bufferProgress - this.displayProgress) > 0.1) {
            requestAnimationFrame(updateProgress.bind(this));
        }
      }
      player.addEventListener('timeupdate', () => {
        const currentTime = player.currentTime;
        const duration = player.duration;
        this.bufferProgress = (currentTime / duration) * 100;
        
        if (isNaN(this.bufferProgress)) this.bufferProgress = 0;

        this.dispatchEvent(new CustomEvent('timeupdate', { 
            bubbles: false,
            composed: false,
            detail: {
              progress: this.bufferProgress.toFixed(2),
              currentTime: currentTime
            }
        }));
        
        if (Math.abs(this.bufferProgress - this.displayProgress) > 1) {
            requestAnimationFrame(updateProgress.bind(this));
        }
      });
      return player;
    }
  }

  playerHandler(target, prop) {
    if (this.getAttribute("host") === "youtube") {
      prop === "play" ? target.playVideo() : target.pauseVideo();
    } else {
      if (prop === "play" && !this.hasAttribute("host")) {
        target.play().catch((error) => {
          if (error.name === "NotAllowedError") {
            this.setAttribute("suspended", "");
            target.controls = true;
            const replacementImageSrc =
              target.previousElementSibling?.currentSrc;
            if (replacementImageSrc) {
              target.poster = replacementImageSrc;
            }
          }
        });
      } else {
        target[prop]();
      }
    }
  }
}
customElements.define("video-media", VideoMedia);

class ModelMedia extends DeferredMedia {
  constructor() {
    super();

    this.player;

    if (this.closeElement) {
      this.closeElement.addEventListener(
        "click",
        this.onCloseClick.bind(this)
      );
    }
  }

  get closeElement() {
    return this.querySelector('[id^="DeferredPosterClose-"]');
  }

  onCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.modelViewerUI) {
      this.modelViewerUI.pause();
    }
  }

  playerTarget() {
    return new Promise(() => {
      this.setAttribute("loaded", "");

      Shopify.loadFeatures([
        {
          name: "shopify-xr",
          version: "1.0",
          onLoad: this.setupShopifyXR.bind(this),
        },
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ]);
    });
  }

  playerHandler(target, prop) {
    target[prop]();
  }

  async setupShopifyXR() {
    if (!window.ShopifyXR) {
      document.addEventListener(
        "shopify_xr_initialized",
        this.setupShopifyXR.bind(this)
      );
      return;
    }

    document
      .querySelectorAll('[id^="ProductJSON-"]')
      .forEach((modelJSON) => {
        window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
        modelJSON.remove();
      });
    window.ShopifyXR.setupXRElements();
  }

  setupModelViewerUI(errors) {
    if (errors) return;

    const modelViewer = this.querySelector("model-viewer");
    if (modelViewer && !modelViewer.hasAttribute("loaded")) {
      modelViewer.setAttribute("loaded", "");
      modelViewer.addEventListener(
        "shopify_model_viewer_ui_toggle_play",
        this.modelViewerPlayed.bind(this)
      );
      modelViewer.addEventListener(
        "shopify_model_viewer_ui_toggle_pause",
        this.modelViewerPaused.bind(this)
      );

      this.modelViewerUI = new Shopify.ModelViewerUI(modelViewer);
    }
  }

  modelViewerPlayed() {
    this.setAttribute("playing", "");
    this.closeElement.removeAttribute("hidden");
    (this.controlledElement ?? this).dispatchEvent(
      new CustomEvent("modelViewer:play", { bubbles: true })
    );
  }

  modelViewerPaused() {
    this.removeAttribute("playing");
    this.closeElement.setAttribute("hidden", "");
    (this.controlledElement ?? this).dispatchEvent(
      new CustomEvent("modelViewer:pause", { bubbles: true })
    );
  }
}
customElements.define("model-media", ModelMedia);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event.target);
      this.updateVariantMetafieldInfo();

      theme.pubsub.publish(
        theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
        {
          data: {
            event,
            target,
            selectedOptionValues: this.selectedOptionValues,
          },
        }
      );
    });

    // Initialize variant metafield info on load
    this.updateVariantMetafieldInfo();
  }

  updateVariantMetafieldInfo() {
    if (
      this.querySelector("[data-selected-variant]") &&
      this.querySelector("[data-variants-metafields]")
    ) {
      // Get the selected variant data from the JSON
      const selectedVariantData = JSON.parse(
        this.querySelector("[data-selected-variant]").textContent
      );
      const variantId = selectedVariantData.id;

      // Get all variants' metafields data
      const variantMetafieldsData = JSON.parse(
        this.querySelector("[data-variants-metafields]").textContent
      );

      // Get the display element
      const variantInfoElement = document.getElementById(
        `VariantMarkInfo-${this.id.split("VariantPicker-")[1]}`
      );

      if (variantInfoElement && variantMetafieldsData[variantId]) {
        variantInfoElement.innerHTML =
          variantMetafieldsData[variantId] || "";
      }
    }
  }

  updateSelectionMetadata(target) {
    if (target.tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.hasAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "");
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll(
        "select option[selected], fieldset input:checked"
      )
    ).map((selector) => selector.getAttribute("data-option-value-id"));
  }
}
customElements.define("variant-selects", VariantSelects);

class ProductInfo extends HTMLElement {
  onVariantChangeUnsubscriber = undefined;
  cartUpdateUnsubscriber = undefined;
  abortController = undefined;
  pendingRequestUrl = null;
  preProcessHtmlCallbacks = [];
  postProcessHtmlCallbacks = [];

  constructor() {
    super();
  }

  get sectionId() {
    return this.hasAttribute("data-original-section-id")
      ? this.getAttribute("data-original-section-id")
      : this.getAttribute("data-section-id");
  }

  get productId() {
    return this.getAttribute("data-product-id");
  }

  get productForm() {
    return this.querySelector('form[is="product-form"]');
  }

  get productStickyForm() {
    return document.getElementById(
      `ProductStickyForm-${this.sectionId}-${this.productId}`
    );
  }

  get pickupAvailability() {
    return this.querySelector("pickup-availability");
  }

  get variantSelectors() {
    return this.querySelector("variant-selects");
  }

  get quantityInput() {
    return this.querySelector("quantity-input input");
  }

  connectedCallback() {
    this.initProductAnimation();

    this.onVariantChangeUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this)
    );

    this.initQuantityHandlers();
    this.dispatchEvent(
      new CustomEvent("product-info:loaded", { bubbles: true })
    );
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber();
    this.cartUpdateUnsubscriber?.();
  }

  initProductAnimation() {
    if (theme.config.motionReduced) return;

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media)
        await theme.utils.imageLoaded(this.media);
      this.animation.load();
    });
  }

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    this.resetProductFormState();

    const productUrl =
      target.getAttribute("data-product-url") ||
      this.pendingRequestUrl ||
      this.getAttribute("data-product-url");
    this.pendingRequestUrl = productUrl;
    const shouldSwapProduct =
      this.getAttribute("data-product-url") !== productUrl;

    if (shouldSwapProduct) {
      window.location.href = productUrl;
      return;
    }

    this.renderProductInfo({
      requestUrl: this.buildRequestUrlWithParams(
        productUrl,
        selectedOptionValues
      ),
      targetId:
        target.tagName === "OPTION"
          ? target.parentElement.id
          : target.id,
      callback: this.handleUpdateProductInfo(),
    });
  }

  resetProductFormState() {
    this.productForm?.handleErrorMessage();
    this.productForm?.toggleSubmitButton(true, "");
    this.productStickyForm?.handleErrorMessage();
    this.productStickyForm?.toggleSubmitButton(true, "");
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = null;
        if (callback !== undefined) {
          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          callback(parsedHTML);
        }
      })
      .then(() => {
        // set focus to last clicked option value
        const activeElement = document.getElementById(targetId);
        if (activeElement === null) return;

        if (activeElement.hasAttribute("align-selected")) {
          const scrollElement = activeElement.closest(
            activeElement.getAttribute("align-selected")
          );
          scrollElement.scrollTo({
            left: activeElement.offsetLeft,
            behavior: "instant",
          });
        }
        setTimeout(() => {
          activeElement.focus({ preventScroll: true });
        }, 100);
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.log("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = [];
    params.push(`section_id=${this.sectionId}`);

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(",")}`);
    }

    return `${url}?${params.join("&")}`;
  }

  handleUpdateProductInfo() {
    return (parsedHTML) => {
      const variant = this.getSelectedVariant(parsedHTML);

      this.pickupAvailability?.update(variant);
      this.updateOptionValues(parsedHTML);
      this.updateURL(variant?.id);
      this.updateVariantInputs(variant?.id);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      const updateSourceFromDestination = (id) => {
        const source = parsedHTML.getElementById(
          `${id}-${this.sectionId}-${this.productId}`
        );
        const destination = document.querySelector(
          `#${id}-${this.sectionId}-${this.productId}`
        );
        if (source && destination) {
          destination.innerHTML = source.innerHTML;
          destination.removeAttribute("hidden");
        }
      };

      updateSourceFromDestination("ProductGallery");
      updateSourceFromDestination("Price");
      updateSourceFromDestination("BuyButtonPrice");
      updateSourceFromDestination("StickyPrice");
      updateSourceFromDestination("Sku");
      updateSourceFromDestination("Inventory");
      updateSourceFromDestination("Volume");
      updateSourceFromDestination("PricePerItem");
      updateSourceFromDestination("BackInStock");
      updateSourceFromDestination("ProductBundle");

      this.updateQuantityRules(
        this.sectionId,
        this.productId,
        parsedHTML
      );
      updateSourceFromDestination("QuantityRules");
      updateSourceFromDestination("QuantityRulesCart");
      updateSourceFromDestination("VolumeNote");

      this.productForm?.toggleSubmitButton(
        !variant.available,
        theme.variantStrings.soldOut
      );
      this.productStickyForm?.toggleSubmitButton(
        !variant.available,
        theme.variantStrings.soldOut
      );

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          productId: this.productId,
          parsedHTML: parsedHTML,
          variant: variant,
        },
      });

      (this.productForm ?? document).dispatchEvent(
        new CustomEvent("variant:change", {
          detail: {
            variant: variant,
          },
        })
      );
      document.dispatchEvent(new CustomEvent("document:variant:change", {
        detail: {
          variant: variant,
        },
      }))
    };
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector(
      "[data-selected-variant]"
    )?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  updateOptionValues(parsedHTML) {
    const variantSelects = parsedHTML.getElementById(
      `VariantPicker-${this.sectionId}-${this.productId}`
    );
    if (variantSelects) {
      theme.HTMLUpdateUtility.viewTransition(
        this.variantSelectors,
        variantSelects,
        this.preProcessHtmlCallbacks
      );
    }
  }

  updateURL(variantId) {
    if (!variantId || this.getAttribute("data-update-url") === "false")
      return;

    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set("variant", variantId);
    window.history.replaceState(
      { path: newUrl.toString() },
      "",
      newUrl.toString()
    );
  }

  updateVariantInputs(variantId) {
    if (!variantId) return;

    const productForms = document.querySelectorAll(
      `#ProductForm-${this.sectionId}-${this.productId}, #ProductFormInstallment-${this.sectionId}-${this.productId}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = variantId ?? "";
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  setUnavailable() {
    this.productForm?.toggleSubmitButton(
      true,
      theme.variantStrings.unavailable,
      true
    );
    this.productStickyForm?.toggleSubmitButton(
      true,
      theme.variantStrings.unavailable,
      true
    );

    const selectors = [
      "ProductGallery",
      "Price",
      "BuyButtonPrice",
      "StickyPrice",
      "Inventory",
      "Sku",
      "PricePerItem",
      "BackInStock",
      "ProductBundle",
      "VolumeNote",
      "Volume",
      "QuantityRules",
      "QuantityRulesCart",
    ]
      .map((id) => `#${id}-${this.sectionId}-${this.productId}`)
      .join(", ");
    document
      .querySelectorAll(selectors)
      .forEach((selector) => selector.setAttribute("hidden", ""));
  }

  initQuantityHandlers() {
    if (!this.quantityInput) return;

    this.setQuantityBoundries();
    if (!this.hasAttribute("data-original-section-id")) {
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.fetchQuantityRules.bind(this)
      );
    }
  }

  setQuantityBoundries() {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
      data: {
        sectionId: this.sectionId,
        productId: this.productId,
      },
    });
  }

  fetchQuantityRules() {
    const currentVariantId = this.productForm?.variantIdInput?.value;
    if (!currentVariantId) return;

    this.querySelector('label[is="quantity-label"]')?.setAttribute(
      "aria-busy",
      "true"
    );
    fetch(
      `${this.getAttribute(
        "data-product-url"
      )}?variant=${currentVariantId}&section_id=${this.sectionId}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const parsedHTML = new DOMParser().parseFromString(
          responseText,
          "text/html"
        );
        this.updateQuantityRules(
          this.sectionId,
          this.productId,
          parsedHTML
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.querySelector(
          'label[is="quantity-label"]'
        )?.removeAttribute("aria-busy");
      });
  }

  updateQuantityRules(sectionId, productId, parsedHTML) {
    if (!this.quantityInput) return;

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityRules, {
      data: {
        sectionId,
        productId,
        parsedHTML,
      },
    });

    this.setQuantityBoundries();
  }
}
customElements.define("product-info", ProductInfo);

class ProductForm extends HTMLFormElement {
  constructor() {
    super();

    this.variantIdInput.disabled = false;
    this.addEventListener("submit", this.onSubmitHandler);
  }

  get cartDrawer() {
    return document.querySelector("cart-drawer");
  }

  get submitButton() {
    return (this._submitButton =
      this._submitButton || this.querySelector('[type="submit"]'));
  }

  get submitButtons() {
    return (this._submitButtons =
      this._submitButtons ||
      document.querySelectorAll(
        `[type="submit"][form="${this.getAttribute("id")}"]`
      ));
  }

  get hideErrors() {
    return this.getAttribute("data-hide-errors") === "true";
  }

  get variantIdInput() {
    return this.querySelector('[name="id"]');
  }

  get bundles() {
    return Array.from(
      document.querySelectorAll(
        `[aria-controls="${this.getAttribute(
          "id"
        )}"] input[name="bundles"]:checked`
      )
    );
  }

  onSubmitHandler(event) {
    if (
      document.body.classList.contains("template-cart") ||
      theme.settings.cartType === "page"
    )
      return;

    event.preventDefault();
    if (this.submitButton.hasAttribute("aria-disabled")) return;
    this.activeElement = event.submitter || event.currentTarget;

    this.handleErrorMessage();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const config = theme.utils.fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData(this);
    formData.append("sections", sectionsToBundle);
    formData.append("sections_url", window.location.pathname);

    config.body = formData;

    if (this.bundles.length > 0) {
      let allFormData = {
        items: this.bundles.reverse().map((item) => ({
          id: item.value,
          quantity: 1,
        })),
      };

      const json = {};
      for (const [name, value] of formData.entries()) {
        name == "id" || name == "quantity"
          ? (json[name] = value)
          : (allFormData[name] = value);
      }
      allFormData.items.push(json);

      config.body = JSON.stringify(allFormData);
      config.headers["Content-Type"] = "application/json";
    }

    this.submitButton.setAttribute("aria-disabled", "true");
    this.submitButton.setAttribute("aria-busy", "true");

    fetch(`${theme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          theme.pubsub.publish(
            theme.pubsub.PUB_SUB_EVENTS.cartError,
            {
              source: "product-form",
              productVariantId: formData.get("id"),
              errors:
                parsedState.errors || parsedState.description,
              message: parsedState.message,
            }
          );
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(
            new CustomEvent("ajaxProduct:error", {
              detail: {
                errorMessage: parsedState.description,
              },
            })
          );

          const submitButtonText =
            this.submitButton.querySelector(".btn-text>span");
          if (
            !submitButtonText ||
            !submitButtonText.hasAttribute("data-sold-out")
          )
            return;
          submitButtonText.innerText =
            submitButtonText.getAttribute("data-sold-out");
          this.submitButton.setAttribute("aria-disabled", "true");
          this.error = true;
          return;
        }

        const cartJson = await (
          await fetch(theme.routes.cart_url, {
            ...theme.utils.fetchConfig("json", "GET"),
          })
        ).json();
        cartJson["sections"] = parsedState["sections"];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: "product-form",
          productVariantId: formData.get("id"),
          cart: cartJson,
        });
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", {
            detail: {
              product: parsedState,
            },
          })
        );

        this.cartDrawer?.show(this.activeElement);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.submitButton.removeAttribute("aria-busy");
        this.submitButtons.forEach((submitButton) =>
          submitButton.removeAttribute("aria-busy")
        );

        if (!this.error) {
          this.submitButton.removeAttribute("aria-disabled");
          this.submitButtons.forEach((submitButton) =>
            submitButton.removeAttribute("aria-disabled")
          );
        }
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessage =
      this.errorMessage ||
      this.querySelector(".product-form__error-message");
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute("hidden", !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  toggleSubmitButton(disable = true, text, unavailable = false) {
    if (!this.submitButton) return;

    this.submitButton.removeAttribute("loading");
    this.submitButton.removeAttribute("unavailable");

    const submitButtonText = this.submitButton.querySelector(".btn-text");
    const submitButtonTextChild =
      this.submitButton.querySelector(".btn-text>span");

    if (disable) {
      this.submitButton.setAttribute("disabled", "");
      if (unavailable) this.submitButton.setAttribute("unavailable", "");
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      } else {
        this.submitButton.setAttribute("loading", "");
      }
    } else {
      this.submitButton.removeAttribute("disabled");
      (submitButtonTextChild || submitButtonText).textContent =
        this.submitButton.hasAttribute("data-pre-order")
          ? theme.variantStrings.preOrder
          : theme.variantStrings.addToCart;
    }
  }
}
customElements.define("product-form", ProductForm, { extends: "form" });

class ProductStickyForm extends HTMLElement {
  constructor() {
    super();

    this.scopeFrom =
      document.querySelector(".quick-order-list") ||
      document.getElementById(this.getAttribute("form"));
    this.scopeTo = document.querySelector(".footer-group");

    if (!this.scopeFrom || !this.scopeTo) {
      return;
    }

    const intersectionObserver = new IntersectionObserver(
      this.handleIntersection.bind(this)
    );
    intersectionObserver.observe(this.scopeFrom);
    intersectionObserver.observe(this.scopeTo);

    this.onVariantChangedListener = this.onVariantChanged.bind(this);

    this.querySelector('.close-btn')?.addEventListener('click', () => {
      this.style.display = 'none'
    })
  }

  get productForm() {
    return document.forms[this.getAttribute("form")];
  }

  get submitButton() {
    return (this._submitButton =
      this._submitButton || this.querySelector('[type="submit"]'));
  }

  get productOptions() {
    return (this._productOptions =
      this._productOptions ||
      this.querySelector("[data-sticky-product-options]"));
  }

  get productMedia() {
    return (this._productMedia =
      this._productMedia ||
      this.querySelector("[data-sticky-product-media]"));
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this.scopeFrom) {
        this.scopeFromPassed = entry.boundingClientRect.bottom < 0;
      }
      if (entry.target === this.scopeTo) {
        this.scopeToReached = entry.isIntersecting;
      }
    });

    if (this.scopeFromPassed && !this.scopeToReached) {
      Motion.animate(
        this.firstElementChild,
        {
          opacity: 1,
          visibility: "visible",
          transform: ["translateY(15px)", "translateY(0)"],
        },
        { duration: 1, easing: [0.16, 1, 0.3, 1] }
      );
    } else {
      Motion.animate(
        this.firstElementChild,
        {
          opacity: 0,
          visibility: "hidden",
          transform: ["translateY(0)", "translateY(15px)"],
        },
        { duration: 1, easing: [0.16, 1, 0.3, 1] }
      );
    }
  }

  connectedCallback() {
    (this.productForm ?? document).addEventListener(
      "variant:change",
      this.onVariantChangedListener
    );
  }

  disconnectedCallback() {
    (this.productForm ?? document).removeEventListener(
      "variant:change",
      this.onVariantChangedListener
    );
  }

  onVariantChanged(event) {
    this.updateProductMedia(event.detail.variant);
    this.updateProductOptions(event.detail.variant);
  }

  updateProductMedia(currentVariant) {
    if (!currentVariant || !currentVariant.featured_media) return;

    const image = this.productMedia.querySelector("img");
    const newImage = new Image(
      currentVariant.featured_media.preview_image.width,
      currentVariant.featured_media.preview_image.height
    );

    newImage.alt = currentVariant.featured_media.alt;
    newImage.src = currentVariant.featured_media.preview_image.src;
    newImage.srcset = this.generateSrcset(
      currentVariant.featured_media.preview_image
    );
    newImage.sizes = image.sizes;

    this.productMedia.replaceChildren(newImage);
  }

  updateProductOptions(currentVariant) {
    if (!currentVariant) return;

    this.productOptions.innerText = currentVariant.title;
  }

  handleErrorMessage(errorMessage = false) {
    if (this.productForm?.hideErrors) return;

    this.errorMessage =
      this.errorMessage ||
      this.querySelector(".product-form__error-message");
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute("hidden", !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  toggleSubmitButton(disable = true, text) {
    if (!this.submitButton) return;

    const submitButtonText = this.submitButton.querySelector(".btn-text");
    const submitButtonTextChild =
      this.submitButton.querySelector(".btn-text>span");

    if (disable) {
      this.submitButton.setAttribute("disabled", "");
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
    } else {
      this.submitButton.removeAttribute("disabled");
      (submitButtonTextChild || submitButtonText).textContent =
        this.submitButton.hasAttribute("data-pre-order")
          ? theme.variantStrings.preOrder
          : theme.variantStrings.addToCart;
    }
  }

  generateSrcset(image) {
    const widths = this.productMedia
      .getAttribute("data-widths")
      .split(",")
      .map((width) => parseInt(width));
    return widths
      .filter((width) => width <= image.width)
      .map((width) => {
        return `${image.src}&width=${width} ${width}w`;
      })
      .join(", ");
  }
}
customElements.define("product-sticky-form", ProductStickyForm);

class ProductBundleDetails extends AccordionDetails {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onCartUpdate(event) {
    if (event.source === "product-form") {
      Array.from(
        this.querySelectorAll('input[name="bundles"]:checked')
      ).forEach((input) => {
        if (!input.disabled) input.checked = false;
      });
    }
  }
}
customElements.define("product-bundle-details", ProductBundleDetails, {
  extends: "details",
});

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      setTimeout(() => this.pauseAllMedia(), 500);
    });

    (this.productForm ?? document).addEventListener(
      "variant:change",
      this.onVariantChanged.bind(this)
    );

    this.addEventListener("lightbox:open", (event) =>
      this.openZoom(event.detail.index)
    );
    this.sliderGallery.addEventListener(
      "slider:change",
      this.onSlideChange.bind(this)
    );

    this.countMediaGallery();
  }

  get viewInSpaceButton() {
    return this.querySelector("[data-shopify-xr]");
  }

  get productForm() {
    return document.forms[this.getAttribute("form")];
  }

  get sliderGallery() {
    return this.querySelector("slider-element");
  }

  get sliderDots() {
    return this.querySelector("media-dots");
  }

  get mediaPreview() {
    return this.querySelector(".product__preview .product__media");
  }

  get hideVariants() {
    return (this._hideVariants =
      this._hideVariants ||
      this.querySelectorAll(".product__media--variant").length > 0);
  }

  get gangOption() {
    if (this._gangOption) return this._gangOption;
    const mediaItemWithGang =
      this.sliderGallery.querySelector("[data-gang-option]");
    return mediaItemWithGang
      ? (this._gangOption =
          mediaItemWithGang.getAttribute("data-gang-option"))
      : false;
  }

  get photoswipe() {
    if (this._photoswipe) return this._photoswipe;

    const lightbox = new PhotoSwipeLightbox({
      arrowPrevSVG:
        '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      arrowNextSVG:
        '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      closeSVG:
        '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="m7.5 22.5 15-15m-15 0 15 15"/></svg>',
      bgOpacity: 1,
      pswpModule: () => import(theme.settings.pswpModule),

      arrowPrevTitle: theme.strings.previous,
      arrowNextTitle: theme.strings.next,
      closeTitle: theme.strings.close,

      // Recommended options for this plugin
      allowPanToNext: false,
      allowMouseDrag: true,
      wheelToZoom: false,
      returnFocus: true,
      zoom: false,
    });

    lightbox.addFilter(
      "thumbEl",
      (_thumbEl, data) => data["thumbnailElement"]
    );

    lightbox.on("contentLoad", (event) => {
      const { content } = event;

      if (
        content.type === "video" ||
        content.type === "external_video" ||
        content.type === "model"
      ) {
        event.preventDefault();

        // Create a container for video and assign it to the `content.element` property
        content.element = document.createElement("div");
        content.element.className = "pswp__video-container";
        content.element.appendChild(
          content.data.domElement.cloneNode(true)
        );
      }
    });

    lightbox.on("change", () => {
      this.sliderGallery.select(lightbox.pswp.currIndex + 1, true);
    });

    lightbox.on("close", () => {
      const slideSelected = this.sliderGallery.selected(
        lightbox.pswp.currIndex
      );
      lightbox.pswp._lastActiveElement = slideSelected
        ? slideSelected.querySelector("button")
        : document.activeElement;
    });

    lightbox.init();
    return (this._photoswipe = lightbox);
  }

  onVariantChanged(event) {
    const currentVariant = event.detail.variant;
    if (!currentVariant.featured_media) return;

    const newMedia = this.sliderGallery.querySelector(
      `[data-media-id="${currentVariant.featured_media.id}"]`
    );
    if (newMedia === null) return;

    if (this.gangOption) {
      this.sliderGallery.items.forEach(
        (item) =>
          (item.hidden =
            item.getAttribute("data-gang-connect") !==
            newMedia.getAttribute("data-gang-connect"))
      );
      this.sliderGallery.reset();

      if (this.sliderDots) {
        this.sliderDots.items.forEach(
          (item) =>
            (item.hidden =
              item.getAttribute("data-gang-connect") !==
              newMedia.getAttribute("data-gang-connect"))
        );
        this.sliderDots.reset();
        this.sliderDots.resetIndexes();
        this.sliderDots.transitionTo(1, true);
      }
    }

    this.setActiveMedia(
      currentVariant.featured_media.id,
      this.hideVariants
    );

    if (this.mediaPreview) {
      this.sliderGallery
        .querySelectorAll("[data-media-id]")
        .forEach((media) => media.classList.remove("xl:hidden"));
      this.mediaPreview.parentNode.replaceChild(
        newMedia.cloneNode(true),
        this.mediaPreview
      );
      newMedia.classList.add("xl:hidden");
    }

    this.countMediaGallery();
  }

  onSlideChange(event) {
    const activeMedia = event.detail.currentElement;
    this.playActiveMedia(activeMedia);

    if (this.viewInSpaceButton) {
      if (activeMedia.getAttribute("data-media-type") === "model") {
        this.viewInSpaceButton.setAttribute(
          "data-shopify-model3d-id",
          activeMedia.getAttribute("data-media-id")
        );
      } else {
        this.viewInSpaceButton.setAttribute(
          "data-shopify-model3d-id",
          this.viewInSpaceButton.getAttribute(
            "data-shopify-model3d-default-id"
          )
        );
      }
    }
  }

  setActiveMedia(mediaId, prepend) {
    const activeMedia = this.sliderGallery.querySelector(
      `[data-media-id="${mediaId}"]`
    );

    if (prepend) {
      activeMedia.parentElement.prepend(activeMedia);
      this.sliderGallery.reset();

      if (this.sliderDots) {
        const activeThumbnail = this.sliderDots.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        activeThumbnail.parentElement.prepend(activeThumbnail);
        this.sliderDots.reset();
        this.sliderDots.resetIndexes();
      }
    } else {
      this.sliderGallery.select(
        this.sliderGallery.itemsToShow.indexOf(activeMedia) + 1,
        true
      );
    }

    if (this.gangOption) {
      this.sliderGallery.select(1, true);
    }

    if (theme.config.mqlSmall) {
      const quickViewModal = this.closest("quick-view");
      if (quickViewModal) {
        quickViewModal.querySelector(".quick-view__content").scrollTo({
          top: activeMedia.getBoundingClientRect().top,
          behavior: theme.config.motionReduced ? "auto" : "smooth",
        });
      } else {
        window.scrollTo({
          top:
            activeMedia.getBoundingClientRect().top +
            window.scrollY -
            95,
          behavior: theme.config.motionReduced ? "auto" : "smooth",
        });
      }
    }
  }

  playActiveMedia(activeMedia) {
    if (typeof activeMedia === "undefined") return;

    const deferredMedia = activeMedia.querySelector(".deferred-media");

    this.sliderGallery
      .querySelectorAll(".deferred-media")
      .forEach((media) => {
        deferredMedia === media ? media.play() : media.pause();
      });
  }

  pauseAllMedia() {
    this.sliderGallery
      .querySelectorAll("[data-media-id]")
      .forEach((media, index) => {
        if (index > 0) {
          const deferredMedia =
            media.querySelector(".deferred-media");
          if (
            deferredMedia &&
            typeof deferredMedia.pause === "function"
          )
            deferredMedia.pause();
        }
      });
  }

  openZoom(index = 0) {
    let dataSource = this.sliderGallery.itemsToShow.map((media) => {
      const image = media.querySelector("img");

      if (media.getAttribute("data-media-type") === "image") {
        return {
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute("width")),
          height: parseInt(image.getAttribute("height")),
          alt: image.alt,
          thumbCropped: true,
        };
      }

      if (
        media.getAttribute("data-media-type") === "video" ||
        media.getAttribute("data-media-type") === "external_video" ||
        media.getAttribute("data-media-type") === "model"
      ) {
        const video = media.querySelector(".deferred-media");
        return {
          thumbnailElement: image,
          domElement: video,
          type: media.getAttribute("data-media-type"),
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: 800,
          height: 800 / video.getAttribute("data-aspect-ratio"),
          alt: image.alt,
          thumbCropped: true,
        };
      }
    });

    if (this.mediaPreview && this.mediaPreview.offsetParent) {
      if (this.mediaPreview.getAttribute("data-media-type") === "image") {
        const image = this.mediaPreview.querySelector("img");
        dataSource.push({
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute("width")),
          height: parseInt(image.getAttribute("height")),
          alt: image.alt,
          thumbCropped: true,
        });

        if (index === -1) {
          index = dataSource.length - 1;
        }
      }
    }

    this.photoswipe.loadAndOpen(index, dataSource);
  }

  countMediaGallery() {
    let mediaCount = 0;
    this.sliderGallery
      .querySelectorAll("[data-media-id]")
      .forEach((media) => {
        if (
          !media.classList.contains("xl:hidden") &&
          !media.hasAttribute("hidden")
        ) {
          mediaCount++;
        }
      });

    if (this.mediaPreview) {
      mediaCount++;
    }

    mediaCount > 1
      ? this.classList.remove("with-only1")
      : this.classList.add("with-only1");
  }
}
customElements.define("media-gallery", MediaGallery);

class MediaLightboxButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener("click", this.onButtonClick);
  }

  onButtonClick() {
    const media = this.closest("[data-media-id]");
    const sliderGallery = this.closest("slider-element");
    const openIndex = sliderGallery
      ? sliderGallery.itemsToShow.indexOf(media)
      : -1;

    this.dispatchEvent(
      new CustomEvent("lightbox:open", {
        bubbles: true,
        detail: {
          index: openIndex,
        },
      })
    );
  }
}
customElements.define("media-lightbox-button", MediaLightboxButton, {
  extends: "button",
});

class MediaHoverButton extends MediaLightboxButton {
  constructor() {
    super();
  }

  get zoomRatio() {
    return 2;
  }

  onButtonClick(event) {
    if (theme.config.isTouch) {
      super.onButtonClick();
    } else {
      const media = this.closest('[data-media-type="image"]');
      if (media) {
        const image = media.querySelector("img");
        this.gallery = this.closest("slider-element");

        this.magnify(image);
        this.moveWithHover(image, event);
      }
    }
  }

  createOverlay(image) {
    const overlayImage = document.createElement("img");
    overlayImage.setAttribute("src", `${image.src}`);
    const overlay = document.createElement("media-hover-overlay");
    this.prepareOverlay(overlay, overlayImage);

    this.toggleLoadingSpinner(image);

    overlayImage.onload = () => {
      this.toggleLoadingSpinner(image);
      image.parentElement.insertBefore(overlay, image);
    };

    if (this.gallery) this.gallery.classList.add("magnify");

    return overlay;
  }

  prepareOverlay(container, image) {
    container.setAttribute(
      "class",
      "media z-10 absolute top-0 left-0 w-full h-full"
    );
    container.setAttribute("aria-hidden", "true");
    container.style.backgroundImage = `url('${image.src}')`;
    container.style.cursor = "zoom-out";
  }

  toggleLoadingSpinner(image) {
    const loadingSpinner = image.parentElement;
    loadingSpinner.classList.toggle("loading");
    loadingSpinner.classList.toggle("pointer-events-none");
  }

  moveWithHover(image, event) {
    // calculate mouse position
    const ratio = image.height / image.width;
    const container = event.target.getBoundingClientRect();
    const xPosition = event.clientX - container.left;
    const yPosition = event.clientY - container.top;
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

    // determine what to show in the frame
    this.overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    this.overlay.style.backgroundSize = `${image.width * this.zoomRatio}px`;
  }

  magnify(image) {
    this.overlay = this.createOverlay(image);
    this.overlay.onclick = () => this.reset();
    this.overlay.onmousemove = (event) => this.moveWithHover(image, event);
    this.overlay.onmouseleave = () => this.reset();
  }

  reset() {
    this.overlay.remove();

    if (this.gallery) this.gallery.classList.remove("magnify");
  }
}
customElements.define("media-hover-button", MediaHoverButton, {
  extends: "button",
});

class MediaDots extends SliderDots {
  constructor() {
    super();
  }

  init() {
    super.init();
    this.resetIndexes();
  }

  resetIndexes() {
    let newIndex = 1;
    this.itemsToShow.forEach((item, index) => {
      item.setAttribute("data-index", newIndex);
      item.setAttribute("aria-current", index === 0 ? "true" : "false");

      newIndex++;
    });
  }
}
customElements.define("media-dots", MediaDots);

class XModal extends ModalElement {
  constructor() {
    super();
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define("x-modal", XModal);

class LogoList extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), {
      margin: "200px 0px 200px 0px",
    });
  }

  get childElement() {
    return this.firstElementChild;
  }

  get speed() {
    return this.hasAttribute("data-speed")
      ? parseInt(this.getAttribute("data-speed"))
      : 16;
  }

  get maximum() {
    return this.hasAttribute("data-maximum")
      ? parseInt(this.getAttribute("data-maximum"))
      : Math.ceil(this.parentWidth / this.childElementWidth) + 2;
  }

  get direction() {
    return this.hasAttribute("data-direction")
      ? this.getAttribute("data-direction")
      : "left";
  }

  get parentWidth() {
    return this.getWidth(this);
  }

  get childElementWidth() {
    return this.getWidth(this.childElement);
  }

  disconnectedCallback() {
    if (this.marquee) {
      this.pause();
      this.marquee.destroy();
    }
  }

  init() {
    if (this.childElementCount === 1) {
      this.childElement.classList.add("animate");

      for (let index = 0; index < this.maximum; index++) {
        this.clone = this.childElement.cloneNode(true);
        this.clone.setAttribute("aria-hidden", true);
        this.appendChild(this.clone);
        this.clone
          .querySelectorAll(".media")
          .forEach((media) => media.classList.remove("loading"));
      }

      if (theme.config.isTouch) {
        this.style.setProperty("--duration", `${33 - this.speed}s`);
      } else {
        this.marquee = new Flickity(this, {
          prevNextButtons: false,
          pageDots: false,
          wrapAround: true,
          freeScroll: true,
          rightToLeft: this.direction === "right",
        });

        // Set initial position to be 0
        this.marquee.x = 0;

        // Start the marquee animation
        this.play();

        this.addEventListener("mouseenter", this.pause);
        this.addEventListener("mouseleave", this.play);
      }
    }
  }

  play() {
    // Set the decrement of position x
    this.marquee.x -= this.speed * 0.1;

    // Settle position into the slider
    this.marquee.settle(this.marquee.x);

    // Set the requestId to the local variable
    this.requestId = requestAnimationFrame(this.play.bind(this));
  }

  pause() {
    if (this.requestId) {
      // Cancel the animation
      cancelAnimationFrame(this.requestId);

      // Reset the requestId for the next animation.
      this.requestId = undefined;
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }
}
customElements.define("logo-list", LogoList);

class TextScrolling extends HTMLElement {
  constructor() {
    super();

    this.items.forEach((item) => {
      const header = item.querySelector(".heading");

      Motion.scroll(
        Motion.animate(header, { opacity: [0, 0, 1, 1, 1, 0, 0] }),
        { target: header, offsets: ["33vh", "66vh"] }
      );
    });
  }

  get items() {
    return (this._items = this._items || Array.from(this.children));
  }
}
customElements.define("text-scrolling", TextScrolling);

class TabsElement extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
    this.buttons.forEach((button, index) => {
      button.addEventListener("click", () => (this.selectedIndex = index))
      button.addEventListener("mouseenter", () => (this.selectedIndex = index))
    });

    if (Shopify.designMode) {
      this.addEventListener(
        "shopify:block:select",
        (event) =>
          (this.selectedIndex = this.buttons.indexOf(event.target))
      );
    }
  }

  static get observedAttributes() {
    return ["selected-index"];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute(
      "selected-index",
      Math.min(Math.max(index, 0), this.buttons.length - 1).toString()
    );
  }

  get buttons() {
    return (this._buttons =
      this._buttons ||
      Array.from(this.querySelectorAll('button[role="tab"]')));
  }

  get indicators() {
    return (this._indicators =
      this._indicators ||
      Array.from(this.querySelectorAll(".indicators")));
  }

  load() {
    const toButton = this.buttons[parseInt(this.selectedIndex)];
    const toPanel = document.getElementById(
      toButton.getAttribute("aria-controls")
    );
    Motion.animate(
      toPanel,
      {
        transform: ["translateY(2rem)", "translateY(0)"],
        opacity: [0, 1],
      },
      { duration: theme.config.motionReduced ? 0 : 0.15 }
    ).finished;
    toPanel.querySelector("motion-list")?.load();
  }

  unload() {
    const fromButton = this.buttons[parseInt(this.selectedIndex)];
    const fromPanel = document.getElementById(
      fromButton.getAttribute("aria-controls")
    );
    Motion.animate(
      fromPanel,
      {
        transform: ["translateY(0)", "translateY(2rem)"],
        opacity: [1, 0],
      },
      { duration: theme.config.motionReduced ? 0 : 0.15 }
    ).finished;
    fromPanel.querySelector("motion-list")?.unload();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle(
        "button--primary",
        index === parseInt(newValue)
      );
      button.classList.toggle(
        "button--secondary",
        index !== parseInt(newValue)
      );
      button.disabled = index === parseInt(newValue);
    });

    this.indicators.forEach((indicators, index) => {
      indicators.hidden = index !== parseInt(newValue);
      if (index === parseInt(newValue)) {
        indicators.querySelectorAll("button").forEach((button) => {
          button.unload();
          button.load();
        });
      }
    });

    if (
      name === "selected-index" &&
      oldValue !== null &&
      oldValue !== newValue
    ) {
      const fromButton = this.buttons[parseInt(oldValue)];
      const toButton = this.buttons[parseInt(newValue)];
      this.transition(
        document.getElementById(
          fromButton.getAttribute("aria-controls")
        ),
        document.getElementById(toButton.getAttribute("aria-controls"))
      );
    }
  }

  async transition(fromPanel, toPanel) {
    if (fromPanel) {
      await Motion.animate(
        fromPanel,
        {
          transform: ["translateY(0)", "translateY(2rem)"],
          opacity: [1, 0],
        },
        { duration: theme.config.motionReduced ? 0 : 0.15 }
      ).finished;
    }

    fromPanel.hidden = true;
    toPanel.hidden = false;

    Motion.animate(
      toPanel,
      {
        transform: ["translateY(2rem)", "translateY(0)"],
        opacity: [0, 1],
      },
      { duration: theme.config.motionReduced ? 0 : 0.15 }
    ).finished;
    toPanel.querySelector("motion-list")?.load();
  }
}
customElements.define("tabs-element", TabsElement);

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get date() {
    return this._date = this._date || new Date(this.getAttribute('data-expires'));
  }

  get isCompact() {
    return this.getAttribute('data-compact') === 'true' || (this.getAttribute('data-compact') === 'mobile' && theme.config.mqlSmall);
  }

  get timerList() {
    return this.querySelector('.countdown__timer-list')
  }

  get completedMessage() {
    return this.querySelector('.countdown__timer-message')
  }

  /**
   * @param {*} unit d | h | m | s
   */
  getTimeText(unit) {
    let text
    switch (unit) {
      case 'd':
        text = 'dy'
        break
      case 'h':
        text = 'hr'
        break
      case 'm':
        text = 'min'
        break
      case 's':
        text = 'sec'
        break
    }
    return text
  }

  createSplitItem() {
    const split = document.createElement('div')
    split.textContent = ':'
    split.classList.add('countdown__split')
    this.timerList.appendChild(split)
    return split
  }

  createCountdownItem(unit) {
    const countdownItem = document.createElement('div')
    countdownItem.classList.add('countdown__item', 'time-' + unit)
    const p = document.createElement('p')
    p.textContent = '--'
    countdownItem.appendChild(p)

    let unitElement = null
    if (!this.hasAttribute('hide-unit')) {
      unitElement = document.createElement('span')
      unitElement.textContent = this.getTimeText(unit)
      countdownItem.appendChild(unitElement)
    }
    this.timerList.appendChild(countdownItem)
    return {
      item: countdownItem,
      time: p,
      unit: unitElement
    }
  }

  init() {

    this.daysElement = this.createCountdownItem('d')
    this.daysElement.split = this.createSplitItem()
    this.hoursElement = this.createCountdownItem('h')
    this.hoursElement.split = this.createSplitItem()
    this.minsElement = this.createCountdownItem('m')
    this.minsElement.split = this.createSplitItem()
    this.secsElement = this.createCountdownItem('s')

    this.calculate();
    this.timerInterval = setInterval(this.calculate.bind(this), 1000);
  }

  calculate() {
    const now = new Date(),
      countTo = new Date(this.date),
      timeDifference = (countTo - now);

    if (timeDifference < 0) {
      this.complete();
      return;
    }

    const secondsInADay = 60 * 60 * 1000 * 24,
      secondsInAHour = 60 * 60 * 1000;

    const days = Math.floor(timeDifference / (secondsInADay) * 1);
    const hours = Math.floor((timeDifference % (secondsInADay)) / (secondsInAHour) * 1);
    const mins = Math.floor(((timeDifference % (secondsInADay)) % (secondsInAHour)) / (60 * 1000) * 1);
    const secs = Math.floor((((timeDifference % (secondsInADay)) % (secondsInAHour)) % (60 * 1000)) / 1000 * 1);

    this.daysElement.time.textContent = (days + '').padStart(2, '0')
    // this.daysElement.unit.textContent = this.getTimeText('d', days > 1)
    if (days < 1) {
      this.daysElement.item.classList.add('hidden-important')
      this.daysElement.split.classList.add('hidden-important')
    } else {
      this.daysElement.item.classList.remove('hidden-important')
      this.daysElement.split.classList.remove('hidden-important')
    }

    this.hoursElement.time.textContent = (hours + '').padStart(2, '0')
    // this.hoursElement.unit.textContent = this.getTimeText('h', hours > 1)

    this.minsElement.time.textContent = (mins + '').padStart(2, '0')
    // this.minsElement.unit.textContent = this.getTimeText('m', mins > 1)

    this.secsElement.time.textContent = (secs + '').padStart(2, '0')
    // this.secsElement.unit.textContent = this.getTimeText('s', secs > 1)

    // if (this.isCompact) {
    //   const dayHTML = days > 0 ? `<div class="countdown__item"><p>${days}${theme.dateStrings.d}</p></div>` : '';
    //   const hourHTML = `<div class="countdown__item"><p>${hours}${theme.dateStrings.h}</p></div>`;
    //   const minHTML = `<div class="countdown__item"><p>${mins}${theme.dateStrings.m}</p></div>`;
    //   const secHTML = `<div class="countdown__item"><p>${secs}${theme.dateStrings.s}</p></div>`;

    //   this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    // }
    // else {
    //   const dayHTML = days > 0 ? `<div class="countdown__item"><p>${days}</p><span>${days === 1 ? theme.dateStrings.day : theme.dateStrings.days}</span></div>` : '';
    //   const hourHTML = `<div class="countdown__item"><p>${hours}</p><span>${hours === 1 ? theme.dateStrings.hour : theme.dateStrings.hours}</span></div>`;
    //   const minHTML = `<div class="countdown__item"><p>${mins}</p><span>${mins === 1 ? theme.dateStrings.minute : theme.dateStrings.minutes}</span></div>`;
    //   const secHTML = `<div class="countdown__item"><p>${secs}</p><span>${secs === 1 ? theme.dateStrings.second : theme.dateStrings.seconds}</span></div>`;

    //   this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    // }
  }

  complete() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.setAttribute('completed', '')
  }
}
customElements.define('countdown-timer', CountdownTimer);

class ImageComparison extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media);
      this.init();
    });
  }

  get button() {
    return this.querySelector("button");
  }

  get bounding() {
    return this.getBoundingClientRect();
  }

  get horizontal() {
    return this.getAttribute("data-layout") === "horizontal";
  }

  get media() {
    return Array.from(this.querySelectorAll("img, svg"));
  }

  init() {
    this.active = false;

    this.button.addEventListener(
      "touchstart",
      this.startHandler.bind(this),
      theme.supportsPassive ? { passive: false } : false
    );
    document.body.addEventListener(
      "touchend",
      this.endHandler.bind(this),
      theme.supportsPassive ? { passive: true } : false
    );
    document.body.addEventListener(
      "touchmove",
      this.onHandler.bind(this),
      theme.supportsPassive ? { passive: true } : false
    );

    this.button.addEventListener("mousedown", this.startHandler.bind(this));
    document.body.addEventListener("mouseup", this.endHandler.bind(this));
    document.body.addEventListener("mousemove", this.onHandler.bind(this));

    setTimeout(() => this.animate(), 1e3);
  }

  animate() {
    this.setAttribute("animate", "");

    this.classList.add("animating");
    setTimeout(() => {
      this.classList.remove("animating");
    }, 1e3);
  }

  startHandler(event) {
    event.preventDefault();
    this.active = true;
    this.classList.add("scrolling");
  }

  endHandler() {
    this.active = false;
    this.classList.remove("scrolling");
  }

  onHandler(e) {
    if (!this.active) return;

    const event = (e.touches && e.touches[0]) || e;
    let x = this.horizontal
      ? event.pageX - (this.bounding.left + window.scrollX)
      : event.pageY - (this.bounding.top + window.scrollY);

    this.scrollIt(x);
  }

  scrollIt(x) {
    const distance = this.horizontal ? this.clientWidth : this.clientHeight;

    const max = distance - 20;
    const min = 20;
    const mouseX = Math.max(min, Math.min(x, max));
    const mousePercent = (mouseX * 100) / distance;
    this.style.setProperty("--percent", mousePercent + "%");
  }
}
customElements.define("image-comparison", ImageComparison);

class LookbookElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media);
      this.init();
    });
  }

  get media() {
    return Array.from(this.querySelectorAll("img, svg"));
  }

  get items() {
    return (this._items =
      this._items || Array.from(this.querySelectorAll(".hotspot")));
  }

  init() {
    this.items.forEach((item) =>
      item.addEventListener("mouseenter", (event) =>
        this.select(this.items.indexOf(event.target))
      )
    );

    if (Shopify.designMode) {
      const section = this.closest(".shopify-section");
      section.addEventListener(
        "shopify:section:select",
        this.animate.bind(this)
      );
      section.addEventListener(
        "shopify:section:deselect",
        this.closeAll.bind(this)
      );
      this.addEventListener("shopify:block:select", (event) =>
        this.open(this.items.indexOf(event.target))
      );
    }

    setTimeout(() => this.animate(), 1e3);
  }

  animate() {
    this.openAll();

    this.timer = setTimeout(() => this.closeAll(), 3e3);
  }

  open(selectedIndex) {
    this.items.forEach((item, index) =>
      item.classList.toggle("active", selectedIndex === index)
    );
  }

  openAll() {
    this.items.forEach((item) => item.classList.add("active"));
  }

  closeAll() {
    this.items.forEach((item) => item.classList.remove("active"));
    clearTimeout(this.timer);
  }

  select(selectedIndex) {
    this.items.forEach((item, index) =>
      item.setAttribute(
        "aria-current",
        selectedIndex === index ? "true" : "false"
      )
    );
    this.dispatchEvent(
      new CustomEvent("lookbook:change", {
        bubbles: true,
        detail: { index: selectedIndex },
      })
    );
  }
}
customElements.define("lookbook-element", LookbookElement);

class ShopTheLook extends HTMLElement {
  constructor() {
    super();

    this.lookbook.addEventListener("lookbook:change", (event) =>
      this.carousel.select(event.detail.index)
    );
    this.carousel.addEventListener("carousel:change", (event) =>
      this.lookbook.select(event.detail.index)
    );
  }

  get lookbook() {
    return this.querySelector("lookbook-element");
  }

  get carousel() {
    return this.querySelector("carousel-element");
  }
}
customElements.define("shop-the-look", ShopTheLook);

class SpinningText extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), {
        margin: "600px 0px 600px 0px",
      });
    } else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get string() {
    let string = this.getAttribute("data-string");
    string = string.replace(/  +/g, " ");
    return "•" + string.replace(/ +/g, "•");
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const canTrig = CSS.supports("(top: calc(sin(1) * 1px))");
    const OPTIONS = {
      TEXT: this.string,
      SIZE: 2,
      SPACING: 2,
    };

    const HEADING = document.createElement("div");
    const text = OPTIONS.TEXT;

    // Take the text and split it into spans...
    const chars = text.split("");
    this.style.setProperty("--char-count", chars.length);

    for (let c = 0; c < chars.length; c++) {
      HEADING.innerHTML += `<span aria-hidden="true" class="split-char" style="--char-index: ${c};">${chars[c]}</span>`;
    }
    HEADING.innerHTML += `<span class="sr-only">${OPTIONS.TEXT}</span>`;
    HEADING.classList = "split-chars";

    // Set the styles
    this.style.setProperty("--font-size", OPTIONS.SIZE);
    this.style.setProperty("--character-width", OPTIONS.SPACING);
    this.style.setProperty(
      "--radius",
      canTrig
        ? "calc((var(--character-width) / sin(var(--inner-angle))) * -1ch)"
        : `calc(
        (${OPTIONS.SPACING} / ${Math.sin(
            360 / this.children.length / (180 / Math.PI)
          )})
        * -1ch
      )`
    );

    // Append
    this.appendChild(HEADING);
  }
}
customElements.define("spinning-text", SpinningText);

class SlideshowElement extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), {
        margin: "200px 0px 200px 0px",
      });
    } else {
      new theme.initWhenVisible(
        theme.utils.throttle(this.init.bind(this))
      );
    }
  }

  static get observedAttributes() {
    return ["selected-index"];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute(
      "selected-index",
      Math.min(Math.max(index, 0), this.items.length - 1).toString()
    );
  }

  get items() {
    return (this._items = this._items || Array.from(this.children));
  }

  get autoplay() {
    return this.hasAttribute("autoplay");
  }

  get speed() {
    return this.hasAttribute("autoplay")
      ? parseInt(this.getAttribute("autoplay-speed")) * 1000
      : 5000;
  }

  get adaptiveHeightProperty() {
    return false;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const that = this;
    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        autoPlay: this.autoplay ? this.speed : false,
        adaptiveHeightProperty: this.adaptiveHeightProperty,
        on: {
          ready: function () {
            const { selectedElement } = this;
            that.onReady(selectedElement);
          },
        },
      });

      this.slider.on("change", this.onChange.bind(this));
      this.addEventListener("slider:previous", () =>
        this.slider.previous()
      );
      this.addEventListener("slider:next", () => this.slider.next());
      this.addEventListener("slider:play", () =>
        this.slider.playPlayer()
      );
      this.addEventListener("slider:pause", () =>
        this.slider.pausePlayer()
      );

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) =>
          this.slider.select(this.items.indexOf(event.target))
        );
      }
    } else if (this.items.length === 1) {
      const selectedElement = this.firstChild;
      that.onReady(selectedElement);
    }
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (
      name === "selected-index" &&
      oldValue !== null &&
      oldValue !== newValue
    ) {
      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];

      const animateElement = toElement.querySelector("animate-element");
      animateElement?.refresh();

      if (fromElement.getAttribute("data-type") === "video") {
        const fromVideoElement = theme.utils.visibleMedia(
          fromElement.querySelectorAll("video-media")
        );
        fromVideoElement?.pause();
      }

      if (toElement.getAttribute("data-type") === "video") {
        const toVideoElement = theme.utils.visibleMedia(
          toElement.querySelectorAll("video-media")
        );
        toVideoElement?.play();
      }
    }
  }

  onChange() {
    this.selectedIndex = this.slider.selectedIndex;
    this.dispatchEvent(
      new CustomEvent("slider:change", {
        bubbles: true,
        detail: { currentPage: this.slider.selectedIndex },
      })
    );
  }

  select(selectedIndex) {
    this.slider.select(selectedIndex);
  }

  onReady(selectedElement) {
    if (selectedElement.getAttribute("data-type") === "video") {
      const videoElement = theme.utils.visibleMedia(
        selectedElement.querySelectorAll("video-media")
      );
      videoElement?.play();
    }

    if (!theme.config.isTouch) {
      const animateElement =
        selectedElement.querySelector("animate-element");
      animateElement?.refresh();
    }
  }
}
customElements.define("slideshow-element", SlideshowElement);

class SlideshowHero extends SlideshowElement {
  constructor() {
    super();

    window.addEventListener(
      "scroll",
      theme.utils.throttle(this.onScrollHandler.bind(this)),
      false
    );
  }

  get adaptiveHeightProperty() {
    return true;
  }

  get heroSection() {
    return this.closest(".hero-section");
  }

  get nextSection() {
    let nextSibling = this.heroSection.nextElementSibling;
    for (let i = 0; i < 20; i++) {
      if (
        nextSibling.classList.contains("shopify-section") &&
        !nextSibling.classList.contains("header-section")
      )
        break;
      nextSibling = nextSibling.nextElementSibling;
    }

    if (nextSibling && nextSibling.nodeType != 1) {
      nextSibling = document.getElementById("PageContainer");
    }

    return nextSibling;
  }

  onScrollHandler() {
    const bounding = this.nextSection.getBoundingClientRect();
    this.heroSection.classList.toggle("hero-scrolled", bounding.y < -30);
  }
}
customElements.define("slideshow-hero", SlideshowHero);

class SlideshowWords extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;

    if (this.controlledElement) {
      this.controlledElement.addEventListener(
        "slider:change",
        (event) => (this.selectedIndex = event.detail.currentPage)
      );
    }
  }

  static get observedAttributes() {
    return ["selected-index"];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute(
      "selected-index",
      Math.min(Math.max(index, 0), this.items.length - 1).toString()
    );
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  get items() {
    return (this._items = this._items || Array.from(this.children));
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (
      name === "selected-index" &&
      oldValue !== null &&
      oldValue !== newValue
    ) {
      this.transition(
        this.items[parseInt(oldValue)],
        this.items[parseInt(newValue)]
      );
    }
  }

  transition(fromElement, toElement) {
    const fromWords = Array.from(
      fromElement.querySelectorAll("animate-element")
    );
    const toWords = Array.from(
      toElement.querySelectorAll("animate-element")
    );

    fromWords.forEach((element) => element.reset());

    setTimeout(() => {
      this.items.forEach((item) => {
        item.setAttribute(
          "aria-current",
          parseInt(item.getAttribute("data-index")) ===
            parseInt(this.selectedIndex)
            ? "true"
            : "false"
        );
      });

      toWords.forEach((element) => element.refresh());
    }, 500 + 30 * fromWords.length);
  }
}
customElements.define("slideshow-words", SlideshowWords);

class SlideshowParallax extends HTMLDivElement {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  get mobileDisabled() {
    return false;
  }

  load() {
    if (theme.config.motionReduced) return;
    if (
      this.mobileDisabled &&
      (theme.config.isTouch || theme.config.mqlSmall)
    )
      return;

    this.motion();
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, {
        transform: [
          `translateY(${this.getAttribute("data-start")})`,
          `translateY(${this.getAttribute("data-stop")})`,
        ],
      }),
      {
        target: this.controlledElement ?? document.body,
        offset: Motion.ScrollOffset.Exit,
      }
    );
  }
}
customElements.define("slideshow-parallax", SlideshowParallax, {
  extends: "div",
});

class ControlButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener("click", this.onClick);
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  get paused() {
    return this.hasAttribute("paused");
  }

  static get observedAttributes() {
    return ["paused"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "paused") {
      if (oldValue === null && newValue === "") {
        return (this.controlledElement ?? this).dispatchEvent(
          new CustomEvent("slider:pause", { bubbles: true })
        );
      }

      if (newValue === null) {
        return (this.controlledElement ?? this).dispatchEvent(
          new CustomEvent("slider:play", { bubbles: true })
        );
      }
    }
  }

  onClick() {
    this.paused
      ? this.removeAttribute("paused")
      : this.setAttribute("paused", "");
  }
}
customElements.define("control-button", ControlButton, { extends: "button" });

class QuickView extends XModal {
  constructor() {
    super();
  }

  get selector() {
    return ".quick-view__content";
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.hide.bind(this)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  beforeShow() {
    super.beforeShow();
    this.quickview();
  }

  afterShow() {
    super.afterShow();

    document.dispatchEvent(
      new CustomEvent("quickview:open", {
        detail: {
          productUrl: this.getAttribute("data-product-url"),
        },
      })
    );
  }

  afterHide() {
    super.afterHide();

    const drawerContent = this.querySelector(this.selector);
    drawerContent.innerHTML = "";
  }

  quickview() {
    const drawerContent = this.querySelector(this.selector);
    const productUrl = this.getAttribute("data-product-url").split("?")[0];
    const sectionUrl = `${productUrl}?view=modal`;

    fetch(sectionUrl)
      .then((response) => response.text())
      .then((responseText) => {
        setTimeout(() => {
          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const productElement = parsedHTML.querySelector(
            this.selector
          );
          this.setInnerHTML(drawerContent, productElement.innerHTML);
          theme.a11y.trapFocus(this, this.focusElement);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          document.dispatchEvent(
            new CustomEvent("quickview:loaded", {
              detail: {
                productUrl:
                  this.getAttribute("data-product-url"),
              },
            })
          );
        }, 200);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  setInnerHTML(element, innerHTML) {
    element.innerHTML = innerHTML;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(
        document.createTextNode(oldScriptTag.innerHTML)
      );
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define("quick-view", QuickView);

class FiltersToggle extends HoverButton {
  constructor() {
    super();

    this.addEventListener("click", this.onClick);

    if (theme.config.hasLocalStorage) {
      const expanded =
        window.localStorage.getItem(
          `${theme.settings.themeName}:filters-toogle`
        ) || "false";
      this.onToggle(expanded === "true");
    }
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  get expanded() {
    return this.getAttribute("aria-expanded") === "true";
  }

  onClick() {
    this.onToggle(this.expanded);
  }

  onToggle(expanded) {
    this.setAttribute("aria-expanded", expanded ? "false" : "true");
    if (this.controlledElement)
      this.controlledElement.classList.toggle("xl:block", !expanded);

    if (theme.config.hasLocalStorage) {
      window.localStorage.setItem(
        `${theme.settings.themeName}:filters-toogle`,
        expanded
      );
    }
  }
}
customElements.define("filters-toogle", FiltersToggle, { extends: "button" });

class RevealBanner extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), {
      margin: "600px 0px 600px 0px",
    });
  }

  init() {
    const tracker = this.querySelector(".reveal-banner__tracker");
    Motion.scroll(
      Motion.animate(
        this.querySelectorAll(".banner__media"),
        {
          clipPath: [
            "inset(20% 35% 0% 35% round var(--rounded-block))",
            "inset(0% 0%)",
          ],
        },
        { easing: "linear" }
      ),
      { target: tracker, offset: Motion.ScrollOffset.Enter }
    );

    const tracker2nd = this.querySelector(".reveal-banner__tracker2nd");
    if (tracker2nd) {
      Motion.scroll(
        Motion.timeline([
          [
            this.querySelector(".banner__overlay"),
            { opacity: [0, 1] },
            { easing: "linear" },
          ],
          [
            this.querySelector(".banner__content"),
            {
              opacity: [0, 1],
              visibility: ["hidden", "visible"],
              transform: ["translateY(10%)", "translateY(0)"],
            },
            { easing: "linear", at: "<" },
          ],
        ]),
        { target: tracker2nd, offset: Motion.ScrollOffset.Enter }
      );
    }
  }
}
customElements.define("reveal-banner", RevealBanner);

class TestimonialParallax extends ParallaxElement {
  constructor() {
    super();
  }

  get mobileDisabled() {
    return true;
  }
}
customElements.define("testimonial-parallax", TestimonialParallax, {
  extends: "div",
});

class SplittingBanner extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), {
      margin: "600px 0px 600px 0px",
    });
  }

  init() {
    const splitting = Splitting({
      target: this.querySelector(".splitting"),
      by: "chars",
      whitespace: true,
    });

    const wrapper = this.querySelector(".splitting-wrapper");
    const tracker = this.querySelector(".reveal-banner__tracker");
    Motion.scroll(
      Motion.timeline([
        [
          splitting[0].chars,
          { opacity: [0.3, 1] },
          {
            delay: theme.config.motionReduced
              ? 0
              : Motion.stagger(0.1),
          },
        ],
        //[wrapper, { transform: ['translateY(-20vh)', 'translateY(0)'] }, { easing: 'linear', at: '<' }]
      ]),
      { target: tracker, offset: Motion.ScrollOffset.Enter }
    );

    Motion.scroll(
      Motion.animate(
        wrapper,
        { opacity: theme.config.mqlSmall ? [1, 0, 0] : [1, 1, 0] },
        { easing: "linear" }
      ),
      { target: tracker, offset: Motion.ScrollOffset.Exit }
    );

    const tracker2nd = this.querySelector(".reveal-banner__tracker2nd");
    if (tracker2nd) {
      Motion.scroll(
        Motion.animate(
          this.querySelector(".button-wrapper"),
          {
            opacity: [0, 1, 1],
            transform: ["translateY(100%)", "translateY(0)"],
          },
          { easing: "linear" }
        ),
        { target: tracker2nd, offset: Motion.ScrollOffset.Enter }
      );
    }
  }
}
customElements.define("splitting-banner", SplittingBanner);

class ProductBundleInfo extends ProductInfo {
  constructor() {
    super();
  }

  get productForm() {
    return this.querySelector('form[is="product-bundle-form"]');
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector(
      `product-bundle-info[data-product-id="${this.productId}"] [data-selected-variant]`
    )?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }
}
customElements.define("product-bundle-info", ProductBundleInfo);

class ProductBundleForm extends HTMLFormElement {
  constructor() {
    super();

    this.querySelector('[name="id"]').disabled = false;
    this.addEventListener("submit", this.onSubmitHandler);
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  get submitButton() {
    return (this._submitButton =
      this._submitButton || this.querySelector('[type="submit"]'));
  }

  getSelectedVariant() {
    const selectedVariant = this.querySelector(
      "[data-selected-variant]"
    )?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton.hasAttribute("aria-disabled")) return;

    const product = this.closest(".product-card");
    const variant = this.getSelectedVariant();
    variant.default_featured_media = this.hasAttribute("data-product-image")
      ? this.getAttribute("data-product-image")
      : "";

    this.submitButton.removeAttribute("aria-busy");
    (this.controlledElement ?? this).dispatchEvent(
      new CustomEvent("bundle:added", {
        bubbles: true,
        detail: { product: product, variant: variant },
      })
    );
  }

  handleErrorMessage() {
    // todo
  }

  toggleSubmitButton(disable = true, text) {
    if (!this.submitButton) return;

    const submitButtonText = this.submitButton.querySelector(".btn-text");
    const submitButtonTextChild =
      this.submitButton.querySelector(".btn-text>span");

    if (disable) {
      this.submitButton.setAttribute("disabled", "");
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
    } else {
      this.submitButton.removeAttribute("disabled");
      (submitButtonTextChild || submitButtonText).textContent =
        theme.variantStrings.addToBundle;
    }
  }
}
customElements.define("product-bundle-form", ProductBundleForm, {
  extends: "form",
});

class ProductBundleRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      const variant = event.target.closest(
        "[data-product-bundle-variant]"
      );
      (this.controlledElement ?? this).dispatchEvent(
        new CustomEvent("bundle:removed", {
          bubbles: true,
          detail: { variant: variant },
        })
      );
    });
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }
}
customElements.define(
  "product-bundle-remove-button",
  ProductBundleRemoveButton
);

class ProductBundleToggleButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", () => {
      this.controlledElement.classList.toggle("active");
    });
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }
}
customElements.define(
  "product-bundle-toggle-button",
  ProductBundleToggleButton
);

class ProductBundle extends HTMLElement {
  constructor() {
    super();

    this.template;
    this.bundleCount = 0;

    this.addEventListener("bundle:added", this.onAddHandler.bind(this));
    this.addEventListener(
      "bundle:removed",
      this.onRemoveHandler.bind(this)
    );
    this.addEventListener(
      "change",
      theme.utils.debounce(this.onChangeHandler.bind(this), 300)
    );
    this.submitButton.addEventListener(
      "click",
      this.onSubmitHandler.bind(this)
    );
  }

  get bundleMin() {
    return this.hasAttribute("data-minimum")
      ? parseInt(this.getAttribute("data-minimum"))
      : 3;
  }

  get bundleMax() {
    const minimum = this.bundleMin;
    const maximum = this.hasAttribute("data-maximum")
      ? parseInt(this.getAttribute("data-maximum"))
      : 3;
    return maximum > minimum ? maximum : minimum;
  }

  get variants() {
    return Array.from(
      this.querySelectorAll("[data-product-bundle-variant]")
    );
  }

  get submitButton() {
    return this.querySelector("[data-product-bundle-submit]");
  }

  get cartDrawer() {
    return document.querySelector("cart-drawer");
  }

  get preventDuplicate() {
    return this.hasAttribute("data-prevent-duplicate");
  }

  get template() {
    return (this._template =
      this._template ||
      this.querySelector(
        "[data-product-bundle-variant][available]"
      ).cloneNode(true));
  }

  onChangeHandler() {
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  onSubmitHandler(event) {
    const validateQuantity = this.variants.every((variant) => {
      const target = variant.querySelector("quantity-input").input;
      const inputValue = parseInt(target.value);
      let message = "";

      if (inputValue < parseInt(target.getAttribute("data-min"))) {
        message = theme.quickOrderListStrings.minError.replace(
          "[min]",
          target.getAttribute("data-min")
        );
      } else if (inputValue > parseInt(target.max)) {
        message = theme.quickOrderListStrings.maxError.replace(
          "[max]",
          target.max
        );
      } else if (inputValue % parseInt(target.step) !== 0) {
        message = theme.quickOrderListStrings.stepError.replace(
          "[step]",
          target.step
        );
      }

      if (message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();

        return false;
      }

      return true;
    });
    if (!validateQuantity) return;

    const data = {
      items: this.variants.map((variant) => ({
        id: variant.getAttribute("data-variant-id"),
        quantity: variant.querySelector("quantity-input").value || 1,
      })),
    };

    if (
      document.body.classList.contains("template-cart") ||
      theme.settings.cartType === "page"
    ) {
      theme.utils.postLink2(theme.routes.cart_add_url, {
        parameters: {
          ...data,
        },
      });
      return;
    }

    event.preventDefault();
    if (this.submitButton.hasAttribute("aria-disabled")) return;
    this.activeElement = event.submitter || event.currentTarget;

    this.handleErrorMessage();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const body = JSON.stringify({
      ...data,
      sections: sectionsToBundle,
      sections_url: window.location.pathname,
    });

    this.submitButton.setAttribute("aria-disabled", "true");
    this.submitButton.setAttribute("aria-busy", "true");

    fetch(`${theme.routes.cart_add_url}`, {
      ...theme.utils.fetchConfig("javascript"),
      body,
    })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(
            new CustomEvent("ajaxProduct:error", {
              detail: {
                errorMessage: parsedState.description,
              },
            })
          );

          const submitButtonText =
            this.submitButton.querySelector(".btn-text>span");
          if (
            !submitButtonText ||
            !submitButtonText.hasAttribute("data-sold-out")
          )
            return;
          submitButtonText.innerText =
            submitButtonText.getAttribute("data-sold-out");
          this.submitButton.setAttribute("aria-disabled", "true");
          this.error = true;
          return;
        }

        const cartJson = await (
          await fetch(theme.routes.cart_url, {
            ...theme.utils.fetchConfig("json", "GET"),
          })
        ).json();
        cartJson["sections"] = parsedState["sections"];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: "product-bundle",
          cart: cartJson,
        });
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", {
            detail: {
              product: parsedState,
            },
          })
        );

        this.cartDrawer?.show(this.activeElement);
        this.resetVariants();
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.submitButton.removeAttribute("aria-busy");

        if (!this.error) {
          this.submitButton.removeAttribute("aria-disabled");
        }
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessage =
      this.errorMessage ||
      this.querySelector(".product-form__error-message");
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute("hidden", !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  onAddHandler(event) {
    this.addVariant(event.detail.product, event.detail.variant);
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  onRemoveHandler(event) {
    this.clearVariant(event.detail.variant);
    this.reorderVariants();
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  updateTotal() {
    let subtotal = 0;
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute("available")) {
        const price = variant.querySelector(
          "[data-product-bundle-variant-price]"
        );
        const quantityInput = variant.querySelector("quantity-input");
        subtotal +=
          parseInt(price.getAttribute("data-price")) *
          parseInt(quantityInput.value);
      }
    });

    const element = this.querySelector("[data-product-bundle-total]");
    element.innerHTML = theme.Currency.formatMoney(
      subtotal,
      theme.settings.moneyFormat
    );
  }

  updateTotalWithCurrency() {
    let subtotal = 0;
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute("available")) {
        const price = variant.querySelector(
          "[data-product-bundle-variant-price]"
        );
        const quantityInput = variant.querySelector("quantity-input");
        subtotal +=
          parseInt(price.getAttribute("data-price")) *
          parseInt(quantityInput.value);
      }
    });

    const element = this.querySelector(
      "[data-product-bundle-total-with-currency]"
    );
    element.innerHTML = theme.Currency.formatMoney(
      subtotal,
      theme.settings.moneyWithCurrencyFormat
    );
  }

  updateProgressBar() {
    const percent = (100 * this.bundleCount) / this.bundleMin;
    const element = this.querySelector(
      "[data-product-bundle-progress-bar]"
    );
    element.style.setProperty("--progress", `${percent}%`);
  }

  addVariant(product, variant) {
    this.bundleCount++;

    if (this.bundleCount >= this.bundleMin) {
      this.submitButton.disabled = false;
    }
    if (this.bundleCount >= this.bundleMax) {
      this.closest(".product-bundle-wrapper").setAttribute("locked", "");
    }

    let variantElement = this.getAvailableAvriant();
    if (variantElement === null) {
      variantElement = this.template.cloneNode(true);
      variantElement.style.order = this.bundleCount;
      this.querySelector(".product-bundle__body").appendChild(
        variantElement
      );
    }

    if (variantElement) {
      const preview_image = variant.featured_media
        ? variant.featured_media.preview_image.src
        : variant.default_featured_media;
      const media = variantElement.querySelector(
        "[data-product-bundle-variant-media]"
      );
      if (preview_image.length > 0) {
        media.innerHTML = `<img src="${this.getResizedImageSrc(
          preview_image,
          180
        )}" srcset="${this.getResizedImageSrc(
          preview_image,
          180
        )} 180w, ${this.getResizedImageSrc(
          preview_image,
          360
        )} 360w, ${this.getResizedImageSrc(
          preview_image,
          540
        )} 540w" loading="lazy" is="lazy-image" />`;
      }

      const content = variantElement.querySelector(
        "[data-product-bundle-variant-content]"
      );
      content.innerHTML = `
        <div class="flex flex-col gap-1">
          <p class="horizontal-product__title font-medium text-base leading-tight">${
        product.querySelector("[data-product-bundle-title]").textContent
      }</p>
          ${
        variant.options.length > 0
          ? `
            <ul class="grid gap-1d5">
              ${variant.options
          .map((option) => {
            return `<li class="text-xs text-opacity leading-tight">${option}</li>`;
          })
          .join("")}
            </ul>
          `
          : ""
      }
        </div>
        <div class="price text-sm flex flex-wrap gap-1d5" data-price="${
      variant.price
    }" data-product-bundle-variant-price>
          ${theme.Currency.formatMoney(
        variant.price,
        theme.settings.moneyFormat
      )}
        </div>`;

      variantElement.product = product;
      variantElement.setAttribute("data-variant-id", variant.id);
      variantElement.removeAttribute("available");
      if (this.preventDuplicate) {
        product.setAttribute("locked", "");
      }
    }
  }

  clearVariant(variant) {
    if (this.preventDuplicate) {
      variant.product.removeAttribute("locked");
    }
    variant.setAttribute("available", "");
    variant.removeAttribute("data-variant-id");
    variant.querySelector("[data-product-bundle-variant-media]").innerHTML =
      "";
    variant
      .querySelector("[data-product-bundle-variant-media]")
      .classList.remove("loading");
    variant.querySelector(
      "[data-product-bundle-variant-content]"
    ).innerHTML =
      '<span class="horizontal-product__skeleton"></span><span class="horizontal-product__skeleton"></span>';
    variant.querySelector("quantity-input").reset();

    this.bundleCount--;

    if (this.bundleCount < this.bundleMin) {
      this.submitButton.disabled = true;
    } else {
      variant.remove();
    }
    if (this.bundleCount < this.bundleMax) {
      this.closest(".product-bundle-wrapper").removeAttribute("locked");
    }
  }

  reorderVariants() {
    let order = 0;
    let minimum = Math.max(this.bundleMin, this.bundleCount) * -1;
    this.variants.forEach((variant) => {
      variant.style.order = variant.hasAttribute("available")
        ? order++
        : minimum++;
    });
  }

  resetVariants() {
    this.variants.forEach((variant) => {
      this.clearVariant(variant);
    });

    this.reorderVariants();
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  getAvailableAvriant() {
    return this.querySelector("[data-product-bundle-variant][available]");
  }

  getResizedImageSrc(src, size, crop = "center") {
    return `${src}${
      src.includes("?") ? "&" : "?"
    }width=${size}&height=${size}&crop=${crop}`.replace(/\n|\r|\s/g, "");
  }
}
customElements.define("product-bundle", ProductBundle);

class NumberCounter extends HTMLElement {
  constructor() {
    super();

    if (theme.config.motionReduced) return;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), {
        margin: "200px 0px 200px 0px",
      });
    } else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const matches = this.textContent.trim().match(/\d+(?:[,. ]\d+)*/);
    const toReplace = matches[0].replace(/[,\. ]+/, "");

    Motion.animate(
      (progress) => {
        const formattedString = Math.round(
          progress * parseInt(toReplace)
        ).toString();
        this.textContent =
          progress === 1 ? matches[0] : formattedString;
      },
      {
        duration: parseFloat(this.getAttribute("data-duration")),
        easing: [0.16, 1, 0.3, 1],
      }
    );
  }
}
customElements.define("number-counter", NumberCounter);

class ScrollingBanner extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), {
        margin: "600px 0px 600px 0px",
      });
    } else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get media() {
    return (this._media =
      this._media ||
      Array.from(
        this.querySelectorAll(".image-with-text__image>.media")
      ));
  }

  get stickyElement() {
    return this.querySelector("sticky-element");
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.detectScrollListener = theme.utils.throttle(
      this.onScrollHandler.bind(this)
    );

    const mql = window.matchMedia("screen and (min-width: 1024px)");
    if (mql.matches) this.load();
    mql.onchange = (mql) => {
      mql.matches ? this.load() : this.unload();
    };
  }

  setMediaHeight() {
    let height = 0;
    this.media.forEach((media) => {
      height += media.getBoundingClientRect().height;
    });
    this.style.setProperty("--scrolling-height", `${height}px`);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;
    const offsetTop =
      this.getBoundingClientRect().top -
      parseFloat(this.stickyElement.inset);

    this.media.forEach((media, index) => {
      const mediaHeight = media.getBoundingClientRect().height;

      if (index !== 0 && offsetTop < 0) {
        const mediaScrollTop = mediaHeight * (index - 1);
        if (Math.abs(offsetTop) > mediaScrollTop) {
          let progress =
            100 -
            Math.floor(
              ((Math.abs(offsetTop) - mediaScrollTop) /
                mediaHeight) *
                100
            );
          if (progress > 95) progress = 100;
          if (progress < 5) progress = 0;

          media.style.clipPath = `polygon(0 ${progress}%, 100% ${progress}%, 100% 100%, 0 100%)`;

          const controlledElement = media.hasAttribute(
            "aria-controls"
          )
            ? document.getElementById(
                media.getAttribute("aria-controls")
              )
            : null;
          if (controlledElement) {
            if (progress === 100)
              controlledElement.classList.add(
                "opacity-0",
                "pointer-events-none"
              );
            if (progress === 0)
              controlledElement.classList.remove(
                "opacity-0",
                "pointer-events-none"
              );
          }
        } else {
          media.style.clipPath = "";
        }
      }
    });

    this.currentScrollTop = scrollTop;
  }

  load() {
    this.setMediaHeight();
    window.addEventListener("scroll", this.detectScrollListener, false);
  }

  unload() {
    window.removeEventListener("scroll", this.detectScrollListener);
    this.style.removeProperty("--scrolling-height");
    this.media.forEach((media) => {
      media.style.clipPath = "";
    });
  }
}
customElements.define("scrolling-banner", ScrollingBanner);

class RevealVideoWithText extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), {
      margin: "600px 0px 600px 0px",
    });
  }

  init() {
    const splitting = Splitting({
      target: this.querySelector(".splitting"),
      by: "chars",
      whitespace: true,
    });

    const wrapper = this.querySelector(".splitting-wrapper");
    const tracker = this.querySelector(".reveal-banner__tracker");

    Motion.scroll(
      Motion.animate(
        wrapper,
        {
          opacity: theme.config.mqlSmall
            ? [1, 0.5, 0.3, 0]
            : [1, 0.5, 0.3, 0],
        },
        { easing: "linear" }
      ),
      { target: tracker, offset: Motion.ScrollOffset.Exit }
    );

    // 视频滚动播放控制
    const videoContainer = this.querySelector(".scroll-video");
    const videoPc = videoContainer.querySelector(".reveal-video__pc");
    const videoMobile = videoContainer.querySelector(
      ".reveal-video__mobile"
    );
    const bannerBox = this.querySelector(".banner__box");

    // 视口宽度
    const isMobile = window.innerWidth <= 767;
    if (videoContainer) {
      // 查找实际的video元素，可能在容器内部
      const video = isMobile
        ? videoMobile
          ? videoMobile
          : videoContainer.querySelector("video")
        : videoPc
        ? videoPc
        : videoContainer.querySelector("video");

      // 起始点：视频元素有1/5的身体进入视口时开始触发滚动逻辑
      const startThreshold = 0.2;
      // 结束点：视频元素顶边超过banner__box元素顶边的比例（相对于视频高度）
      const endThreshold = 0.2;

      // 确保视频已加载
      const videoReady = new Promise((resolve) => {
        if (video.readyState >= 3) {
          resolve();
        } else {
          video.addEventListener("canplay", () => resolve(), {
            once: true,
          });
          // 只有在video元素有load方法时才调用
          if (typeof video.load === "function") {
            video.load();
          }
        }
      });

      videoReady.then(() => {
        // 获取实际视频时长
        const realDuration = video.duration || 3;

        // 基于滚动位置设置视频时间
        const scrollHandler = () => {
          // 获取视频元素的位置和尺寸
          const videoRect = video.getBoundingClientRect();
          const bannerBoxRect = bannerBox.getBoundingClientRect();
          const viewportHeight = window.innerHeight;

          // 计算视频在视口中的可见比例 (0-1范围)
          let visibleRatio = 0;

          // 如果视频顶部已进入视口
          if (videoRect.top < viewportHeight) {
            // 计算已进入视口的高度
            const visibleHeight = Math.min(
              videoRect.height,
              viewportHeight - videoRect.top
            );
            // 计算可见比例
            visibleRatio = visibleHeight / videoRect.height;
          }

          // 计算滚动进度 (0-1范围)
          let scrollProgress = 0;

          // 只有当视频可见比例超过阈值时才开始计算进度
          if (visibleRatio >= startThreshold) {
            // 计算从起始点(视频20%进入视口)到结束点(视频顶部超过banner__box顶部20%)的总距离
            const startPosition =
              viewportHeight - videoRect.height * startThreshold;
            // 结束位置：banner__box顶部减去视频高度的endThreshold比例
            const endPosition =
              bannerBoxRect.top - videoRect.height * endThreshold;
            const totalDistance = startPosition - endPosition;

            // 计算当前进度
            if (totalDistance > 0) {
              const currentPosition = Math.max(
                videoRect.top,
                endPosition
              );
              scrollProgress =
                1 -
                (currentPosition - endPosition) / totalDistance;
              // 确保进度在0-1范围内
              scrollProgress = Math.max(
                0,
                Math.min(1, scrollProgress)
              );
            }
          }

          // 设置视频当前时间（基于滚动进度）
          const targetTime = scrollProgress * realDuration;

          // 当视频时间与目标时间差异较大时，设置时间点
          if (Math.abs(video.currentTime - targetTime) > 0.1) {
            video.currentTime = targetTime;
          }
        };

        // 添加滚动事件监听
        window.addEventListener("scroll", scrollHandler, {
          passive: true,
        });

        // 确保初始状态正确
        scrollHandler();

        // 组件销毁时清理事件监听
        this.cleanup = () => {
          window.removeEventListener("scroll", scrollHandler);
        };
      });
    }
  }

  disconnectedCallback() {
    if (this.cleanup) {
      this.cleanup();
    }
  }
}
customElements.define("reveal-video-with-text", RevealVideoWithText);

class SecondaryVideo extends HTMLDivElement {
  constructor() {
    super();

    if (theme.config.motionReduced || theme.config.isTouch) return;

    if (this.videoMedia) {
      this.addEventListener("mouseenter", this.onEnterHandler);
      this.addEventListener("mouseleave", this.onLeaveHandler);
    }
  }

  get videoMedia() {
    return this.querySelector("video-media");
  }

  onEnterHandler() {
    this.videoMedia?.play();
  }

  onLeaveHandler() {
    this.videoMedia?.pause();
  }
}
customElements.define("secondary-video", SecondaryVideo, { extends: "div" });

class SocialFeed extends XModal {
  constructor() {
    super();
  }

  get shouldCloseAll() {
    return true;
  }

  get lookbook() {
    return this.querySelector("lookbook-element");
  }

  beforeShow() {
    super.beforeShow();
    this.lookbook.closeAll();
  }

  async afterShow() {
    super.afterShow();
    Motion.inView(this.lookbook, async () => {
      await theme.utils.imageLoaded(this.lookbook.media);
      this.lookbook.animate();
    });
  }
}
customElements.define("social-feed", SocialFeed);

class SocialFeedButton extends APIButton {
  constructor() {
    super();

    this.addEventListener("click", this.onButtonClick);
  }

  get controlledElement() {
    return this.hasAttribute("aria-controls")
      ? document.getElementById(this.getAttribute("aria-controls"))
      : null;
  }

  onButtonClick() {
    if (this.controlledElement) this.controlledElement.show();
  }
}
customElements.define("social-feed-button", SocialFeedButton, {
  extends: "button",
});

class IconsCarousel extends HTMLDivElement {
  constructor() {
    super();

    this.onEnterListener = this.onEnterHandler.bind(this);
    this.onLeaveListener = this.onLeaveHandler.bind(this);
  }

  connectedCallback() {
    if (theme.config.isTouch) return;

    this.addEventListener("mouseenter", this.onEnterListener);
    this.addEventListener("mouseleave", this.onLeaveListener);
  }

  disconnectedCallback() {
    if (theme.config.isTouch) return;

    this.removeEventListener("mouseenter", this.onEnterListener);
    this.removeEventListener("mouseleave", this.onLeaveListener);
  }

  onEnterHandler() {
    this.scrollTo({
      left: this.scrollWidth,
      behavior: theme.config.motionReduced ? "auto" : "smooth",
    });
  }

  onLeaveHandler() {
    this.scrollTo({
      left: 0,
      behavior: theme.config.motionReduced ? "auto" : "smooth",
    });
  }
}
customElements.define("icons-carousel", IconsCarousel, { extends: "div" });

/*
  基于Swiper库的封装，用法：
  ------------------------------------------------------------------------------------------------------------
  <swiper-component>
    <script data-swiper-options type="application/json">
      {
        "usePage": true,
        "useNav": true,
        "pageBottom": false,
        "paddingRight": "100px"
      }
    </script>
    <script data-swiper-options type="application/json">
      {
        "usePage": false
      }
    </script>
    <div class="swiper">
      <div class="swiper-wrapper">
        {%- for block in section.blocks -%}
          <div class="swiper-slide">Slide {{ forloop.index }}</div>
        {%- endfor -%}
      </div>
    </div>
  </swiper-component>
  ------------------------------------------------------------------------------------------------------------

  配置项如何定义：
    在swiper-component里面定义json模版，可以定义多个，后面的会覆盖前面的属性。
    <script data-swiper-options type="application/json">
      { "usePage": true }
    </script>

  SwiperComponent的配置项：
    usePage: {Boolean} 是否使用分页栏
    pageBottom: {Boolean} true时分页栏在底部，false时分页栏在底部浮动（盖住swiper-slide）
    useNav: {Boolean} 是否使用导航按钮
    navPosition: {String} 导航按钮的位置，可选值：
      float: 左右两侧浮动
      title: 放到section-heading.liquid的右侧
    paddingRight: {String} 分页栏的右边距，如果想在右边展示一部分内容，可设置一个偏移量，比如100px
    sameHeight: {Boolean} true表示所有slide item的高度和"最高"的那个一样高。
    enableDesktop: {Boolean} 是否启用PC端swiper。
    enableMobile: {Boolean} 是否启用移动端swiper。


  其他的配置项均是Swiper的配置项，具体请查看Swiper官方文档。

  关于UI的样式请自行定义，请查看theme.css的swiper-component部分。
 */
class SwiperComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // 合并所有的配置项
    const optionArray = []
    this.querySelectorAll('script[data-swiper-options]').forEach(sc => {
      optionArray.push(JSON.parse(sc.textContent))
    })
    let tmp = {}
    for (let i = 0; i < optionArray.length; i++) {
      tmp = SwiperComponent.deepMerge(tmp, optionArray[i])
    }
    this.customOptions = tmp

    // 这两个决定导航按钮和分页栏是否展示出来
    if (!this.customOptions.enableDesktop) {
      this.classList.add('disabled-swiper-desktop')
    }
    if (!this.customOptions.enableMobile) {
      this.classList.add('disabled-swiper-mobile')
    }

    /* ================= swiper初始化 START ================= */
    this.swiperElement = this.querySelector('.swiper')

    const options = SwiperComponent.deepMerge({
      enableMobile: true,
      enableDesktop: true,
      usePage: true,
      useNav: true,
      pageBottom: true,
      slidesPerView: 2,
      spaceBetween: 20,
      paddingRight: "0",
      paddingRightMobile: "0"
    }, this.customOptions)
    this.options = options

    if (options.pageBottom) {
      this.classList.add('pagination-bottom')
    }

    let pagination = null
    let navigation = null
    // 分页栏
    if (options.usePage === true) {
      const paginationElement = document.createElement('span')
      paginationElement.classList.add('swiper-pagination')
      this.swiperElement.appendChild(paginationElement)
      options.pagination = { el: paginationElement, clickable: true }
      this.paginationElement = paginationElement
    }

    // 导航按钮
    if (options.useNav === true) {
      const navElementPrev = document.createElement('span')
      navElementPrev.classList.add('swiper-nav-button')
      navElementPrev.classList.add('swiper-button-prev')
      this.swiperElement.appendChild(navElementPrev)

      const navElementNext = document.createElement('span')
      navElementNext.classList.add('swiper-nav-button')
      navElementNext.classList.add('swiper-button-next')
      this.swiperElement.appendChild(navElementNext)
      options.navigation = { nextEl: navElementNext, prevEl: navElementPrev }
      this.navNextElement = navElementNext
    }


    this.swiper = new Swiper(this.swiperElement, options);
    /* ================= swiper初始化 END ================= */

    // 监听视口变化，更新导航按钮的位置
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    mediaQuery.addEventListener('change', this.onMediaQueryChanged.bind(this));
    this.onMediaQueryChanged(mediaQuery);
    this.initNavPosition()

    // sameHeight配置，使每个slide item的高度统一的处理代码
    if (options.sameHeight) {
      const onWindowSizeChanged = theme.utils.debounce(() => {
        this.updateSlideItemHeightToMaxHeight(this.swiper)
      }, 300)

      const observer = new ResizeObserver(entries => {
        onWindowSizeChanged()
      });
      observer.observe(document.body);
    }
  }

  initNavPosition () {
    if (this.customOptions.navPosition.includes('title')) {
      const navContainer = this.closest('.shopify-section').querySelector('.section-title-container .swiper-nav-button-container')
      this.swiperElement.querySelectorAll('.swiper-nav-button').forEach(btn => {
        navContainer.appendChild(btn)
      })
    }
  }

  onMediaQueryChanged (e) {
    if (e.matches) {
      this.style.paddingRight = this.options.enableMobile ? this.options.paddingRightMobile : 0
    } else {
      this.style.paddingRight = this.options.enableDesktop ? this.options.paddingRight : 0
    }
    if (!this.customOptions.navPosition || this.customOptions.navPosition === 'float') {
      this.adjustPositionByPaddingRight(this.style.paddingRight)
    }
  }

  adjustPositionByPaddingRight(paddingRight) {
    if (this.paginationElement) {
      this.paginationElement.style.transform = `translateX(calc(${paddingRight} / 2))`
    }
    if (this.navNextElement) {
      this.navNextElement.style.transform = `translateX(${paddingRight})`
    }
  }

  /**
   * 将所有slide元素的高度设置为"最高"的那个slide元素的高度
   */
  updateSlideItemHeightToMaxHeight(swiper) {

    const realItems = []
    this.querySelectorAll('.swiper-slide').forEach(slide => {
      realItems.push(slide.firstElementChild)
    })

    // 1. 计算最大高度
    const calculateMaxHeight = () => {
      let maxHeight = 0
      realItems.forEach(item => {
        item.style.height = 'auto'
        const height = item.getBoundingClientRect().height
        if (height > maxHeight) maxHeight = height
      })
      return maxHeight
    }

    // 2. 设置所有轮播项高度
    const unifySlideHeights = (height) => {
      this.querySelectorAll('.swiper-slide').forEach(slide => {
        slide.style.height = `${height}px`;
        slide.style.minHeight = `${height}px`;
      });

      realItems.forEach(item => {
        item.style.height = '100%'
      })

      // Swiper高度刷新机制
      swiper.updateAutoHeight();
    };

    unifySlideHeights(calculateMaxHeight());
  }

  /**
   * 将source的属性深度覆盖到target中
   * @param target
   * @param source
   * @return {*}
   */
  static deepMerge(target, source) {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        const sourceVal = source[key];
        const targetVal = target[key];

        // 双方都是纯对象且目标对象存在该属性时递归合并
        if (
          typeof sourceVal === 'object' &&
          sourceVal !== null &&
          !Array.isArray(sourceVal) &&
          target.hasOwnProperty(key) &&
          typeof targetVal === 'object' &&
          targetVal !== null &&
          !Array.isArray(targetVal)
        ) {
          SwiperComponent.deepMerge(targetVal, sourceVal);
        } else {
          target[key] = sourceVal;
        }
      }
    }
    return target;
  }
}
customElements.define('swiper-component', SwiperComponent)


/**
 * 原模版的slideshow的height-adapt有bug，开发一个新的slideshow（基于Swiper库实现）。
 */
class SwiperSlideshow extends HTMLElement {
  constructor() {
    super();
    this.isMobileMedia = window.matchMedia('(max-width: 767px)')
  }

  connectedCallback() {
    this.swiperElement = this.querySelector('.swiper')

    const comp = this
    const options = {
      loop: this.dataset.loop === 'true',
      slidesPerView: 1,
      spaceBetween: 0,
      autoHeight: true,
      lazy: true,
      on: {
        init: function () {
          comp.onSwiperReady()
        },
        slideChangeTransitionEnd: function (e) {
          comp.onSlideItemChanged(e.activeIndex)
        },
        slideChangeTransitionStart: function () {
          comp.onSlideItemStartChange()
        }
      }
    }
    if (this.dataset.autoplay === 'true') {
      options.autoplay = {
        delay: parseInt(this.dataset.autoplaySpeed || '5') * 1000,
      }
    }

    let pagination = null
    let navigation = null

    const paginationElement = document.createElement('span')
    paginationElement.classList.add('swiper-pagination')
    this.swiperElement.appendChild(paginationElement)
    options.pagination = { el: paginationElement, clickable: true }

    const navElementPrev = document.createElement('span')
    navElementPrev.classList.add('swiper-nav-button')
    navElementPrev.classList.add('swiper-button-prev')
    this.swiperElement.appendChild(navElementPrev)

    const navElementNext = document.createElement('span')
    navElementNext.classList.add('swiper-nav-button')
    navElementNext.classList.add('swiper-button-next')
    this.swiperElement.appendChild(navElementNext)
    options.navigation = { nextEl: navElementNext, prevEl: navElementPrev }


    this.swiper = new Swiper(this.swiperElement, options);

  }

  onSwiperReady () {
    this.onSlideItemChanged(0)
  }

  onSlideItemStartChange () {
    this.swiperElement.querySelectorAll('.slideshow-item video-media').forEach(vm => {
      vm.pause()
    })
  }

  onSlideItemChanged (index) {
    const slideshowItem = this.swiperElement.querySelector(`.swiper-slide:nth-child(${index + 1}) .slideshow-item`)
    if (slideshowItem.dataset.type === 'video') {
      let videoMedia = null
      if (this.isMobileMedia.matches) {
        videoMedia = slideshowItem.querySelector('video-media.mobile-media')
        if (!videoMedia) {
          videoMedia = slideshowItem.querySelector('video-media:not(.mobile-media)')
        }
      } else {
        videoMedia = slideshowItem.querySelector('video-media:not(.mobile-media)')
      }
      videoMedia?.play()
    }
  }
}
customElements.define('swiper-slideshow', SwiperSlideshow)


class SwiperTestimonials extends HTMLElement {
  constructor() {
    super();
    this.isMobileMedia = window.matchMedia('(max-width: 767px)')
    this.isTabletMedia = window.matchMedia('(min-width: 768px) and (max-width: 1024px)')
    this.isDesktopMedia = window.matchMedia('(min-width: 1025px)')
    this.imageDisplayCountDesktop = parseInt(this.dataset.imageCountDesktop)
    this.imageDisplayCountTablet = parseInt(this.dataset.imageCountTablet)
    this.imageDisplayCountMobile = parseInt(this.dataset.imageCountMobile)

    this.brandContainer = this.querySelector('.testimonials__item-images');
    this.brandImages = Array.from(this.querySelectorAll('.testimonials__item-image'))
    this.brandImages.forEach(item => {
      item.addEventListener('click', this.onAuthorImageClick.bind(this))
    })

    this.currentOffset = 0
    this.offsetPadding = 10
    // last状态是为了监听resize事件时，重置状态
    this.lastSelectedIndex = 0
    this.lastDirection = 'next'
    this.lastPadding = this.offsetPadding

  }


  connectedCallback() {

    this.swiperElement = this.querySelector('.testimonials-text.swiper')

    const comp = this
    const options = {
      slidesPerView: 1,
      spaceBetween: 0,
      autoHeight: true,
      loop: true,
      on: {
        init: function () {
          comp.onSlideItemChanged(0, 'prev')
        },
        slideChangeTransitionEnd: function (e) {
        },
        slideChangeTransitionStart: function (e) {
          const dir = e.realIndex > e.previousRealIndex ? 'next' : 'prev'
          comp.onSlideItemChanged(e.realIndex, dir)
        }
      }
    }
    if (this.dataset.autoplay === 'true') {
      options.autoplay = {
        delay: parseInt(this.dataset.autoplaySpeed || '5') * 1000,
      }
    }

    let pagination = null
    let navigation = null

    // const paginationElement = document.createElement('span')
    // paginationElement.classList.add('swiper-pagination')
    // this.swiperElement.appendChild(paginationElement)
    // options.pagination = { el: ".swiper-pagination", clickable: true }

    // const navElementPrev = document.createElement('span')
    // navElementPrev.classList.add('swiper-nav-button')
    // navElementPrev.classList.add('swiper-button-prev')
    // this.appendChild(navElementPrev)

    // const navElementNext = document.createElement('span')
    // navElementNext.classList.add('swiper-nav-button')
    // navElementNext.classList.add('swiper-button-next')
    // this.appendChild(navElementNext)
    // options.navigation = { nextEl: navElementNext, prevEl: navElementPrev }


    this.swiper = new Swiper(this.swiperElement, options);

    this.initResizeListener()
  }

  initResizeListener() {
    const onWindowSizeChanged = theme.utils.debounce(() => {
      this.updateSlideItemHeightToMaxHeight(this.swiper)
      this.resetEnsureCurrentItemVisible()
    }, 300)
    const observer = new ResizeObserver(entries => {
      onWindowSizeChanged()
    });
    observer.observe(document.body);
  }

  onSlideItemChanged (index, dir) {
    this.brandImages.forEach(item => (item.classList.remove('current')))
    this.brandImages[index].classList.add('current')
    this.ensureCurrentItemVisible(this.brandImages[index], dir)
  }

  getRequiredOffset(elementRect, containerRect, padding, dir) {
    // 调整容器边界，考虑padding
    const paddedLeft = containerRect.left + padding
    const paddedRight = containerRect.right - padding

    // 如果元素完全在左侧
    if (elementRect.right <= paddedLeft) {
      return paddedLeft - elementRect.left
    }
    // 如果元素完全在右侧
    else if (elementRect.left >= paddedRight) {
      return paddedRight - elementRect.right
    }
    // 如果元素部分在左侧
    else if (elementRect.left < paddedLeft) {
      return paddedLeft - elementRect.left
    }
    // 如果元素部分在右侧
    else if (elementRect.right > paddedRight) {
      // 对于next方向，我们希望元素尽可能在右侧可见
      if (dir === 'next') {
        return paddedRight - elementRect.right
      }
      // 对于prev方向，我们希望元素尽可能在左侧可见
      else if (dir === 'prev') {
        return paddedLeft - elementRect.left
      }
    }
    return 0
  }

  resetEnsureCurrentItemVisible() {
    this.currentOffset = 0
    this.brandImages.forEach(item => {
      item.style.transform = `translateX(${this.currentOffset}px)`
    })

    setTimeout(() => {
      const item = this.brandImages.find(item => item.classList.contains('current'))
      this.ensureCurrentItemVisible(item, this.lastDirection)
    }, 500)

  }
  ensureCurrentItemVisible(item, dir) {

    const boxList = this.brandImages
    const padding = this.offsetPadding
    // 保存当前选择
    this.lastSelectedIndex = boxList.indexOf(item)
    this.lastDirection = dir
    this.lastPadding = padding

    const containerRect = this.brandContainer.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()
    let adjacentItem = null
    let adjacentItemRect = null

    // 获取相邻元素
    const index = boxList.indexOf(item)
    if (dir === 'next' && index < boxList.length - 1) {
      adjacentItem = boxList[index + 1]
      adjacentItemRect = adjacentItem.getBoundingClientRect()
    }
    else if (dir === 'prev' && index > 0) {
      adjacentItem = boxList[index - 1]
      adjacentItemRect = adjacentItem.getBoundingClientRect()
    }

    // 计算需要的偏移量
    let offset = 0

    // 首先检查当前元素是否完全可见（考虑padding）
    const itemOffset = this.getRequiredOffset(itemRect, containerRect, padding, dir)
    if (itemOffset !== 0) {
      offset = itemOffset
      // 如果当前元素被平移，确保相邻元素也可见（考虑padding）
      if (adjacentItem) {
        const adjacentOffset = this.getRequiredOffset(adjacentItemRect, containerRect, padding, dir)
        // 对于next方向，确保下一个元素在右侧可见
        if (dir === 'next' && adjacentOffset !== 0) {
          offset = adjacentOffset
        }
        // 对于prev方向，确保上一个元素在左侧可见
        else if (dir === 'prev' && adjacentOffset !== 0) {
          offset = adjacentOffset
        }
      }
    }
    // 如果当前元素已经可见，检查相邻元素（考虑padding）
    else if (adjacentItem) {
      offset = this.getRequiredOffset(adjacentItemRect, containerRect, padding, dir)
    }

    // 如果不需要移动，直接返回
    if (offset === 0) {
      return
    }

    // 计算新的总偏移量
    this.currentOffset += offset

    this.brandImages.forEach(item => {
      item.style.transform = `translateX(${this.currentOffset}px)`
    })
  }

  onAuthorImageClick (e) {
    const targetElement = e.currentTarget.closest('.testimonials__item-image');
    const targetIndex = parseInt(targetElement.dataset.index)
    const position = targetIndex - 1
    if (position >= 0) {
      this.swiper.slideToLoop(position)
    }
  }



  /**
   * 将所有slide元素的高度设置为"最高"的那个slide元素的高度
   */
  updateSlideItemHeightToMaxHeight(swiper) {

    const realItems = []
    swiper.el.querySelectorAll('.swiper-slide').forEach(slide => {
      realItems.push(slide.firstElementChild)
    })

    // 1. 计算最大高度
    const calculateMaxHeight = () => {
      let maxHeight = 0
      realItems.forEach(item => {
        item.style.height = 'auto'
        const height = item.getBoundingClientRect().height
        if (height > maxHeight) maxHeight = height
      })
      return maxHeight
    }

    // 2. 设置所有轮播项高度
    const unifySlideHeights = (height) => {
      swiper.el.querySelectorAll('.swiper-slide').forEach(slide => {
        slide.style.height = `${height}px`;
        slide.style.minHeight = `${height}px`;
      });

      realItems.forEach(item => {
        item.style.height = '100%'
      })

      // Swiper高度刷新机制
      swiper.updateAutoHeight();
    };

    unifySlideHeights(calculateMaxHeight());
  }
}
customElements.define('swiper-testimonials', SwiperTestimonials)

class WaterFall extends HTMLElement {
  constructor() {
    super();
  }

  initWaterfallContainerByOrderMode (containerClass, columns, clone) {
    const containers = []
    for (let i = 0; i < columns; i++) {
      const containerEl = document.createElement('div')
      containerEl.classList.add('waterfall-container', containerClass)
      this.appendChild(containerEl)
      containers.push(containerEl)
    }

    let i = 0
    this.blocks.forEach(blockEl => {
      if (clone) {
        containers[i].appendChild(blockEl.cloneNode(true))
      } else {
        containers[i].appendChild(blockEl)
      }
      i++
      if (i >= columns) {
        i = 0
      }
    })
  }

  initWaterfallContainerByMinHeightMode (containerClass, columns, clone) {
    // 创建指定数量的容器列
    const containers = [];
    const containerHeights = new Array(columns).fill(0); // 跟踪每列高度

    for (let i = 0; i < columns; i++) {
      const containerEl = document.createElement('div');
      containerEl.classList.add('waterfall-container', containerClass);
      this.appendChild(containerEl);
      containers.push(containerEl);
    }

    // 计算所有块的尺寸（需元素实际渲染）
    const blockDimensions = [];
    this.blocks.forEach(block => {
      const temp = clone ? block.cloneNode(true) : block;
      this.appendChild(temp);  // 临时加入文档流获取尺寸
      blockDimensions.push({
        element: block,
        height: temp.offsetHeight
      });
      this.removeChild(temp);  // 获取后从文档流中移除
    });

    // 按高度排序 (高大元素优先平衡布局)
    blockDimensions.sort((a, b) => b.height - a.height);

    // 平衡分配算法：优先放入高度最小的列
    blockDimensions.forEach(data => {
      const minHeightIndex = containerHeights.indexOf(Math.min(...containerHeights));
      const targetElement = clone ? data.element.cloneNode(true) : data.element;

      containers[minHeightIndex].appendChild(targetElement);
      containerHeights[minHeightIndex] += data.height;
    });
  }


  connectedCallback() {
    this.blocks = this.querySelectorAll('.waterfall__block')
    if (this.dataset.mode === 'order') {
      this.initWaterfallContainerByOrderMode('desktop', parseInt(this.dataset.columnsDesktop || '1'), true)
      this.initWaterfallContainerByOrderMode('mobile', parseInt(this.dataset.columnsDesktop || '1'), false)
    } else {
      this.initWaterfallContainerByMinHeightMode('desktop', parseInt(this.dataset.columnsDesktop || '1'), true)
      this.initWaterfallContainerByMinHeightMode('mobile', parseInt(this.dataset.columnsDesktop || '1'), false)
    }
  }
}
customElements.define('water-fall', WaterFall)


/*
  一个播放按钮，用法：
  html:
  <play-button id="myPlay"></play-button>

  JS:
  const btn = document.getElementById('myPlay');
  // 点击按钮时，获取按钮的播放状态
  btn.addEventListener('playStatusChanged', (e) => {
    const isPlaying = e.detail.isPlaying;
  })

  // 设置按钮的播放进度，0～100
  btn.setProgress(10.3)

  // 设置播放状态
  btn.setPlaying(true)
 */
class PlayButton extends HTMLElement {
  constructor() {
    super()
    this.innerHTML = `
    <svg class="pt-progress" viewBox="0 0 100 100">
      <circle class="pt-progress-background" cx="50" cy="50" r="45"></circle>
      <circle class="pt-progress-circle" cx="50" cy="50" r="45" style="stroke-dasharray: 283; stroke-dashoffset: 283;"></circle>
    </svg>
    `
  }

  connectedCallback() {
    this.progress = this.querySelector('.pt-progress-circle')

    this.addEventListener('click', () => {

      this.classList.toggle('playing')

      const playing = this.classList.contains('playing')

      // 创建并分发自定义事件
      const event = new CustomEvent('playStatusChanged', {
        detail: {
          isPlaying: playing
        },
        bubbles: true,    // 允许事件冒泡
        composed: true    // 允许事件穿过 shadow DOM
      });

      this.dispatchEvent(event);
    })
  }

  setPlaying(isPlaying) {
    this.classList.remove('playing')
    if (isPlaying) {
      this.classList.add('playing')
    }
  }

  /**
   * @param progress {Number} 0 ~ 100
   */
  setProgress (progress) {
    const t = 283.0 - 283.0 / 100.0 *  progress
    this.progress.style.strokeDashoffset = t
  }
}
customElements.define('play-button', PlayButton);


class VideoHeroV2 extends HTMLElement {
  constructor() {
    super();
    this.isMobileMedia = window.matchMedia('(max-width: 767px)')

    document.addEventListener('matchSmall', () => {
      this.mediaQueryChanged(true)
    })
    document.addEventListener('unmatchSmall', () => {
      this.mediaQueryChanged(false)
    })


  }

  connectedCallback() {
    this.playButton = this.querySelector('play-button')
    this.playButton.addEventListener('playStatusChanged', this.onPlayButtonPlayingChanged.bind(this))

    this.videoDesktop = this.querySelector('video-media:not(.mobile-media)')
    if (this.videoDesktop) {
      this.videoDesktop.addEventListener('playStart', this.onVideoPlayStart.bind(this))
      this.videoDesktop.addEventListener('playStop', this.onVideoPlayStop.bind(this))
      this.videoDesktop.addEventListener('videoProgress', this.onVideoProgressUpdated.bind(this))
    }

    this.videoMobile = this.querySelector('video-media.mobile-media')
    if (this.videoMobile) {
      this.videoMobile.addEventListener('playStart', this.onVideoPlayStart.bind(this))
      this.videoMobile.addEventListener('playStop', this.onVideoPlayStop.bind(this))
      this.videoMobile.addEventListener('videoProgress', this.onVideoProgressUpdated.bind(this))
    }

    this.mediaQueryChanged(this.isMobileMedia.matches)


    this.observer = new IntersectionObserver(entries => this.handleIntersection(entries),
      {
        root: null,
        rootMargin: '300px',
        threshold: 0.01
      }
    );

    this.observer.observe(this);
  }

  handleIntersection(entries) {
    if (entries[0].isIntersecting) {
      this.observer.disconnect()
      this.getPlayer()?.play()
    }
  }

  mediaQueryChanged(isMobile) {
    if (isMobile) {
      this.videoDesktop?.pause()
    } else {
      this.videoMobile?.pause()
    }
    this.getPlayer()?.play()
  }

  getPlayer() {
    if (this.isMobileMedia.matches) {
      return this.videoMobile || this.videoDesktop
    } else {
      return this.videoDesktop
    }
  }

  onPlayButtonPlayingChanged(e) {
    const isPlaying = e.detail.isPlaying
    if (isPlaying) {
      this.getPlayer()?.play()
    } else {
      this.getPlayer()?.pause()
    }
  }

  onVideoPlayStart() {
    this.playButton.setPlaying(true)
  }

  onVideoPlayStop() {
    this.playButton.setPlaying(false)
  }

  onVideoProgressUpdated(e) {
    const percent = e.detail.progress
    this.playButton.setProgress(percent)
  }
}
customElements.define('video-hero-v2', VideoHeroV2);

class VideoHeroV3 extends HTMLElement {
  constructor() {
    super();
    this.playAll = false
    this.playBeforeSec = parseInt(this.dataset.autoPlayTime || 999)
    this.playBeforeSecMobile = parseInt(this.dataset.autoPlayTimeMobile || 999)

    this.isMobileMedia = window.matchMedia('(max-width: 767px)')
    this.isMobileMedia.addEventListener('change', (e) => {
      this.mediaQueryChanged(e.matches)
    })

    this.querySelector('.button-watchvideo')?.addEventListener('click', this.openVideoModal.bind(this))

    this.playButton = this.querySelector('play-button')
    this.playButton.addEventListener('playStatusChanged', this.onPlayButtonPlayingChanged.bind(this))

    this.videoDesktop = this.querySelector('video-media:not(.mobile-media)')
    if (this.videoDesktop) {
      this.videoDesktop.addEventListener('playStart', this.onVideoPlayStart.bind(this))
      this.videoDesktop.addEventListener('playStop', this.onVideoPlayStop.bind(this))
      this.videoDesktop.addEventListener('videoProgress', this.onVideoProgressUpdated.bind(this))
      this.videoDesktop.addEventListener('timeupdate', this.onVideoTimeUpdated.bind(this))
    }
    
    this.videoMobile = this.querySelector('video-media.mobile-media')
    if (this.videoMobile) {
      this.videoMobile.addEventListener('playStart', this.onVideoPlayStart.bind(this))
      this.videoMobile.addEventListener('playStop', this.onVideoPlayStop.bind(this))
      this.videoMobile.addEventListener('videoProgress', this.onVideoProgressUpdated.bind(this))
      this.videoMobile.addEventListener('timeupdate', this.onVideoTimeUpdated.bind(this))
    }

    this.overlay = this.querySelector('.video-herov3-overlay')
    this.overlayOpacity = parseFloat(this.overlay.dataset.opacity)
    this.contentElement = this.querySelector('.content__text')

    const timeline = gsap.timeline()
    // 第一阶段：从底部移动到中间
    timeline.fromTo(
      this.contentElement,
      { y: '100vh', opacity: 0 },  // 从底部开始（完全在视图外）
      { 
        y: '50vh',                  // 移动到垂直中间
        opacity: 1,                // 淡入
        duration: 0.7,             // 动画持续时间
        ease: 'power2.out'         // 缓动效果
      }
    );
    timeline.fromTo(
      this.overlay,
      { opacity: 0 },
      { opacity: this.overlayOpacity, duration: .7, ease: 'power2.out' },
      '<'
    )

    // 第二阶段：停留200ms
    timeline.to(
      this.contentElement,
      { 
        duration: 0.1,             // 停留时间（ScrollMagic会将其转换为滚动距离）
        ease: 'none'              // 无动画，保持静止
      }
    );

    // 第三阶段：contentElement移动到顶部，overlay淡出
    timeline.to(
      this.contentElement,
      { 
        y: '-10vh',
        duration: 0.7,
        opacity: 0,
        ease: 'power2.out'
      }
    ).to(
      this.overlay,
      { 
        opacity: 0,
        duration: 0.7,
        ease: 'power2.out'
      },
      '<' // 与contentElement动画同时开始
    );


    new ScrollMagic.Scene({triggerElement: this, triggerHook: 0, duration: 1500})
      .setTween(timeline)
      .setPin(this)
      .addTo(window.scrollMagicController)
  }

  mediaQueryChanged(isMobile) {
    if (isMobile) {
      this.videoDesktop?.pause()
    } else {
      this.videoMobile?.pause()
    }
    this.getPlayer()?.play()
  }

  getPlayer() {
    if (this.isMobileMedia.matches) {
      return this.videoMobile || this.videoDesktop
    } else {
      return this.videoDesktop
    }
  }

  onPlayButtonPlayingChanged(e) {
    const isPlaying = e.detail.isPlaying
    if (isPlaying) {
      this.getPlayer()?.play()
    } else {
      this.getPlayer()?.pause()
    }
  }

  onVideoPlayStart() {
    this.playButton.setPlaying(true)
  }

  onVideoPlayStop() {
    this.playButton.setPlaying(false)
  }

  onVideoProgressUpdated(e) {
    const percent = e.detail.progress
    this.playButton.setProgress(percent)
  }

  onVideoTimeUpdated(e) {
    if (this.playAll) return
    let sec
    if (this.isMobileMedia.matches) {
      sec = this.playBeforeSecMobile
    } else {
      sec = this.playBeforeSec
    }
    if (e.detail.currentTime >= sec) {
      this.getPlayer()?.replay()
    }
  }

  onPlayButtonClick() {
    this.playAll = true
    this.classList.add('play-all')
  }

  openVideoModal() {
    if (!this.getPlayer()) return;

    // 创建模态框
    this.modal = document.createElement('div');
    this.modal.className = 'video-modal';
    this.modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.9);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
    `;

    // 创建关闭按钮容器
    const closeButton = document.createElement('div');
    closeButton.className = 'video-modal-close';
    closeButton.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      cursor: pointer;
      z-index: 10000;
      color: white;
      font-size: 30px;
      text-align: center;
      line-height: 40px;
    `;
    closeButton.innerHTML = '×';
    closeButton.addEventListener('click', this.closeVideoModal.bind(this));

    // 创建视频容器
    const videoContainer = document.createElement('div');
    videoContainer.className = 'video-container';
    videoContainer.style.cssText = `
      width: 90%;
      max-width: 1200px;
      position: relative;
    `;

    // 克隆视频元素并保留所有属性和子元素
    const mediaType = this.isMobileMedia.matches ? 'mobile' : 'desktop'
    const videoClone = this.querySelector('template[video]')
      .cloneNode(true)
      .content
      .querySelector(`.${mediaType} video`)
    videoClone.style.cssText = `
      width: 100%;
      height: auto;
      display: block;
      max-height: 90vh;
    `;
    
    // 确保视频有控制栏和声音
    videoClone.controls = true;
    videoClone.muted = false; // 允许声音

    // 组装模态框
    videoContainer.appendChild(videoClone);
    this.modal.appendChild(videoContainer);
    this.modal.appendChild(closeButton);
    document.body.appendChild(this.modal);
    document.body.style.overflow = 'hidden';

    // 自动播放视频（现代浏览器可能需要用户交互后才允许带声音的自动播放）
    const playPromise = videoClone.play();
    
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        // 如果自动播放失败，显示控制栏让用户手动播放
        videoClone.controls = true;
      });
    }
  }

  closeVideoModal() {
    if (!this.modal) return;

    // 暂停视频
    const video = this.modal.querySelector('video');
    if (video) {
      video.pause();
    }

    // 移除模态框
    this.modal.remove();
    this.modal = null;
    document.body.style.overflow = '';
  }
}
customElements.define('video-hero-v3', VideoHeroV3);

class VideoHeroV4 extends HTMLElement {
  constructor() {
    super()

    this.startPlayOnVisiblePercent = parseFloat(this.dataset.playOnVisiblePercent)
    this.contentShowOnVideoEnd = this.dataset.contentShowOnVideoEnd === 'true'
    this.contentShowOnVideoInSec = null
    if (this.dataset.contentShowOnVideoInSec) {
      this.contentShowOnVideoInSec = parseFloat(this.dataset.contentShowOnVideoInSec)
    }
    this.contentShowOnVideoInSecAnimated = false

    this.overlay = this.querySelector('.video-overlay')
    this.videoContent = this.querySelector('.video-content')

    if (this.contentShowOnVideoEnd || this.contentShowOnVideoInSec > 0) {
      this.overlay.style.opacity = 0
      this.videoContent.style.opacity = 0
    }

    this.isMobileMedia = window.matchMedia('(max-width: 767px)')
    this.isMobileMedia.addEventListener('change', (e) => {
      this.mediaQueryChanged(e.matches)
    })

    this.videoDesktop = this.querySelector('video-media:not(.mobile-media)')
    this.videoMobile = this.querySelector('video-media.mobile-media')

    if (this.contentShowOnVideoEnd) {
      this.videoDesktop.addEventListener('playComplete', this.onVideoEnd.bind(this))
      this.videoMobile.addEventListener('playComplete', this.onVideoEnd.bind(this))
    } else if (this.contentShowOnVideoInSec) {
      this.videoDesktop.addEventListener('timeupdate', this.onVideoTimeUpdate.bind(this))
      this.videoMobile.addEventListener('timeupdate', this.onVideoTimeUpdate.bind(this))
    }

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (entry.isIntersecting) {
        const visibleHeight = entry.intersectionRect.height;
        const elHeight = entry.boundingClientRect.height;
        
        // 处理元素高度为0的情况（防止除以0）
        const visiblePercent = elHeight > 0 ? visibleHeight / elHeight : 0;
        
        // 考虑元素被部分遮挡的情况
        const isFullyVisible = entry.intersectionRatio === 1;
        const isVisibleEnough = visiblePercent >= this.startPlayOnVisiblePercent;
        const isCoveringViewport = visibleHeight >= window.innerHeight;
        
        if (isFullyVisible || isVisibleEnough || isCoveringViewport) {
          this.startPlayVideo();
          observer.disconnect();
        }
      }
    }, {
      threshold: [0,0.1,0.2,0.3,0.4,0.5,0.6,0.7,0.8,0.9,1], // 自定义计算可见区域
      rootMargin: '0px' // 可扩展监控边界
    })
    observer.observe(this)
  }

  mediaQueryChanged(isMobile) {
    if (isMobile) {
      this.videoDesktop?.pause()
    } else {
      this.videoMobile?.pause()
    }
    if (this.played === true) {
      this.getPlayer()?.play()
    }
  }

  getPlayer() {
    if (this.isMobileMedia.matches) {
      return this.videoMobile || this.videoDesktop
    } else {
      return this.videoDesktop
    }
  }

  startPlayVideo() {
    this.played = true
    this.getPlayer()?.play()
  }

  onVideoEnd() {
    if (this.contentShowOnVideoEnd) {
      gsap.fromTo(this.overlay, { opacity: 0 }, { opacity: 1, duration: .3 })
      gsap.fromTo(this.videoContent, { opacity: 0 }, { opacity: 1, duration: .3 })
    }
  }

  onVideoTimeUpdate(e) {
    if (this.contentShowOnVideoInSecAnimated) return
    if (e.detail.currentTime >= this.contentShowOnVideoInSec) {
      this.contentShowOnVideoInSecAnimated = true
      gsap.fromTo(this.overlay, { opacity: 0 }, { opacity: 1, duration: .3 })
      gsap.fromTo(this.videoContent, { opacity: 0 }, { opacity: 1, duration: .3 })
    }
  }
}
customElements.define('video-hero-v4', VideoHeroV4);

class PinAnimeBanner extends HTMLElement {
  constructor() {
    super();
    const timeline = gsap.timeline()
    timeline.fromTo(this.querySelectorAll('.pab__content-text-item-group.group-1'), {
      y: 0,
      opacity: 1
    }, {
      opacity: 0,
      y: -20,
      // transformOrigin: "center top",
      // force3D: true, // 启用3D加速
      // snap: { scaleY: 0.1 }, // 限制缩放步进值
      ease: "power2.inOut",
      delay: 0.05
    })
    timeline.fromTo(this.querySelectorAll('.pab__content-text-item-group.group-2'), {
      y: -20,
      opacity: 0
    }, {
      opacity: 1,
      y: 0,
      // transformOrigin: "center bottom",
      // force3D: true, // 启用3D加速
      // snap: { scaleY: 0.1 }, // 限制缩放步进值
      ease: "power2.inOut"
    }, '<')

    new ScrollMagic.Scene({triggerElement: this, triggerHook: 0, duration: 10})
      .setTween(timeline)
      .setPin(this)
      .addTo(window.scrollMagicController)
  }

  connectedCallback() {
  }
}
customElements.define('pin-anime-banner', PinAnimeBanner)

class PinWipes extends HTMLElement {
  constructor() {
    super()
    this.isMobileMedia = window.matchMedia(theme.config.mediaQuerySmall)
    this.isMobileMedia.addEventListener('change', this.onMediaQueryChanged.bind(this))

    this.horizontalOnMobile = this.dataset.horizontalOnMobile === 'true'

    this.wipes = this.querySelectorAll('.wipe__item')
    this.pages = Array.from(this.querySelectorAll('.wipe__pagination li') || [])
    this.initWipes()
    this.updateActivePage(0)
  }

  onMediaQueryChanged() {
    if (this.horizontalOnMobile === true) {
      // 保留当前滚动位置
      const scrollProgress = this.scrollMagicScene ? this.scrollMagicScene.progress() : 0;
      
      // 清除现有动画
      if (this.scrollMagicScene) this.scrollMagicScene.destroy(true);
      if (this.timeline) this.timeline.kill();
      
      // 重建动画
      this.initWipes();
      
      // 恢复滚动位置
      if (this.scrollMagicScene) {
        this.scrollMagicScene.progress(scrollProgress);
      }
    }
  }

  initWipes() {
    const items = Array.from(this.wipes)
    if (items.length <= 1) return

    let moveDir = 'y'
    if (this.horizontalOnMobile === true && this.isMobileMedia.matches) {
      moveDir = 'x'
    }

    this.timeline = gsap.timeline()
    let duration = 0
    for (let i = items.length - 1; i > 0; i--) {
      const currentIndex = items.length - i - 1
      const fromOpt = {}
      fromOpt[moveDir] = '0%'
      const toOpt = {
        delay: 0, 
          duration: 1,
          onReverseComplete: () => {
            this.updateActivePage(currentIndex)
          },
          onStart: () => {
            this.updateActivePage(currentIndex)
          },
          onComplete: () => {
            this.updateActivePage(currentIndex + 1)
          }
      }
      toOpt[moveDir] = '-100%'
      this.timeline.fromTo(items[i], fromOpt, toOpt)
      duration += 1
    }

    this.scrollMagicScene = new ScrollMagic.Scene({
      triggerElement: this,
      triggerHook: 0,
      duration: (duration * 100) + '%'
    })
    .setPin(this)
    .setTween(this.timeline)
    .addTo(window.scrollMagicController)
  }

  updateActivePage(index) {
    for (let i = 0; i < this.pages.length; i++) {
      this.pages[i].classList.remove('active')
      if (index === i) {
        this.pages[i].classList.add('active')
      }
    }
  }

  connectedCallback() {

  }
}
customElements.define('pin-wipes', PinWipes)


class TabTextWithImage extends HTMLElement {
  constructor() {
    super()

    this.imageSplideElement = this.querySelector('.ttwi__image-container .splide')
    this.tabs = Array.from(this.querySelectorAll('.ttwi__tabs .ttwi__tab'))
    this.tabs.forEach(tab => {
      tab.addEventListener('click', this.onTabItemClick.bind(this))
    })

    this.tabContentSplideElement = this.querySelector('.ttwi__tab-contents .splide')
  }

  connectedCallback() {
    this.imageSplide = new Splide(this.imageSplideElement, {
      arrows: false,
      pagination: false,
      drag: false,
      perPage: 1
    })
    this.imageSplide.mount()

    this.tabContentSplide = new Splide(this.tabContentSplideElement, {
      arrows: false,
      pagination: false,
      drag: false,
      perPage: 1
    })
    this.tabContentSplide.mount()
  }

  onTabItemClick(e) {
    const tab = e.target.closest('.ttwi__tab')
    if (tab) {
      this.tabs.forEach(tab => tab.classList.remove('active'))
      tab.classList.add('active')
      const index = this.tabs.indexOf(tab)
      this.imageSplide.go(index)
      this.tabContentSplide.go(index)
    }
  }
}
customElements.define('tab-text-with-image', TabTextWithImage)

class MagicHeading extends HTMLElement {
  constructor() {
    super();

    this.texts = []
    try {
      this.texts = JSON.parse(this.querySelector('script[data-texts]')?.textContent)
    } catch (ignore) {
    }
  }

  connectedCallback() {
    const magicHeading = this.querySelector('.magic-heading')
    if (!magicHeading || this.texts.length === 0) return;
    
    let magicHeadingText = magicHeading.textContent;
    const sortedTexts = [...this.texts].sort((a, b) => b.content.length - a.content.length);
    const replacements = new Map();
    sortedTexts.forEach(text => {
      if (!text) return;
      const marker = `__${Math.random().toString(36).substr(2, 9)}__`;
      let newline = ''
      if (text.newline === true) {
        newline = '<br>'
      }
      replacements.set(marker, `${newline}<span class="magic-block block-${text.blockId}">${text.content}</span>`);
      const regex = new RegExp(escapeRegExp(text.content), 'g');
      magicHeadingText = magicHeadingText.replace(regex, marker);
    })
    replacements.forEach((value, key) => {
      magicHeadingText = magicHeadingText.replace(new RegExp(key, 'g'), value);
    });
    magicHeading.innerHTML = magicHeadingText;
    function escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
  }
}
customElements.define('magic-heading', MagicHeading)

class BannerRichText extends HTMLElement {
  constructor() {
    super()

    const content = this.querySelector('template[data-content]').content.firstElementChild?.cloneNode(true)

    const map = {}
    this.rows = Array.from(content.querySelectorAll('.brt__content-row'))
    for (const row of this.rows) {
      const arr = map[row.dataset.boxId] || []
      arr.push(row)
      map[row.dataset.boxId] = arr
    }


    for (const boxId of Object.keys(map)) {
      const rows = map[boxId]

      const contentContainer = document.createElement('div')
      contentContainer.classList.add(`box-layout-${boxId}`, 'brt__content')
      this.appendChild(contentContainer)

      const box = document.createElement('div')
      box.classList.add('brt__content-box')
      contentContainer.appendChild(box)

      const boxSettings = content.querySelector(`.box-tmp.box-${boxId}`)
      if (boxSettings && boxSettings.dataset.animeDirection) {
        box.classList.add('use-show-anime')
        box.setAttribute('data-anime-direction', boxSettings.dataset.animeDirection)
        boxSettings.remove()
      }

      for (const row of rows) {
        box.appendChild(row)
      }
    }

    this.initShowAnimation()
  }

  initShowAnimation() {
    const animeEls = Array.from(this.querySelectorAll('.use-show-anime'))
    if (animeEls.length > 0) {
      const timeline = gsap.timeline()
      animeEls.forEach(el => {
        const direction = el.dataset.animeDirection || 'toUp'
        let xy = '', from = '', to = ''
        switch (direction) {
          case 'toUp':
            xy = 'y'
            from = 100
            to = 0
            break
          case 'toDown':
            xy = 'y'
            from = -100
            to = 0
            break
          case 'toLeft':
            xy = 'x'
            from = 100
            to = 0
            break
          case 'toRight':
            xy = 'x'
            from = -100
            to = 0
            break
        }
        const fromOpt = { opacity: 0 }
        fromOpt[xy] = from
        const toOpt = { opacity: 1 }
        toOpt[xy] = to
        timeline.fromTo(el, fromOpt, toOpt, 0)
      })
      new ScrollMagic.Scene({triggerElement: this, triggerHook: 0, duration: 700})
        .setTween(timeline)
        .setPin(this)
        .addTo(window.scrollMagicController)
    }
  }

  connectedCallback() {

  }
}
customElements.define('banner-rich-text', BannerRichText)

class ImageAnimateWithOverlay extends HTMLElement {
  constructor() {
    super()

    const section = this.querySelector('.iawo-section')
    const blocks = Array.from(section.querySelectorAll('.iawo-content-block'))
    const timeline = gsap.timeline()

    /*
    动画效果：
    文字分两部分，一部分没有.with-overlay，另一部分有.with-overlay。
    没有.with-overlay的文字只是单纯的动画，有.with-overlay的文字和overlay元素的渐变有关联。

    下面的代码逻辑换算成下面的比较容易理解：
    timeline.add('marker-1')
    timeline.to('.box1', { y: -100 })

    timeline.to('.box2', { x: 100 }, 'marker-1')
    timeline.add('marker-2')
    timeline.to('.box2', { y: -100 })

    timeline.to('.box3.with-overlay', { x: 100 }, 'marker-2')
    timeline.add('marker-3')
    timeline.to('.box3.with-overlay', { y: -100 }, 'overlayStart')

    timeline.to('.box4', { x: 100 }, 'marker-3')


    timeline.fromTo('.overlay', { opacity: 1 }, { opacity: 0, duration: timeline.duration() * 0.8 }, 'overlayStart')
    */
    let overlayStartPosition
    for (let i = 0; i < blocks.length; i++) {
      const el = blocks[i]
      if (i === 0) {
        timeline.add('block_' + i)
        timeline.to(el, { opacity: 0, y: -50 })
      } else if (i === blocks.length - 1) {
        timeline.to(el, { opacity: 1 }, 'block_' + (i - 1))
      } else {
        const withOverlay = el.classList.contains('with-overlay')
        if (!overlayStartPosition) {
          overlayStartPosition = withOverlay ? 'overlayStart' : undefined
        }
        timeline.to(el, { opacity: 1 }, 'block_' + (i - 1))
        timeline.add('block_' + i)
        const opt = { opacity: 0 }
        if (!withOverlay) {
          opt.y = -50
        }
        timeline.to(el, opt, overlayStartPosition)
      }
    }

    const overlay = section.querySelector('.iawo-overlay')

    // overlay透明度变化从第一个.with-overlay元素的动画（透明度1>0）开始执行，贯穿整个timeline
    timeline.fromTo(
      overlay,
      {
        opacity: parseFloat(this.dataset.opacityStart)
      },
      { 
        opacity: parseFloat(this.dataset.opacityEnd),
        duration: timeline.duration() * 0.8
      },
      overlayStartPosition
    );


    new ScrollMagic.Scene({triggerElement: section, triggerHook: 0, duration: '100%'})
      .setTween(timeline)
      .setPin(section)
      .addTo(window.scrollMagicController)
  }

  connectedCallback() {

  }
}

customElements.define('image-animate-with-overlay', ImageAnimateWithOverlay)

/**
 * see snippets/slider-component-options.liquid
 * see theme.css slider-component
 */
class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.sliderComponentContainer = document.createElement('div')
    this.sliderComponentContainer.classList.add('slider-component-container')
    this.appendChild(this.sliderComponentContainer)

    this.sliderElement = this.querySelector('.splide')
    this.sliderComponentContainer.appendChild(this.sliderElement)

    // 合并所有的配置项
    const optionArray = []
    this.querySelectorAll('script[data-slider-options]').forEach(sc => {
      optionArray.push(JSON.parse(sc.textContent))
    })
    let tmp = {}
    for (let i = 0; i < optionArray.length; i++) {
      tmp = theme.utils.deepMerge(tmp, optionArray[i])
    }
    this.customSliderOptions = tmp

    this.sliderOptions = theme.utils.deepMerge({
      enableMobile: true,
      enableDesktop: true,
      pageBottom: true
    }, this.customSliderOptions)
    this.enableArrows = this.sliderOptions.arrows
    this.sliderOptions.arrows = false

    if (this.enableArrows) {
      this.createCustomArrows()
    }
    this.initSlider()
    this.initSlideItemHeight()
  }

  connectedCallback() {
    
  }

  onSliderReady() {
    this.updateArrowStates()
  }

  initSlider() {
    this.slider = new Splide(this.sliderElement, this.sliderOptions)
    this.slider.on('ready', this.onSliderReady.bind(this))
    this.slider.on('move', () => this.updateArrowStates());
    this.slider.mount()
  }

  updateArrowStates() {
    const splide = this.slider;
    // 处理上一个箭头状态
    const isStart = splide.index <= 0;
    const prevDisabled = isStart && !splide.options.rewind;

    this.prevArrows?.forEach(item => (item.disabled = prevDisabled))
    
    // 处理下一个箭头状态
    const isEnd = splide.index >= splide.Components.Controller.getEnd();
    const nextDisabled = isEnd && !splide.options.rewind;
    this.nextArrows?.forEach(item => (item.disabled = nextDisabled))
  }

  createCustomArrows() {
    // 创建箭头容器
    this.arrowsContainer = document.createElement('div');
    this.arrowsContainer.className = 'custom-splide-arrows';
    
    // 创建上一个箭头按钮
    const prevArrow = document.createElement('button');
    prevArrow.className = 'custom-splide-arrow custom-splide-arrow--prev';
    prevArrow.setAttribute('aria-label', 'Previous slide');
    
    // 创建下一个箭头按钮
    const nextArrow = document.createElement('button');
    nextArrow.className = 'custom-splide-arrow custom-splide-arrow--next';
    nextArrow.setAttribute('aria-label', 'Next slide');
    
    // 添加箭头图标（可以根据你的需求修改）
    if (this.dataset.navIcon) {
      const prevImg = document.createElement('img');
      prevImg.src = this.dataset.navIcon;
      prevImg.alt = 'Previous';
      prevArrow.appendChild(prevImg);
      
      const nextImg = document.createElement('img');
      nextImg.src = this.dataset.navIcon;
      nextImg.alt = 'Next';
      nextImg.style.transform = 'scaleX(-1)';
      nextArrow.appendChild(nextImg);
    } else {
      // 如果没有指定图标，使用简单文本
      prevArrow.innerHTML = `
      <svg class="{{ class }}" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path stroke-linecap="round" stroke-linejoin="round" d="M14 6L8 12L14 18"></path>
      </svg>
      `
      nextArrow.innerHTML = `
      <svg class="{{ class }}" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path stroke-linecap="round" stroke-linejoin="round" d="M10 6L16 12L10 18"></path>
      </svg>
      `
    }
    
    // 将箭头添加到容器
    this.arrowsContainer.appendChild(prevArrow);
    this.arrowsContainer.appendChild(nextArrow);

    this.nextArrows = [nextArrow]
    this.prevArrows = [prevArrow]

    this.sliderComponentContainer.appendChild(this.arrowsContainer);

    const navContainer = this.closest('.shopify-section').querySelector('.section-title-container .swiper-nav-button-container');
    if (navContainer) {
      navContainer.appendChild(this.arrowsContainer.cloneNode(true));
      this.nextArrows.push(navContainer.querySelector('.custom-splide-arrow--next'))
      this.prevArrows.push(navContainer.querySelector('.custom-splide-arrow--prev'))
    }

    this.prevArrows.forEach(item => item.addEventListener('click', () => this.slider.go('<')))
    this.nextArrows.forEach(item => item.addEventListener('click', () => this.slider.go('>')))
  }

  initSlideItemHeight() {
    // sameHeight配置，使每个slide item的高度统一的处理代码
    if (this.sliderOptions.sameHeight) {
      const onWindowSizeChanged = theme.utils.debounce(() => {
        this.updateSlideItemHeightToMaxHeight()
      }, 300)

      const observer = new ResizeObserver(entries => {
        onWindowSizeChanged()
      });
      observer.observe(document.body);
    }
  }

   /**
   * 将所有slide元素的高度设置为"最高"的那个slide元素的高度
   */
  updateSlideItemHeightToMaxHeight() {

    const slideItems = this.sliderElement.querySelectorAll('.splide__slide')
    const realItems = []
    slideItems.forEach(slide => {
      realItems.push(slide.firstElementChild)
    })

    // 1. 计算最大高度
    const calculateMaxHeight = () => {
      let maxHeight = 0
      realItems.forEach(item => {
        item.style.height = 'auto'
        const height = item.getBoundingClientRect().height
        if (height > maxHeight) maxHeight = height
      })
      return maxHeight
    }

    // 2. 设置所有轮播项高度
    const unifySlideHeights = (height) => {
      slideItems.forEach(slide => {
        slide.style.height = `${height}px`;
        slide.style.minHeight = `${height}px`;
      });

      realItems.forEach(item => {
        item.style.height = '100%'
      })
    };

    unifySlideHeights(calculateMaxHeight());
  }

  onPlayButtonClick(e) {
    e.target.closest('.slider-component__item-image')?.querySelector('video-media')?.togglePlay()
  }
}
customElements.define('slider-component', SliderComponent)

class VideoWithTexts extends HTMLElement {
  constructor() {
    super()

    this.contentContainer = this.querySelector('.vwts__content-container')
    this.videoContainer = this.querySelector('.vwts__video-container')
    this.splideElement = this.videoContainer.querySelector('.splide')
    this.splideListElement = this.splideElement.querySelector('.splide__list')

    this.tabList = this.querySelector('.vwts__video-tab-list')
    this.tabIndicator = this.tabList.querySelector('.vwts__video-tab-indicator')
    this.tabItems = Array.from(this.tabList.querySelectorAll('.vwts__video-tab-name'))
    this.tabItems.forEach(item => item.addEventListener('click', this.onTabItemClick.bind(this)))

    const content = this.querySelector('template[data-content]').content.firstElementChild?.cloneNode(true)
    const contents = Array.from(content.querySelectorAll('.vwts__content'))

    let rowContainer, colContainer
    for (const contentEl of contents) {
      const type = contentEl.dataset.type
      if (type === 'row') {
        rowContainer = contentEl
        colContainer = null
        this.contentContainer.appendChild(rowContainer)
      } else if (type === 'col') {
        colContainer = contentEl
        rowContainer?.appendChild(colContainer)
        rowContainer?.setAttribute('data-has-col', true)
      } else if (type === 'video') {
        contentEl.classList.add('splide__slide')
        this.splideListElement.appendChild(contentEl)
      } else {
        const box = colContainer || rowContainer
        box?.appendChild(contentEl)
      }
    }

    this.splideItems = Array.from(this.splideElement.querySelectorAll('.splide__slide'))

    const enableSlideOnDesktop = this.videoContainer.classList.contains('tab')
    const enableSlideOnMobile = this.videoContainer.classList.contains('tab-mobile')
    this.videoSplide = new Splide(this.splideElement, {
      perPage: 1,
      pagination: false,
      arrows: false,
      mediaQuery: 'min',
      breakpoints: {
        0: {
          perPage: 1,
          destroy: !enableSlideOnMobile
        },
        768: {
          perPage: 1,
          destroy: !enableSlideOnDesktop
        }
      }
    })
    this.videoSplide.on('move', (newIndex, prevIndex, destIndex) => {
      this.onSlideIndexChanged(destIndex, prevIndex)
    })
    this.videoSplide.on('ready', () => {
      this.updateActiveTab(0)
    })
    this.videoSplide.mount()


    // 为视频添加ended事件监听
    this.splideItems.forEach(item => {
      item.querySelector('video-media')?.addEventListener('playComplete', this.onVideoEnded.bind(this))
    })
  }

  connectedCallback() {

  }

  updateActiveTab(index) {
    let left = 0
    for (let i = 0; i < index; i++) {
      left += this.tabItems[i].getBoundingClientRect().width
    }
    const width = this.tabItems[index].getBoundingClientRect().width
    this.tabIndicator.style.width = `${width}px`
    this.tabIndicator.style.left = `${left}px`
  }

  onTabItemClick(e) {
    const index = this.tabItems.indexOf(e.currentTarget)
    this.videoSplide.go(index)
  }

  onSlideIndexChanged(index, prevIndex) {
    this.updateActiveTab(index)
    this.splideItems[prevIndex].querySelector('video-media')?.pause()
    this.splideItems[index].querySelector('video-media')?.replay()
  }

  onVideoEnded() {
    if (this.dataset.autoNext === 'true') {

      if (this.videoSplide.index === this.videoSplide.length - 1) {
        this.videoSplide.go(0)
      } else {
        this.videoSplide.go('>')
      }
    }
  }
}
customElements.define('video-with-texts', VideoWithTexts)

class PuzzleComp extends HTMLElement {
  constructor() {
    super()
    this.desktopAutoAdjustWidth = this.dataset.desktopAutoAdjustWidth === 'true'
    this.desktopStyle = this.dataset.desktopStyle
    this.desktopOriginWidth = parseInt(this.dataset.desktopWidth)
    this.desktopOriginHeight = parseInt(this.dataset.desktopHeight)
    this.mobileAutoAdjustWidth = this.dataset.mobileAutoAdjustWidth === 'true'
    this.mobileStyle = this.dataset.mobileStyle
    this.mobileOriginWidth = parseInt(this.dataset.mobileWidth)
    this.mobileOriginHeight = parseInt(this.dataset.mobileHeight)

    this.items = Array.from(this.querySelectorAll('.puzzle__item'))
    this.mobileQuery = window.matchMedia(theme.config.mediaQuerySmall)

    window.addEventListener('resize', theme.utils.throttle(this.adjustContainerWithPadding.bind(this)))
    this.adjustContainerWithPadding()
  }

  connectedCallback() {
    
  }

  /**
   * 响应式调整容器及其内部元素
   */
  adjustContainerWithPadding() {
    let container = this
    let padding = 0
    let media, puzzleStyle, originalWidth, originalHeight


    const reset = () => {
      // 恢复原始尺寸
      this.style.width = originalWidth == 0 ? '100%' : originalWidth + 'px';
      this.style.height = puzzleStyle == 'grid' ? 'auto' : originalHeight + 'px';
      
      this.items.forEach(child => {
        if (puzzleStyle == 'puzzle') {
          child.style.left = child.dataset[media + 'Left'] + 'px'
          child.style.top = child.dataset[media + 'Top'] + 'px'
          child.style.width = child.dataset[media + 'Width'] + 'px'
          child.style.height = child.dataset[media + 'Height'] + 'px'
        } else {
          child.style.left = 'unset'
          child.style.top = 'unset'
          child.style.width = '100%'
          child.style.height = 'auto'
        }
      });
    }

    if (this.mobileQuery.matches) {
      media = 'mobile'
      puzzleStyle = this.mobileStyle
      originalWidth = this.mobileOriginWidth
      originalHeight = this.mobileOriginHeight
      if (!this.mobileAutoAdjustWidth) {
        return reset()
      }
    } else {
      media = 'desktop'
      puzzleStyle = this.desktopStyle
      originalWidth = this.desktopOriginWidth
      originalHeight = this.desktopOriginHeight
      if (!this.desktopAutoAdjustWidth) {
        return reset()
      }
    }
    

    // 获取视口宽度
    const viewportWidth = this.parentElement.getBoundingClientRect().width;
    
    // 检查是否需要调整（考虑间距）
    if (viewportWidth < originalWidth + 2 * padding) {
        // 计算可用宽度（视口宽度减去左右间距）
        const availableWidth = viewportWidth - 2 * padding;
        
        // 确保可用宽度不小于0
        const effectiveWidth = Math.max(availableWidth, 0);
        
        // 计算缩放比例（基于可用宽度）
        const scale = effectiveWidth / originalWidth;
        
        // 调整容器尺寸（宽度减去间距）
        container.style.width = `${effectiveWidth}px`;
        container.style.height = `${originalHeight * scale}px`;
        
        // 调整子元素
        this.items.forEach(child => {
          const originalLeft = parseFloat(child.dataset[media + 'Left']);
          const originalTop = parseFloat(child.dataset[media + 'Top']);
          const originalWidth = parseFloat(child.dataset[media + 'Width']);
          const originalHeight = parseFloat(child.dataset[media + 'Height']);
          
          child.style.left = `${originalLeft * scale}px`;
          child.style.top = `${originalTop * scale}px`;
          child.style.width = `${originalWidth * scale}px`;
          child.style.height = `${originalHeight * scale}px`;
        })
    } else {
      reset()
    }
  }
}
customElements.define('puzzle-comp', PuzzleComp)

class SpiralDistribution extends HTMLElement {
  constructor() {
    super()
    this.isMobileQuery = window.matchMedia(theme.config.mediaQuerySmall);
    this.isDesignMode = this.hasAttribute('shopify-design-mode')
    this.designPoint = this.dataset.designPoint
    this.icons = Array.from(this.querySelectorAll('.image-icon'))

    this.icons.forEach(item => {
      item.style.left = '50%';
      item.style.top = '50%';
      item.style.opacity = '0';
      item.style.transform = 'translate(-50%, -50%) scale(1) rotate(0deg)';
    });

    if (this.isDesignMode) {
      this.initImageIconsForDesignMode()
    } else {
      this.initImageIcons()
    }
  }

  initImageIcons() {

    const masterTimeline = gsap.timeline();
    
    let pointXStart = 'pointXStart';
    let pointYStart = 'pointYStart';
    let widthStart = 'widthStart';
    let heightStart = 'heightStart';
    let opacityStart = 'opacityStart';
    let rotateStart = 'rotateStart'

    let pointX = 'pointX';
    let pointY = 'pointY';
    let width = 'width';
    let height = 'height';
    let opacity = 'opacity';
    let rotate = 'rotate'
    
    if (this.isMobileQuery.matches) {
      pointXStart = 'pointXMobileStart';
      pointYStart = 'pointYMobileStart';
      widthStart = 'widthMobileStart';
      heightStart = 'heightMobileStart';
      opacityStart = 'opacityMobileStart'
      rotateStart = 'rotateMobileStart'

      pointX = 'pointXMobile';
      pointY = 'pointYMobile';
      width = 'widthMobile';
      height = 'heightMobile';
      opacity = 'opacityMobile'
      rotate = 'rotateMobile'
    }
    
    // 为每个图标创建动画并添加到时间线
    this.icons.forEach(item => {
      masterTimeline.fromTo(item, {
        opacity: parseFloat(item.dataset[opacityStart]) / 100.0,
        left: `${item.dataset[pointXStart]}%`,
        top: `${item.dataset[pointYStart]}%`,
        width: `${item.dataset[widthStart]}px`,
        height: `${item.dataset[heightStart]}px`,
        rotate: item.dataset[rotateStart],
      }, {
        opacity: parseFloat(item.dataset[opacity]) / 100.0,
        left: `${item.dataset[pointX]}%`,
        top: `${item.dataset[pointY]}%`,
        width: `${item.dataset[width]}px`,
        height: `${item.dataset[height]}px`,
        rotate: item.dataset[rotate],
        ease: "none",
        transformOrigin: "center center"
      }, 0); // 所有动画同时开始
    });
    
    // 创建ScrollMagic场景
    new ScrollMagic.Scene({
      triggerElement: this,
      triggerHook: 0.6,
      duration: '70%'
    })
    .setTween(masterTimeline)
    .addTo(window.scrollMagicController);

  }

  initImageIconsForDesignMode() {
    let suffix = this.designPoint == 'start' ? 'Start' : ''
    let pointX = 'pointX' + suffix
    let pointY = 'pointY' + suffix
    let width = 'width' + suffix
    let height = 'height' + suffix
    let opacity = 'opacity' + suffix
    let rotate = 'rotate' + suffix
    if (this.isMobileQuery.matches) {
      pointX = 'pointXMobile' + suffix
      pointY = 'pointYMobile' + suffix
      width = 'widthMobile' + suffix
      height = 'heightMobile' + suffix
      opacity = 'opacityMobile' + suffix
      rotate = 'rotateMobile' + suffix
    }
    this.icons.forEach(item => {
      item.style.opacity = parseFloat(item.dataset[opacity]) / 100.0
      item.style.left = `${item.dataset[pointX]}%`
      item.style.top = `${item.dataset[pointY]}%`
      item.style.width = `${item.dataset[width]}px`
      item.style.height = `${item.dataset[height]}px`
      item.style.transform = `rotate(${item.dataset[rotate]}deg)`
    })
  }
}
customElements.define('spiral-distribution', SpiralDistribution)

class ProductTextContainer extends HTMLElement {
  constructor() {
    super();
    let nextEl = this
    const els = []
    while (true) {
      nextEl = nextEl.nextElementSibling
      if (nextEl && nextEl.classList.contains('product__text')) {
        els.push(nextEl)
      } else {
        break
      }
    }

    els.forEach(item => {
      this.appendChild(item)
    })
  }
}
customElements.define('product-text-container', ProductTextContainer);


class VideoHeroV5 extends HTMLElement {
  constructor() {
    super();
    this.headerNotTransparentInProgress = 0.96
    this.scrollRatio = parseFloat(this.dataset.scrollRatio)
    this.videoReady = false
    this.paddingBottom = 200

    // 文字开始动画的进度百分比范围
    this.TEXT_ANIME_PERCENT_START = 1;
    this.TEXT_ANIME_PERCENT_END = 4;
    // 容器开始缩放的进度百分比范围
    this.CONTAINER_SCALE_PERCENT_START = 96;
    this.CONTAINER_SCALE_PERCENT_END = 99;

    if (this.hasAttribute('allow-transparent-header')) {
      this.stickyHeader.setAlwaysTransparent(true)
    }
  }

  get stickyHeader() {
    return document.querySelector('header.header')
  }

  get videoData() {
    return this.querySelector('video.video-data')
  }

  get canvas() {
    return this.querySelector('canvas')
  }

  get contentBox() {
    return this.querySelector('.vh5__content-box')
  }

  get stickyContainer() {
    return this.querySelector('.vh5__sticky-container')
  }

  get progress() {
    return this.querySelector('.progress')
  }

  async loadVideo() {
    if (!this.videoData) return
    this.videoData.load();
    
    try {
      await this.videoData.play();
      this.videoData.pause();
      this.videoReady = true
      this.initContainerHeight();
      this.initWhenVideoReady();
    } catch (e) {
    }
  }

  initContainerHeight() {
    const screenHeight = window.innerHeight;
    const duration = this.videoData.duration;
    const totalHeight = duration * this.scrollRatio * screenHeight - this.paddingBottom;
    this.style.height = `${totalHeight}px`;
  }

  updateVideoProgressByScrollY(scrollY) {
    if (!this.videoReady) return;
    const maxScroll = this.clientHeight - window.innerHeight;
    const progress = Math.min(scrollY, maxScroll) / maxScroll;
    
    this.videoData.currentTime = this.videoData.duration * progress;

    this.onPercentageChanged(progress * 100);

    if (this.progress) {
      this.progress.textContent = `${(progress * 100).toFixed(1)}%`;
    }
    this.stickyHeader.setAlwaysTransparent(progress <= this.headerNotTransparentInProgress)
  }

  onPercentageChanged(percent) {
    const textAnimePercent = 100 * (percent - this.TEXT_ANIME_PERCENT_START) / (this.TEXT_ANIME_PERCENT_END - this.TEXT_ANIME_PERCENT_START)
    const textOpacity = (100 - textAnimePercent) / 100.0;
    this.contentBox.style.opacity = textOpacity;
    const textScale = 1 - (textAnimePercent / 100.0) * 0.2;
    this.contentBox.style.transform = `scale(${textScale})`;

    const containerScalePercent = 100 * (percent - this.CONTAINER_SCALE_PERCENT_START) / (this.CONTAINER_SCALE_PERCENT_END - this.CONTAINER_SCALE_PERCENT_START)
    const padding = containerScalePercent / 100.0 * 20;
    this.style.paddingLeft = `${padding}px`;
    this.style.paddingRight = `${padding}px`;
    const translateY = containerScalePercent / 100.0 * 120;
    this.stickyContainer.style.transform = `translateY(-${translateY}px)`
  }

  initWhenVideoReady() {
    window.addEventListener('scroll', this.handleScroll.bind(this));

    this.canvas.width = this.videoData.videoWidth;
    this.canvas.height = this.videoData.videoHeight;
    this.canvasContext = this.canvas.getContext('2d');
    this.canvasContext.imageSmoothingQuality = 'high';
    this.canvasContext.imageSmoothingEnabled = false;
    this.videoData.addEventListener('seeked', this.drawVideoFrame.bind(this));

    this.drawVideoFrame();
  }

  handleScroll() {
    requestAnimationFrame(() => {
      this.updateVideoProgressByScrollY(window.scrollY);
    });
  }

  drawVideoFrame() {
    this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvasContext.drawImage(
        this.videoData,
        0, 0,
        this.canvas.width, this.canvas.height
    );
  }

  connectedCallback() {
    this.loadVideo();
  }

  disconnectedCallback() {

  }
}
customElements.define('video-hero-v5', VideoHeroV5);

class VideoHeroV6 extends HTMLElement {
  constructor() {
    super()
    this.isMobileMedia = window.matchMedia(theme.config.mediaQuerySmall)
    this.imageSeqUrl = this.dataset.imageSeqUrl
    this.imageSeqUrlMobile = this.dataset.imageSeqUrlMobile
    this.frameStart = parseInt(this.dataset.imageSeqStart)
    this.frameEnd = parseInt(this.dataset.imageSeqEnd)
    this.frameCount = this.frameEnd - this.frameStart
    this.canvas = this.querySelector('canvas')
    this.context = this.canvas.getContext("2d")
    // { src: '', loaded: false }
    this.imagesCache = []

    this.initImageCache()
    if (this.frameEnd > this.frameStart) {
      this.init()
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.startLoadImages(this.isMobileMedia.matches).then(() => {
            })
            observer.unobserve(this)
          }
        })
      },
      {
        rootMargin: "400px", // 关键！提前 400px 触发
        threshold: 0, // 只要进入扩展区域就触发
      }
    )

    observer.observe(this)

  }

  get stickyContainer() {
    return this.querySelector('.vh6__sticky-container')
  }

  get contentBox() {
    return this.querySelector('.vh6__content-box')
  }

  get imageSeqUrlTemplate() {
    if (this.isMobileMedia.matches) {
      return this.imageSeqUrlMobile || this.imageSeqUrl
    } else {
      return this.imageSeqUrl
    }
  }


  connectedCallback() {
  }

	onFirstImageLoaded() {
		this.renderImage()
	}

  initImageCache() {
    const currentFrame = (urlTemplate, index) => {
      const seq = index.padStart(4, "0")
      return urlTemplate.replace('[xxxx]', seq)
    }
    let frameIndex = 1
    for (let i = this.frameStart; i <= this.frameEnd; i++) {
      this.imagesCache.push({
        src: currentFrame(this.imageSeqUrl, i + ''),
        srcMobile: currentFrame(this.imageSeqUrlMobile || this.imageSeqUrl, i + ''),
        frame: frameIndex++,
        loaded: false
      })
    }
  }

  async startLoadImages(isMobile, batchSize = 60, maxRetries = 2) {
    let retryCount = 0;

    while (true) {
      const remaining = this.imagesCache.filter(i => !i.loaded).length;
      if (remaining === 0) {
        // 所有图片加载完成
        break;
      }

      const results = await this.loadImages(batchSize, isMobile);
      // const failed = results.filter(r => r.status === 'rejected');

      // if (failed.length > 0) {
      //    console.warn(`${failed.length} 张图片加载失败`, failed.map(f => f.reason.message));
          
      //    if (retryCount < maxRetries) {
      //        retryCount++;
      //        console.log(`第 ${retryCount} 次重试...`);
      //    } else {
      //        console.error('达到最大重试次数，跳过剩余错误');
      //        break;
      //    }
      // } else {
      //    retryCount = 0; // 成功时重置重试计数
      // }
    }
  }

  loadImages(total = 60, isMobile) {
    const arr = this.imagesCache.filter(i => i.loaded == false).slice(0, total)
    const promises = arr.map(imageObj => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = isMobile ? imageObj.srcMobile : imageObj.src;
            img.onload = () => {
                imageObj.loaded = true;
                imageObj.imgElement = img
								if (imageObj.frame == 1) {
									this.onFirstImageLoaded()
								}
                resolve(imageObj); // 加载成功
            };
            img.onerror = () => {
                imageObj.loaded = true;
                this.images.push(false)
                reject(new Error(`Failed to load: ${imageObj.src}`)); // 加载失败
            };
        });
    });

    // 等待所有图片加载完成
    return Promise.allSettled(promises)
  }

  init() {

    this.canvas.width = this.canvas.offsetWidth;
    this.canvas.height = this.canvas.offsetHeight;

    this.airpods = {
      frame: 0
    };

    const render = this.renderImage.bind(this)

    const masterTL = gsap.timeline({
      scrollTrigger: {
        trigger: this,
        start: 'top top',
        pin: true,
        scrub: 0.8
      }
    });

    const frameAnim = masterTL.to(this.airpods, {
      frame: this.frameCount - 1,
      snap: "frame",
      ease: "none",
      duration: (this.frameCount - 1) / 60,
      onUpdate: render
    });

    masterTL.to(
      this.contentBox,
      {
        scale: 0.5,
        opacity: 0,
        ease: "none",
        duration: 1.6
      },
      0
    );

    masterTL.to(
      this.stickyContainer,
      {
        left: 40,
        right: 40,
        bottom: 40,
        borderBottomLeftRadius: 20,
        borderBottomRightRadius: 20,
        ease: "power2.out"
      },
      frameAnim.duration() * 0.9
    );
  }


  renderImage() {
    const image = this.imagesCache[this.airpods.frame]
    if (image.loaded) {
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.context.drawImage(image.imgElement, 0, 0, this.canvas.width, this.canvas.height);
    }
  }

}
customElements.define('video-hero-v6', VideoHeroV6);

class NavShopButton extends HTMLElement {
  constructor() {
    super()
    this.isMobileQuery = window.matchMedia(theme.config.mediaQuerySmall)
    this.isMobileQuery.addEventListener('change', this.onMediaQueryChanged.bind(this))
    this.showMode = this.dataset.showMode
    this.showAtOffset = parseInt(this.dataset.showAtOffset)
    // 在第几个section出现时显示
    this.showAtSectionIndex = parseInt(this.dataset.showAtSectionIndex || 5)
    const sections = Array.from(document.querySelectorAll('#MainContent .shopify-section'))
    // 在这两个模块之间的模块出现在viewport时展示购买按钮
    if (this.showAtSectionIndex >= 0) {
      this.scopeFrom = this.showAtSectionIndex < sections.length ? sections[this.showAtSectionIndex] : null
    } else {
      this.scopeFrom = null
      this.showBuyButton()
    }
    this.scopeTo = null

    this.onBuyClickListener = this.onBuyClick.bind(this)

    if (this.isProductPage) {
      if (this.showAtSectionIndex == -1) {
        const productFormWrapper = document.querySelector('.product-form-wrapper')
        if (productFormWrapper) {
          this.scopeFrom = productFormWrapper
        }
      }
      this.headerBuyButton.addEventListener('click', this.onBuyClickListener)
    }

    this.onVariantChangedListener = this.onVariantChanged.bind(this);
    this.addEventListener('click', this.onBuyClickListener)
  }

  get headerBuyButton() {
    return document.querySelector('header .header__buy-button')
  }

  get isProductPage() {
    return this.dataset.isProductPage == 'true'
  }

  connectedCallback() {
		if (!this.hasAttribute('only-mobile')) {
			document.querySelector('nav-shop-button[home-buy-button]')?.remove()
			document.addEventListener('document:variant:change', this.onVariantChangedListener);
			this.headerBuyButton.setAttribute('href', this.dataset.productUrl)
	
			let buttonIcon = this.headerBuyButton.querySelector('img')
			if (this.dataset.icon) {
				if (!buttonIcon) {
					buttonIcon = document.createElement('img')
					this.headerBuyButton.prepend(buttonIcon)
				}
				buttonIcon.setAttribute('src', this.dataset.icon)
			}
	
			let buttonContent = this.headerBuyButton.querySelector('.header__buy-button-content')
			if (this.dataset.title) {
				if (!buttonContent) {
					buttonContent = document.createElement('div')
					buttonContent.classList.add('header__buy-button-content')
					buttonIcon.insertAdjacentElement('afterend', buttonContent)
				}
				buttonContent.textContent = this.dataset.title
			}
	
			let buttonLabel = this.headerBuyButton.querySelector('.header__buy-button-btn')
			if (this.dataset.buttonLabel) {
				if (!buttonLabel) {
					buttonLabel = document.createElement('div')
					buttonLabel.classList.add('header__buy-button-btn')
					buttonContent.insertAdjacentElement('afterend', buttonLabel)
				}
				buttonLabel.textContent = this.dataset.buttonLabel
			}
		}

    if (this.showMode == 'section') {
      if (this.scopeFrom || this.scopeTo) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target === this.scopeFrom) {
              this.scopeFromPassed = entry.boundingClientRect.bottom < 0;
            }
            if (entry.target === this.scopeTo) {
              this.scopeToReached = entry.isIntersecting;
            }
          });
  
          if (this.scopeFromPassed && !this.scopeToReached) {
            this.showBuyButton()
          } else {
            this.hideBuyButton()
          }
        });
        this.observer.observe(this.scopeFrom)
        if (this.scopeTo) {
          this.observer.observe(this.scopeTo)
        }
      }

    } else {
      if (this.showAtOffset <= 0) {
        this.showBuyButton()
      } else {
        this.onWindowScrollListener = () => {
          const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
          if (currentScroll > this.showAtOffset) {
            this.showBuyButton()
          } else {
            this.hideBuyButton()
          }
        }
        window.addEventListener('scroll', this.onWindowScrollListener)
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onWindowScrollListener)
    document.removeEventListener('document:variant:change', this.onVariantChangedListener);
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  onMediaQueryChanged(e) {
    if (this.showAtSectionIndex < 0) {
      this.showBuyButton()
    }
  }

  showBuyButton() {
    this.headerBuyButton.classList.remove('hidden')
    Motion.animate(this.headerBuyButton, {
      visibility: 'visible',
      opacity: 1
    })

    if (this.isMobileQuery.matches) {
      Motion.animate(this, { visibility: 'visible', opacity: 1 })
    } else {
      Motion.animate(this, { visibility: 'hidden', opacity: 0 })
    }
  }

  hideBuyButton() {
    Motion.animate(this.headerBuyButton, { visibility: 'hidden', opacity: 0 })
    Motion.animate(this, { visibility: 'hidden', opacity: 0 })
  }

  onBuyClick(e) {
    if (this.isProductPage) {
      document.querySelector('button[is="buy-it-now-button"]')?.click()
    } else if(this.dataset.productUrl) {
      window.location = this.dataset.productUrl
    }
    e.preventDefault()
  }

  onVariantChanged(e) {
    const v = e.detail.variant
    const comparePrice = this.querySelector('.nav-shop-button__price .compare-price')
    if (comparePrice) {
      if (v.compare_at_price > v.price) {
        comparePrice.textContent = theme.Currency.formatMoney(v.compare_at_price)
        comparePrice.classList.remove('hidden')
      } else {
        comparePrice.classList.add('hidden')
      }
    }
    const salePrice = this.querySelector('.nav-shop-button__price .sale-price')
    salePrice.textContent = theme.Currency.formatMoney(v.price)
  }
}
customElements.define('nav-shop-button', NavShopButton);


class StickyAnchorBar extends HTMLElement {
  constructor() {
    super()
    // header-below | bottom | auto
    /*
      header-below: 在导航栏下面
      bottom: 固定在底部
      auto: 在正常文档流中，滚动导致不可见时固定在顶部
    */
    this.positionStyle = this.dataset.style
    this.enableScrollDetect = this.dataset.enableScrollDetect == 'true'
    // 锚标记ID
    this.anchorSections = []
    this.isMobileMedia = window.matchMedia(theme.config.mediaQuerySmall)

    this.onAnchorItemClickListener = this.onAnchorItemClick.bind(this)
    this.onWindowScrolledListener = theme.utils.throttle(this.onWindowScrolled.bind(this));

    this.transparentOnTop = this.dataset.transparentOnTop === 'true'
    if (this.positionStyle == 'header-below' && this.transparentOnTop) {
      this.classList.add('transparent-bg')
    }

    this.footerObserver = new IntersectionObserver((entries) => {
      this.onFooterVisible(entries[0].isIntersecting)
    }, { threshold: 0 });

    this.initSectionIdMarker()

    this.querySelectorAll('.link-item').forEach(link => {
      const linkHref = link.getAttribute('href');
      const linkPath = this.getPurePath(linkHref);
      console.log('link path:', linkPath)
      const currentPath = window.location.pathname;
      if (linkPath === currentPath) {
        link.classList.add('active')
        return false
      }
    })

    theme.ContainerDraggable(this.anchorList)
  }

  get header() {
    return document.querySelector('header.header')
  }

  get anchorIds() {
    const ids = []
    this.anchorItems.forEach(item => {
      if (item.dataset.anchorId) {
        ids.push(item.dataset.anchorId)
      }
    })
    return ids
  }
  
  get anchorList() {
    return this.querySelector('.anchor-list')
  }

  get anchorItems() {
    return Array.from(this.querySelectorAll('.anchor-list .anchor-item:not(.link-item)'))
  }

  get footer() {
    return document.querySelector('footer')
  }

  connectedCallback() {
    this.anchorItems.forEach(item => {
      if (item.dataset.anchorId) {
        item.addEventListener('click', this.onAnchorItemClickListener)
      }
    })

    if (this.positionStyle === 'header-below') {
      this.updateBarTop('header')
    }

    window.addEventListener('scroll', this.onWindowScrolledListener)

    this.footerObserver.observe(this.footer)

    if (this.anchorList.scrollWidth > this.anchorList.clientWidth) {
      this.anchorList.style.justifyContent = 'flex-start'
      this.anchorListScrollToActiveItem()
    }
  }

  disconnectedCallback() {
    this.anchorItems.forEach(item => {
      item.removeEventListener('click', this.onAnchorItemClickListener)
    })
    window.removeEventListener('scroll', this.onWindowScrolledListener)

    this.footerObserver.disconnect()
  }

  getPurePath(url) {
    // 如果是相对路径（如 "/xxx/xx"），转换为完整URL以便解析
    const baseUrl = window.location.origin;
    const fullUrl = url.startsWith('http') ? url : new URL(url, baseUrl).href;

    // 使用 URL API 解析路径
    const { pathname } = new URL(fullUrl);
    return pathname;
  }

  // 将shopify-section打上锚标记
  initSectionIdMarker() {
    if (this.anchorIds.length === 0) {
      return
    }
    const anchorSections = []
    for (const anchorId of this.anchorIds) {
      const sectionAnchor = document.querySelector(`[anchor-id="${anchorId}"]`)
      if (sectionAnchor) {
        let section
        if (sectionAnchor.classList.contains('sticky-anchor')) {
          section = sectionAnchor?.closest('.shopify-section')?.nextElementSibling
        } else {
          section = sectionAnchor?.closest('.shopify-section')
        }
        if (section) {
          section.setAttribute('section-anchor-id', anchorId)
          anchorSections.push(section)
        }
      }
    }
    this.anchorSections = anchorSections
  }

  updateBarTop(top) {
    // Clear any existing interval
    clearInterval(this.intervalId);
    
    if (top === 'header') {
      this.setAttribute('on-top', 'false')
      // Use requestAnimationFrame for smoother animation tracking
      let startTime = null;
      const duration = 500; // Adjust based on your header animation duration
      
      const updatePosition = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        
        // Get current header bottom position
        const currentBottom = this.header.getBoundingClientRect().bottom;
        this.style.setProperty('--sticky-bar-top', `${currentBottom}px`);
        
        // Continue updating until animation duration has passed
        if (elapsed < duration) {
          this.intervalId = requestAnimationFrame(updatePosition);
        }
      };
      
      // Start tracking
      this.intervalId = requestAnimationFrame(updatePosition);
    } else {
      this.style.setProperty('--sticky-bar-top', '0');
      this.setAttribute('on-top', 'true')
    }
  }

  onAnchorItemClick(e) {
    const anchor = e.target.dataset.anchorId
    const scrollView = this.anchorSections.find(item => item.getAttribute('section-anchor-id') == anchor)
    if (scrollView) {
      const offset = this.getBoundingClientRect().height
      let elementPosition = scrollView.getBoundingClientRect().top;
      let offsetPosition = elementPosition + window.scrollY - offset;
      window.scrollTo({top: offsetPosition + 20, behavior: "smooth"})
    }
  }

  onWindowScrolled() {
    if (this.enableScrollDetect) {
      if (this.positionStyle === 'header-below') {
        this.onScrolledForHeaderBelow()
      } else if (this.positionStyle === 'auto') {
        this.onScrolledForAuto()
      }
    }
    this.onScrolledToFindMatchSection()
  }

  /**
   * 滚动网页时，找到anchor item对应的模块，如果找到就将anchor item激活
   */
  onScrolledToFindMatchSection() {
    let barHeight = 0
    if (this.positionStyle == 'auto') {
      barHeight = this.getBoundingClientRect().height
    } else if (this.header.dataset.stickyType == 'always') {
      barHeight = this.header.getBoundingClientRect().height
    }
    const visibleSection = this.anchorSections.find(section => {
      const rect = section.getBoundingClientRect();
      return rect.top - barHeight <= 20 && rect.bottom - barHeight >= 80;
    });
    this.anchorItems.forEach(item => item.classList.remove('active'));
    const sectionAnchorId = visibleSection?.getAttribute('section-anchor-id');
    if (sectionAnchorId) {
      this.anchorItems.find(item => item.dataset.anchorId === sectionAnchorId)?.classList.add('active');
    }
  }

  onScrolledForHeaderBelow() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    if (scrollTop > 50) {
      this.header.setHidden(true)
      this.updateBarTop(0)
      if (this.transparentOnTop) {
        this.classList.remove('transparent-bg')
      }
    } else {
      this.header.setHidden(false)
      this.updateBarTop('header')
      if (this.transparentOnTop) {
        this.classList.add('transparent-bg')
      }
    }
  }

  onScrolledForAuto() {
    const rect = this.getBoundingClientRect()
    const top = rect.top
    const bottom = rect.bottom
    const hideHeader = top <= 0
    this.header.setHidden(hideHeader)
    if (hideHeader) {
      if (!this.classList.contains('pin')) {
        this.classList.add('pin')
      }
      const positionTop = Math.max(0, this.header.getBoundingClientRect().bottom) - 2
      this.style.setProperty('--sticky-bar-top', `${positionTop}px`)
    } else {
      this.classList.remove('pin')
    }
  }

  onFooterVisible(visible) {
    let target
    if (this.positionStyle == 'bottom') {
      target = this
    }
    if (target) {
      if (visible) {
        Motion.animate(target, { opacity: 0, visibility: 'hidden', transform: 'translateY(100%)'})
      } else {
        Motion.animate(target, { opacity: 1, visibility: 'visible', transform: 'translateY(0)'})
      }
    }
  }

  anchorListScrollToActiveItem() {
    const container = this.anchorList;
    const activeItem = this.anchorList.querySelector('.anchor-item.active');
    
    if (!container || !activeItem) return;
    
    const containerRect = container.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();
    
    // 计算容器和元素的中心点
    const containerCenter = containerRect.left + containerRect.width / 2;
    const itemCenter = itemRect.left + itemRect.width / 2;
    
    // 计算滚动偏移，使 activeItem 居中
    const scrollOffset = itemCenter - containerCenter;
    
    // 应用滚动
    container.scrollLeft += scrollOffset;
  }
}
customElements.define('sticky-anchor-bar', StickyAnchorBar);

class CountdownAnnouncementBar extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    const countdownSpan = this.querySelector('.countdown-span')
    if (countdownSpan) {
      const timer = this.querySelector('countdown-timer')
      if (timer) {
        countdownSpan.appendChild(timer)
      }
    }
  }
}
customElements.define('countdown-announcement-bar', CountdownAnnouncementBar)

class BlogArticles extends HTMLElement {
  constructor() {
    super()
    this.dataLoading = this.querySelector('.data-loading')

    this.onPageClickListener = this.onPageClick.bind(this)
  }

  connectedCallback() {
    this.setupPageEventListener(true)
  }

  disconnectedCallback() {
    this.setupPageEventListener(false)
  }

  get currentPageNum() {
    return parseInt(this.querySelector('.pagination .page.current').textContent)
  }

  setupPageEventListener(add) {
    if (add) {
      this.querySelectorAll('.pagination a.page-item').forEach(page => {
        page.addEventListener('click', this.onPageClickListener)
      })
    } else {
      this.querySelectorAll('.pagination a.page-item').forEach(page => {
        page.removeEventListener('click', this.onPageClickListener)
      })
    }
  }

  onPageClick(e) {
    const pageItem = e.target.closest('a')
    if (!pageItem.hasAttribute('disabled')) {
      const currentPageNum = this.currentPageNum
      let pageNum = parseInt(pageItem.getAttribute('aria-label'))
      if (pageItem.classList.contains('previous')) {
        pageNum = currentPageNum - 1
      } else if (pageItem.classList.contains('next')) {
        pageNum = currentPageNum + 1
      }
      this.loadPage(pageNum)
    }
    e.preventDefault()
  }

  setLoading(loading) {
    if (loading) {
      this.dataLoading.setAttribute('loading', '')
    } else {
      this.dataLoading.removeAttribute('loading')
    }
  }

  async loadPage(page) {
    this.setLoading(true)
    const url = `${window.location.pathname}?section_id=${this.dataset.sectionId}&page=${page}`
    const response = await fetch(url)
    const html = await response.text()

    this.setLoading(false)

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const articles = doc.querySelector('.blog-articles--list')
    this.querySelector('.blog-articles--list').innerHTML = articles.innerHTML

    const pagination = doc.querySelector('.pagination')
    this.setupPageEventListener(false)
    this.querySelector('.pagination').innerHTML = pagination.innerHTML
    this.setupPageEventListener(true)

    this.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}
customElements.define('blog-articles', BlogArticles)